import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Sparkles, ChevronLeft, Loader2, Check, ImageUp, ArrowRight, Search, X } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SiteVerification from "@/components/SiteVerification";
import { generateWorksheet } from "@/lib/api";
import { GRADE_SKILLS, TOPIC_KEY_FOR } from "@/pages/GradePage";
import { websiteSchema } from "@/lib/structuredData";

// ─── CA CCSS Kindergarten skill plan (source: IXL CA CCSS K PDF) ─────────────
// ─── Pre-K skill plan (IXL Pre-K sections A–Y, alphabetical) ───────────────
// Every skill is wired to a range-aware pk_* generator or a matching k_*
// generator so the practice problems fit the skill and the Pre-K number range.
const PRE_K_PLAN = [
  {
    domain: "A",
    name: "Numbers to 3",
    color: "bg-brand-coral",
    standards: [
      {
        code: "A",
        desc: "Recognize and name the numbers 1-3.",
        skills: [
          { name: "Identify numbers - up to 3", topic: "pk_identify_3" },
          { name: "Choose the number that you hear - up to 3", topic: "pk_choose_3" },
        ],
      },
    ],
  },
  {
    domain: "B",
    name: "Counting to 3",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "B",
        desc: "Count objects and show numbers up to 3.",
        skills: [
          { name: "Learn to count - up to 3", topic: "pk_learn_count_3" },
          { name: "Count pictures - up to 3", topic: "pk_count_pictures_3" },
          { name: "Count dots - up to 3", topic: "pk_count_dots_3" },
          { name: "Count shapes - up to 3", topic: "pk_count_shapes_3" },
          { name: "Count cubes - up to 3", topic: "pk_count_cubes_3" },
          { name: "Count on frames - up to 3", topic: "pk_count_frames_3" },
          { name: "Count out stickers - up to 3", topic: "pk_count_stickers_3" },
          { name: "Show numbers with cubes - up to 3", topic: "pk_represent_cubes_3" },
          { name: "Show numbers on frames - up to 3", topic: "pk_represent_frames_3" },
          { name: "Represent numbers with pictures - up to 3", topic: "pk_represent_pictures_3" },
          { name: "Represent numbers with shapes - up to 3", topic: "pk_represent_shapes_3" },
        ],
      },
    ],
  },
  {
    domain: "C",
    name: "Numbers to 5",
    color: "bg-brand-green",
    standards: [
      {
        code: "C",
        desc: "Recognize and name the numbers 1-5.",
        skills: [
          { name: "Identify numbers - up to 5", topic: "pk_identify_5" },
          { name: "Choose the number that you hear - up to 5", topic: "pk_choose_5" },
        ],
      },
    ],
  },
  {
    domain: "D",
    name: "Counting to 5",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "D",
        desc: "Count objects, order numbers, and use ordinals up to 5.",
        skills: [
          { name: "Learn to count - up to 5", topic: "pk_learn_count_5" },
          { name: "Count pictures - up to 5", topic: "pk_count_pictures_5" },
          { name: "Count dots - up to 5", topic: "pk_count_dots_5" },
          { name: "Count shapes in rows - up to 5", topic: "pk_count_shapes_5" },
          { name: "Count cubes - up to 5", topic: "pk_count_cubes_5" },
          { name: "Count on frames - up to 5", topic: "pk_count_frames_5" },
          { name: "Count scattered shapes - up to 5", topic: "k_count_scattered_5" },
          { name: "Count out stickers - up to 5", topic: "pk_count_stickers_5" },
          { name: "Show numbers with cubes - up to 5", topic: "pk_represent_cubes_5" },
          { name: "Show numbers on frames - up to 5", topic: "pk_represent_frames_5" },
          { name: "Represent numbers with pictures - up to 5", topic: "pk_represent_pictures_5" },
          { name: "Represent numbers with shapes - up to 5", topic: "pk_represent_shapes_5" },
          { name: "Put numbers up to 5 in counting order", topic: "pk_order_5" },
          { name: "What number comes next? - up to 5", topic: "pk_next_5" },
          { name: "Ordinal numbers - up to fifth", topic: "pk_ordinal_5" },
        ],
      },
    ],
  },
  {
    domain: "E",
    name: "Numbers and counting to 7",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "E",
        desc: "Count objects and show numbers up to 7.",
        skills: [
          { name: "Choose the number that you hear - up to 7", topic: "pk_choose_7" },
          { name: "Learn to count - up to 7", topic: "pk_learn_count_7" },
          { name: "Count pictures - up to 7", topic: "pk_count_pictures_7" },
          { name: "Count cubes - up to 7", topic: "pk_count_cubes_7" },
          { name: "Count on ten frames - up to 7", topic: "pk_count_frames_7" },
          { name: "Count out stickers - up to 7", topic: "pk_count_stickers_7" },
          { name: "Show numbers with cubes - up to 7", topic: "pk_represent_cubes_7" },
          { name: "Show numbers on ten frames - up to 7", topic: "pk_represent_frames_7" },
          { name: "Represent numbers - up to 7", topic: "pk_represent_pictures_7" },
        ],
      },
    ],
  },
  {
    domain: "F",
    name: "Numbers and counting to 9",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "F",
        desc: "Count objects and show numbers up to 9.",
        skills: [
          { name: "Choose the number that you hear - up to 9", topic: "pk_choose_9" },
          { name: "Learn to count - up to 9", topic: "pk_learn_count_9" },
          { name: "Count pictures - up to 9", topic: "pk_count_pictures_9" },
          { name: "Count cubes - up to 9", topic: "pk_count_cubes_9" },
          { name: "Count on ten frames - up to 9", topic: "pk_count_frames_9" },
          { name: "Count out stickers - up to 9", topic: "pk_count_stickers_9" },
          { name: "Show numbers with cubes - up to 9", topic: "pk_represent_cubes_9" },
          { name: "Show numbers on ten frames - up to 9", topic: "pk_represent_frames_9" },
          { name: "Represent numbers - up to 9", topic: "pk_represent_pictures_9" },
        ],
      },
    ],
  },
  {
    domain: "G",
    name: "Numbers to 10",
    color: "bg-brand-coral",
    standards: [
      {
        code: "G",
        desc: "Recognize and name the numbers 1-10.",
        skills: [
          { name: "Identify numbers - up to 10", topic: "pk_identify_10" },
          { name: "Choose the number that you hear - up to 10", topic: "pk_choose_10" },
        ],
      },
    ],
  },
  {
    domain: "H",
    name: "Counting to 10",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "H",
        desc: "Count objects, order numbers, and use ordinals up to 10.",
        skills: [
          { name: "Learn to count - up to 10", topic: "pk_learn_count_10" },
          { name: "Count pictures - up to 10", topic: "pk_count_pictures_10" },
          { name: "Count cubes - up to 10", topic: "pk_count_cubes_10" },
          { name: "Count dots - up to 10", topic: "pk_count_dots_10" },
          { name: "Count shapes in rows - up to 10", topic: "pk_count_shapes_10" },
          { name: "Count on ten frames - up to 10", topic: "k_ten_frames_10" },
          { name: "Count scattered shapes - up to 10", topic: "k_count_scattered_10" },
          { name: "Count shapes in rings - up to 10", topic: "pk_count_shapes_rings_10" },
          { name: "Count out stickers - up to 10", topic: "k_count_stickers_10" },
          { name: "Show numbers with cubes - up to 10", topic: "pk_represent_cubes_10" },
          { name: "Show numbers on ten frames - up to 10", topic: "pk_represent_frames_10" },
          { name: "Represent numbers - up to 10", topic: "pk_represent_pictures_10" },
          { name: "What number comes next? - up to 10", topic: "pk_next_10" },
          { name: "Ordinal numbers - up to tenth", topic: "pk_ordinal_10" },
        ],
      },
    ],
  },
  {
    domain: "I",
    name: "One more and one less",
    color: "bg-brand-green",
    standards: [
      {
        code: "I",
        desc: "Find one more and one less, up to 10.",
        skills: [
          { name: "One more with pictures - up to 5", topic: "pk_one_more_5" },
          { name: "One less with pictures - up to 5", topic: "pk_one_less_5" },
          { name: "One more with pictures - up to 10", topic: "pk_one_more_10" },
          { name: "One less with pictures - up to 10", topic: "pk_one_less_10" },
        ],
      },
    ],
  },
  {
    domain: "J",
    name: "Numbers and counting to 20",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "J",
        desc: "Count and represent numbers up to 20.",
        skills: [
          { name: "Identify numbers - up to 20", topic: "pk_identify_20" },
          { name: "Count pictures - up to 20", topic: "pk_count_pictures_20" },
          { name: "Count dots - up to 20", topic: "pk_count_dots_20" },
          { name: "Count shapes - up to 20", topic: "pk_count_shapes_20" },
          { name: "Count on ten frames - up to 20", topic: "pk_count_frames_20" },
          { name: "Show numbers on ten frames - up to 20", topic: "pk_represent_frames_20" },
          { name: "Represent numbers - up to 20", topic: "pk_represent_pictures_20" },
          { name: "Count up - up to 20", topic: "pk_count_up_20" },
        ],
      },
    ],
  },
  {
    domain: "K",
    name: "Compare groups",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "K",
        desc: "Compare groups by more, fewer, and same.",
        skills: [
          { name: "More", topic: "pk_groups_more" },
          { name: "Fewer", topic: "pk_groups_fewer" },
          { name: "Fewer and more - compare by matching", topic: "pk_compare_by_matching" },
          { name: "Fewer and more - compare by counting", topic: "pk_compare_by_counting" },
          { name: "Fewer and more - compare in a mixed group", topic: "pk_compare_mixed_group" },
          { name: "Fewer, more, and same", topic: "pk_groups_morefewersame" },
          { name: "Are there enough?", topic: "k_are_there_enough" },
          { name: "Are there the same number?", topic: "pk_groups_same" },
          { name: "Count pictures and show a number that is equal - up to 5", topic: "pk_count_equal_5" },
        ],
      },
    ],
  },
  {
    domain: "L",
    name: "Compare numbers",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "L",
        desc: "Compare numbers: larger, largest, smaller, smallest.",
        skills: [
          { name: "Which number is larger?", topic: "pk_compare_larger" },
          { name: "Which number is largest?", topic: "pk_compare_largest" },
          { name: "Which number is smaller?", topic: "pk_compare_smaller" },
          { name: "Which number is smallest?", topic: "pk_compare_smallest" },
        ],
      },
    ],
  },
  {
    domain: "M",
    name: "Patterns",
    color: "bg-brand-coral",
    standards: [
      {
        code: "M",
        desc: "Recognize and extend patterns.",
        skills: [
          { name: "Color patterns", topic: "pk_pattern_color" },
          { name: "Size patterns", topic: "pk_pattern_size" },
          { name: "Shape patterns", topic: "pk_pattern_shape" },
          { name: "What comes next?", topic: "pk_pattern_next" },
        ],
      },
    ],
  },
  {
    domain: "N",
    name: "Positions",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "N",
        desc: "Describe positions of objects.",
        skills: [
          { name: "In front of and behind", topic: "k_in_front_behind" },
          { name: "Inside and outside", topic: "pk_inside_outside" },
          { name: "Left and right", topic: "pk_left_right" },
          { name: "Left, middle, and right", topic: "k_left_middle_right" },
          { name: "Top and bottom", topic: "pk_top_bottom" },
          { name: "Top, middle, and bottom", topic: "k_top_middle_bottom" },
          { name: "Above and below", topic: "k_above_below" },
          { name: "Beside and next to", topic: "k_beside_next_to" },
        ],
      },
    ],
  },
  {
    domain: "O",
    name: "Classify",
    color: "bg-brand-green",
    standards: [
      {
        code: "O",
        desc: "Sort and classify objects.",
        skills: [
          { name: "Different", topic: "pk_same_diff_different" },
          { name: "Same", topic: "pk_same_diff_same" },
          { name: "Same and different", topic: "pk_same_diff_both" },
          { name: "Classify shapes by color", topic: "pk_classify_shapes_by_color" },
          { name: "Classify and sort by color", topic: "pk_classify_and_sort_by_color" },
          { name: "Classify and sort by shape", topic: "pk_classify_and_sort_by_shape" },
        ],
      },
    ],
  },
  {
    domain: "P",
    name: "Flat shapes",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "P",
        desc: "Identify and name flat (2D) shapes.",
        skills: [
          { name: "Circles", topic: "k_circles" },
          { name: "Triangles", topic: "k_triangles" },
          { name: "Rectangles", topic: "k_rectangles" },
          { name: "Squares", topic: "k_squares" },
          { name: "Circles, squares, and triangles", topic: "pk_shapes_circle_square_triangle" },
          { name: "Circles, squares, triangles, and rectangles", topic: "pk_shapes_circle_square_triangle_rectangle" },
          { name: "Name the shape", topic: "k_name_2d_3d_shapes" },
          { name: "Sort flat shapes", topic: "pk_sort_flat_shapes" },
        ],
      },
    ],
  },
  {
    domain: "Q",
    name: "Describe flat shapes",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "Q",
        desc: "Count sides and corners of flat shapes.",
        skills: [
          { name: "Count sides", topic: "pk_count_sides" },
          { name: "Count corners", topic: "pk_count_corners" },
          { name: "Count sides and corners", topic: "pk_count_sides_and_corners" },
        ],
      },
    ],
  },
  {
    domain: "R",
    name: "Solid shapes",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "R",
        desc: "Identify and name solid (3D) shapes.",
        skills: [
          { name: "Flat and solid shapes", topic: "k_flat_solid_shapes" },
          { name: "Describe solid shapes", topic: "k_describe_solid_shapes" },
          { name: "Spheres", topic: "pk_shape_sphere" },
          { name: "Cubes", topic: "pk_shape_cube3d" },
          { name: "Cones", topic: "pk_shape_cone" },
          { name: "Cylinders", topic: "pk_shape_cylinder" },
          { name: "Select solid shapes", topic: "pk_select_solid_shape" },
          { name: "Name the solid shape", topic: "pk_name_solid_shape" },
          { name: "Identify shapes traced from solids", topic: "pk_traced_shapes" },
        ],
      },
    ],
  },
  {
    domain: "S",
    name: "Shapes in the real world",
    color: "bg-brand-coral",
    standards: [
      {
        code: "S",
        desc: "Find flat and solid shapes around us.",
        skills: [
          { name: "Flat shapes in the real world", topic: "pk_flat_shapes_real_world" },
          { name: "Solid shapes in the real world I", topic: "pk_solid_shapes_real_world_1" },
          { name: "Solid shapes in the real world II", topic: "pk_solid_shapes_real_world_2" },
        ],
      },
    ],
  },
  {
    domain: "T",
    name: "Size",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "T",
        desc: "Compare size, weight, and capacity.",
        skills: [
          { name: "Long and short", topic: "k_long_and_short" },
          { name: "Tall and short", topic: "k_tall_and_short" },
          { name: "Wide and narrow", topic: "k_wide_and_narrow" },
          { name: "Light and heavy", topic: "k_light_and_heavy" },
          { name: "Holds more or less", topic: "k_holds_more_less" },
          { name: "Compare size, weight, and capacity", topic: "k_compare_size_weight" },
        ],
      },
    ],
  },
  {
    domain: "U",
    name: "Money",
    color: "bg-brand-green",
    standards: [
      {
        code: "U",
        desc: "Recognize coins and their values.",
        skills: [
          { name: "Pennies and nickels", topic: "pk_money_penny_nickel" },
          { name: "Dimes and quarters", topic: "pk_money_dime_quarter" },
          { name: "Pennies, nickels, dimes, and quarters", topic: "pk_money_all" },
          { name: "Count pennies", topic: "pk_money_count_pennies" },
        ],
      },
    ],
  },
  {
    domain: "V",
    name: "Addition up to 5",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "V",
        desc: "Put together and add within 5.",
        skills: [
          { name: "Put together numbers using cubes - sums up to 5", topic: "pk_add_put_together_5" },
          { name: "Build cube trains to add up to 5 - words", topic: "pk_add_cube_train_words_5" },
          { name: "Add with cubes - sums up to 5", topic: "pk_add_with_cubes_5" },
          { name: "Build cube trains to add up to 5 - addition sentences", topic: "pk_addition_sentences_cube_trains_5" },
          { name: "Add with pictures - sums up to 5", topic: "pk_add_with_pictures_5" },
          { name: "Addition sentences up to 5 - which model matches?", topic: "pk_addition_sentences_which_matches_5" },
          { name: "Addition sentences up to 5 - what does the model show?", topic: "pk_addition_sentences_what_shows_5" },
          { name: "Addition word problems with pictures - sums up to 5", topic: "k_addition_word_problems_5" },
        ],
      },
    ],
  },
  {
    domain: "W",
    name: "Addition up to 10",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "W",
        desc: "Put together and add within 10.",
        skills: [
          { name: "Put together numbers using cubes - sums up to 10", topic: "pk_add_put_together_10" },
          { name: "Build cube trains to add up to 10 - words", topic: "pk_add_cube_train_words_10" },
          { name: "Add with cubes - sums up to 10", topic: "pk_add_with_cubes_10" },
          { name: "Build cube trains to add up to 10 - addition sentences", topic: "pk_addition_sentences_cube_trains_10" },
          { name: "Add with pictures - sums up to 10", topic: "pk_add_with_pictures_10" },
          { name: "Addition sentences up to 10 - which model matches?", topic: "pk_addition_sentences_which_matches_10" },
          { name: "Addition sentences up to 10 - what does the model show?", topic: "pk_addition_sentences_what_shows_10" },
          { name: "Addition word problems with pictures - sums up to 10", topic: "k_addition_word_problems_10" },
        ],
      },
    ],
  },
  {
    domain: "X",
    name: "Subtraction up to 5",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "X",
        desc: "Take away and subtract within 5.",
        skills: [
          { name: "Take away cubes - numbers up to 5", topic: "pk_subtract_take_away_5" },
          { name: "Subtract with cubes - numbers up to 5", topic: "pk_subtract_with_cubes_5" },
          { name: "Subtract with pictures - numbers up to 5", topic: "pk_subtract_with_pictures_5" },
          { name: "Subtraction sentences up to 5 - which model matches?", topic: "pk_subtraction_sentences_which_matches_5" },
          { name: "Subtraction sentences up to 5 - what does the model show?", topic: "pk_subtraction_sentences_what_shows_5" },
          { name: "Subtraction sentences up to 5 - what does the cube train show?", topic: "pk_subtraction_sentences_cube_train_5" },
          { name: "Subtraction word problems with pictures - numbers up to 5", topic: "k_subtraction_word_problems_5" },
        ],
      },
    ],
  },
  {
    domain: "Y",
    name: "Subtraction up to 10",
    color: "bg-brand-coral",
    standards: [
      {
        code: "Y",
        desc: "Take away and subtract within 10.",
        skills: [
          { name: "Take away cubes - numbers up to 10", topic: "pk_subtract_take_away_10" },
          { name: "Subtract with cubes - numbers up to 10", topic: "pk_subtract_with_cubes_10" },
          { name: "Subtract with pictures - numbers up to 10", topic: "pk_subtract_with_pictures_10" },
          { name: "Subtraction sentences up to 10 - which model matches?", topic: "pk_subtraction_sentences_which_matches_10" },
          { name: "Subtraction sentences up to 10 - what does the model show?", topic: "pk_subtraction_sentences_what_shows_10" },
          { name: "Subtraction sentences up to 10 - what does the cube train show?", topic: "pk_subtraction_sentences_cube_train_10" },
          { name: "Subtraction word problems with pictures - numbers up to 10", topic: "k_subtraction_word_problems_10" },
        ],
      },
    ],
  },
];

const K_CCSS_PLAN = [
  {
    domain: "K.CC",
    name: "Counting and Cardinality",
    color: "bg-brand-coral",
    standards: [
      {
        code: "K.CC.1",
        desc: "Count to 100 by ones and by tens.",
        skills: [
          { name: "Count up to 10",       topic: "k_count_up_to_10" },
          { name: "Count up to 20",       topic: "k_count_up_to_20" },
          { name: "Count up to 30",       topic: "k_count_up_to_30" },
          { name: "Count up to 100",      topic: "k_count_up_to_100" },
          { name: "Skip-count by tens",   topic: "k_skip_count_by_tens" },
        ],
      },
      {
        code: "K.CC.2",
        desc: "Count forward beginning from a given number within the known sequence.",
        skills: [
          { name: "Count forward – up to 10",          topic: "k_count_forward_10" },
          { name: "Count forward – up to 20",          topic: "k_count_forward_20" },
          { name: "Count forward – up to 100",         topic: "k_count_forward_100" },
          { name: "Complete a counting sequence",      topic: "k_complete_sequence" },
        ],
      },
      {
        code: "K.CC.3",
        desc: "Write numbers from 0 to 20. Represent a number of objects with a written numeral 0–20.",
        skills: [
          { name: "Identify numbers – up to 10",   topic: "k_identify_numbers_10" },
          { name: "Identify numbers – up to 20",   topic: "k_identify_numbers_20" },
          { name: "Write the number – up to 20",   topic: "k_write_numbers_20" },
          { name: "Names of numbers – up to 20",   topic: "k_number_names_20" },
        ],
      },
      {
        code: "K.CC.4",
        desc: "Understand the relationship between numbers and quantities; connect counting to cardinality.",
        skills: [
          { name: "Count pictures – up to 10",         topic: "k_count_pictures_10" },
          { name: "Count on ten frames – up to 10",    topic: "k_ten_frames_10" },
          { name: "Represent numbers – up to 10",      topic: "k_represent_numbers_10" },
          { name: "Represent numbers – up to 20",      topic: "k_represent_numbers_20" },
          { name: "One more – up to 10",               topic: "k_one_more_10" },
          { name: "One more – up to 20",               topic: "k_one_more_20" },
        ],
      },
      {
        code: "K.CC.5",
        desc: "Count to answer \"how many?\" about as many as 20 things in a line, array, or circle, or 10 things scattered.",
        skills: [
          { name: "Count scattered pictures – up to 5",    topic: "k_count_scattered_5" },
          { name: "Count scattered shapes – up to 10",     topic: "k_count_scattered_10" },
          { name: "Count pictures in arrays – up to 20",   topic: "k_count_arrays_20" },
          { name: "Count out stickers – up to 10",         topic: "k_count_stickers_10" },
        ],
      },
      {
        code: "K.CC.6",
        desc: "Identify whether the number of objects in one group is greater than, less than, or equal to another group.",
        skills: [
          { name: "More, fewer, and same – up to 5",               topic: "k_more_fewer_5" },
          { name: "More, fewer, and same – up to 10",              topic: "k_more_fewer_10" },
          { name: "Are there enough?",                             topic: "k_are_there_enough" },
          { name: "Greater than, less than, or equal to – up to 10", topic: "k_greater_less_10" },
        ],
      },
      {
        code: "K.CC.7",
        desc: "Compare two numbers between 1 and 10 presented as written numerals.",
        skills: [
          { name: "Compare two numbers – up to 5",                   topic: "k_compare_numbers_5" },
          { name: "Compare two numbers – up to 10",                  topic: "k_compare_numbers_10" },
          { name: "Greater and less – compare two numbers up to 10", topic: "k_greater_less_compare_10" },
        ],
      },
    ],
  },
  {
    domain: "K.OA",
    name: "Operations and Algebraic Thinking",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "K.OA.1",
        desc: "Represent addition and subtraction with objects, fingers, drawings, or equations.",
        skills: [
          { name: "Addition sentences up to 5",    topic: "k_addition_sentences_5" },
          { name: "Subtraction sentences up to 5", topic: "k_subtraction_sentences_5" },
          { name: "Addition sentences up to 10",   topic: "k_addition_sentences_10" },
          { name: "Subtraction sentences up to 10",topic: "k_subtraction_sentences_10" },
        ],
      },
      {
        code: "K.OA.2",
        desc: "Solve addition and subtraction word problems, and add and subtract within 10.",
        skills: [
          { name: "Add – sums up to 5",                      topic: "k_add_sums_5" },
          { name: "Subtract – numbers up to 5",              topic: "k_subtract_5" },
          { name: "Addition word problems – sums up to 5",   topic: "k_addition_word_problems_5" },
          { name: "Subtraction word problems – up to 5",     topic: "k_subtraction_word_problems_5" },
          { name: "Add – sums up to 10",                     topic: "k_add_sums_10" },
          { name: "Subtract – numbers up to 10",             topic: "k_subtract_10" },
          { name: "Addition word problems – sums up to 10",  topic: "k_addition_word_problems_10" },
          { name: "Subtraction word problems – up to 10",    topic: "k_subtraction_word_problems_10" },
          { name: "Add or subtract – up to 10",              topic: "k_add_or_subtract_10" },
        ],
      },
      {
        code: "K.OA.3",
        desc: "Decompose numbers less than or equal to 10 into pairs in more than one way.",
        skills: [
          { name: "Take apart numbers – sums up to 5",           topic: "k_take_apart_5" },
          { name: "Decompose a number – up to 10",               topic: "k_decompose_10" },
          { name: "Take apart numbers in different ways – up to 10", topic: "k_take_apart_ways_10" },
        ],
      },
      {
        code: "K.OA.4",
        desc: "For any number from 1 to 9, find the number that makes 10 when added to the given number.",
        skills: [
          { name: "Count to fill a ten frame",                  topic: "k_fill_ten_frame" },
          { name: "Complete the addition sentence to make 10",  topic: "k_make_10_sentences" },
          { name: "Take apart 10 – addition sentences",         topic: "k_take_apart_10" },
        ],
      },
      {
        code: "K.OA.5",
        desc: "Fluently add and subtract within 5.",
        skills: [
          { name: "Add two numbers – sums up to 5",   topic: "k_add_two_5" },
          { name: "Subtract – numbers up to 5",       topic: "k_subtract_fluent_5" },
          { name: "Add or subtract – numbers up to 5",topic: "k_add_or_subtract_5" },
        ],
      },
    ],
  },
  {
    domain: "K.NBT",
    name: "Number and Operations in Base Ten",
    color: "bg-brand-green",
    standards: [
      {
        code: "K.NBT.1",
        desc: "Compose and decompose numbers from 11 to 19 into ten ones and some further ones.",
        skills: [
          { name: "Understand tens and ones – up to 20", topic: "k_tens_ones_20" },
          { name: "Make teen numbers with models",       topic: "k_teen_numbers_models" },
          { name: "Make teen numbers: addition sentences",topic: "k_teen_numbers_sentences" },
          { name: "Take apart teen numbers",             topic: "k_take_apart_teen" },
          { name: "Make and take apart teen numbers",    topic: "k_make_take_apart_teen" },
        ],
      },
    ],
  },
  {
    domain: "K.MD",
    name: "Measurement and Data",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "K.MD.1",
        desc: "Describe measurable attributes of objects, such as length or weight.",
        skills: [
          { name: "Long and short",      topic: "k_long_and_short" },
          { name: "Tall and short",      topic: "k_tall_and_short" },
          { name: "Wide and narrow",     topic: "k_wide_and_narrow" },
          { name: "Light and heavy",     topic: "k_light_and_heavy" },
          { name: "Holds more or less",  topic: "k_holds_more_less" },
        ],
      },
      {
        code: "K.MD.2",
        desc: "Directly compare two objects with a measurable attribute in common.",
        skills: [
          { name: "Compare length",                    topic: "k_compare_length" },
          { name: "Compare weight and capacity",       topic: "k_compare_weight_capacity" },
          { name: "Compare size, weight, and capacity",topic: "k_compare_size_weight" },
        ],
      },
      {
        code: "K.MD.3",
        desc: "Classify objects into given categories; count the numbers of objects in each category and sort by count.",
        skills: [
          { name: "Classify and sort by color", topic: "k_classify_by_color" },
          { name: "Classify and sort by shape", topic: "k_classify_by_shape" },
          { name: "Classify and sort",          topic: "k_classify_sort" },
          { name: "Sort and count objects",     topic: "k_sort_count" },
          { name: "Make picture graphs",        topic: "k_picture_graphs" },
          { name: "Sort shapes into a Venn diagram", topic: "k_venn_diagram" },
        ],
      },
    ],
  },
  {
    domain: "K.G",
    name: "Geometry",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "K.G.1",
        desc: "Describe objects in the environment using names of shapes, and describe their relative positions.",
        skills: [
          { name: "Shapes in the real world",   topic: "k_shapes_real_world" },
          { name: "In front of and behind",     topic: "k_in_front_behind" },
          { name: "Above and below",            topic: "k_above_below" },
          { name: "Beside and next to",         topic: "k_beside_next_to" },
          { name: "Left, middle, and right",    topic: "k_left_middle_right" },
          { name: "Top, middle, and bottom",    topic: "k_top_middle_bottom" },
        ],
      },
      {
        code: "K.G.2",
        desc: "Correctly name shapes regardless of their orientations or overall size.",
        skills: [
          { name: "Circles",                      topic: "k_circles" },
          { name: "Triangles",                    topic: "k_triangles" },
          { name: "Rectangles",                   topic: "k_rectangles" },
          { name: "Squares",                      topic: "k_squares" },
          { name: "Hexagons",                     topic: "k_hexagons" },
          { name: "Spheres, cubes, cones, cylinders", topic: "k_3d_shapes_name" },
          { name: "Name 2D and 3D shapes",        topic: "k_name_2d_3d_shapes" },
        ],
      },
      {
        code: "K.G.3",
        desc: "Identify shapes as two-dimensional (flat) or three-dimensional (solid).",
        skills: [
          { name: "Flat and solid shapes",            topic: "k_flat_solid_shapes" },
          { name: "2D and 3D shapes",                 topic: "k_2d_3d_shapes" },
        ],
      },
      {
        code: "K.G.4",
        desc: "Analyze and compare two- and three-dimensional shapes using informal language to describe their similarities, differences, parts, and attributes.",
        skills: [
          { name: "Count sides and corners",       topic: "k_sides_corners" },
          { name: "Square corners",                topic: "k_square_corners" },
          { name: "Equal sides",                   topic: "k_equal_sides" },
          { name: "Compare sides and corners",     topic: "k_compare_sides_corners" },
          { name: "Compare shapes using attributes",topic: "k_compare_shape_attributes" },
          { name: "Describe solid shapes",         topic: "k_describe_solid_shapes" },
        ],
      },
      {
        code: "K.G.6",
        desc: "Compose simple shapes to form larger shapes.",
        skills: [
          { name: "Compose simple 2D shapes",         topic: "k_compose_2d_shapes" },
          { name: "Build and take apart 3D shapes",   topic: "k_build_take_apart_3d" },
        ],
      },
    ],
  },
];

// ─── CA CCSS Grade 1 skill plan ──────────────────────────────────────────────
// Skill names and sample problems are original — targeting the same standard
// and conceptual skill as any third-party reference, never copied wording.
const G1_CCSS_PLAN = [
  {
    domain: "A",
    name: "Counting to 100",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "A",
        desc: "Counting to 100.",
        skills: [
          { name: "Counting review - within 10", topic: "g1_counting_review_10" },
          { name: "Count to fill a ten frame", topic: "counting_ten_frames" },
          { name: "Counting review - within 20", topic: "g1_counting_review_20" },
          { name: "Count on ten frames - within 40", topic: "g1_count_ten_frames_40" },
          { name: "Count objects to 100", topic: "g1_count_objects_100" },
          { name: "Count forward - within 100", topic: "k_count_forward_100" },
          { name: "Count backward - within 100", topic: "g1_count_backward_100" },
          { name: "Number lines - within 100", topic: "g1_number_line_100" },
          { name: "Count on the hundred chart", topic: "count_hundred_chart" },
          { name: "Hundred chart", topic: "count_hundred_chart" },
          { name: "Ordinal numbers", topic: "pk_ordinal_10" },
        ],
      },
    ],
  },
  {
    domain: "B",
    name: "Counting to 120",
    color: "bg-brand-green",
    standards: [
      {
        code: "B",
        desc: "Counting to 120.",
        skills: [
          { name: "Write the number you hear - within 120", topic: "g1_write_number_heard_120" },
          { name: "Count objects to 120", topic: "g1_count_objects_120" },
          { name: "Count within find the next number - within 120", topic: "g1_count_forward_120" },
          { name: "Count down to find the next number - within 120", topic: "g1_count_down_120" },
          { name: "Count forward - within 120", topic: "g1_count_forward_120" },
          { name: "Number lines - within 120", topic: "g1_number_line_120" },
          { name: "Count on a number chart - within 120", topic: "count_hundred_chart" },
          { name: "Sequences - count up and down by 1", topic: "g1_seq_up_down_1" },
          { name: "Writing numbers in words - convert words to digits", topic: "g1_words_to_digits_120" },
          { name: "Writing numbers in words - convert digits to words", topic: "g1_digits_to_words_120" },
        ],
      },
    ],
  },
  {
    domain: "C",
    name: "Skip-counting",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "C",
        desc: "Skip-counting.",
        skills: [
          { name: "Skip-count by twos", topic: "g1_skip_by_2" },
          { name: "Skip-count by fives", topic: "g1_skip_by_5" },
          { name: "Skip-count by tens", topic: "skip_count_by_tens" },
          { name: "Skip-count by tens - within 120", topic: "skip_count_by_tens" },
          { name: "Skip-count by twos, fives, and tens", topic: "g1_skip_by_2_5_10" },
          { name: "Count by twos, fives, and tens", topic: "g1_skip_by_2_5_10" },
          { name: "Skip-counting patterns - with tables", topic: "skip_count_by_tens" },
          { name: "Sequences - count up and down by 1, 2, 5, and 10", topic: "g1_count_up_down" },
          { name: "Count forward by twos - within 120", topic: "g1_skip_by_2_120" },
          { name: "Count forward by fives - within 120", topic: "g1_skip_by_5_120" },
          { name: "Count forward by tens - within 120", topic: "skip_count_by_tens" },
          { name: "Count forward by twos, fives, and tens - within 120", topic: "g1_skip_by_2_5_10_120" },
          { name: "Count forward by ones and tens - within 120", topic: "skip_count_by_tens" },
          { name: "Count objects by tens and ones - within 120", topic: "skip_count_by_tens" },
        ],
      },
    ],
  },
  {
    domain: "D",
    name: "Comparing within 10",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "D",
        desc: "Comparing within 10.",
        skills: [
          { name: "Compare: more or fewer?", topic: "compare_two_numbers" },
          { name: "Compare: how many more?", topic: "compare_two_numbers" },
          { name: "Compare: how many fewer?", topic: "compare_two_numbers" },
          { name: "Compare: how many more or fewer?", topic: "compare_two_numbers" },
          { name: "Compare numbers within 10 using words", topic: "compare_two_numbers" },
          { name: "Compare numbers within 10 using symbols", topic: "compare_two_numbers" },
        ],
      },
    ],
  },
  {
    domain: "E",
    name: "Understand addition",
    color: "bg-brand-coral text-white",
    standards: [
      {
        code: "E",
        desc: "Understand addition.",
        skills: [
          { name: "Add using cubes - sums within 10", topic: "g1_add_cubes_10" },
          { name: "Add using pictures - sums within 10", topic: "g1_add_pictures_visual_10" },
          { name: "Addition sentences within 10: which model fits?", topic: "pk_addition_sentences_which_matches_10" },
          { name: "Addition sentences within 10: what does the model represent?", topic: "pk_addition_sentences_what_shows_10" },
          { name: "Write an addition sentence for pictures - sums within 10", topic: "g1_add_write_sentence_10" },
          { name: "Addition sentences using number lines - sums within 10", topic: "g1_number_line_add_10" },
          { name: "Translate words into an addition sentence - sums within 10", topic: "g1_add_translate_words_10" },
        ],
      },
    ],
  },
  {
    domain: "F",
    name: "Addition strategies within 10",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "F",
        desc: "Addition strategies within 10.",
        skills: [
          { name: "Add in any order", topic: "g1_related_addition_facts" },
          { name: "Add by counting on - sums within 10", topic: "g1_add_facts_10" },
          { name: "Complete the addition sentence to make ten - using models", topic: "k_fill_ten_frame" },
          { name: "Complete the addition sentence to make ten", topic: "k_make_10_sentences" },
        ],
      },
    ],
  },
  {
    domain: "G",
    name: "Addition within 10",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "G",
        desc: "Addition within 10.",
        skills: [
          { name: "Addition facts - sums within 10", topic: "g1_add_facts_10" },
          { name: "Take apart numbers in different ways - sums within 10", topic: "g1_missing_add_10" },
          { name: "Make a number by adding - sums within 10", topic: "g1_missing_add_10" },
          { name: "Different ways to make a number - addition sentences within 10", topic: "g1_missing_add_10" },
        ],
      },
    ],
  },
  {
    domain: "H",
    name: "Addition word problems within 10",
    color: "bg-brand-green",
    standards: [
      {
        code: "H",
        desc: "Addition word problems within 10.",
        skills: [
          { name: "Addition word problems using pictures - sums within 10", topic: "g1_wp_add_pictures_10" },
          { name: "Write an addition sentence for word problems using pictures - sums within 10", topic: "g1_wp_add_sentence_pictures_10" },
          { name: "Build cube trains to solve addition word problems - sums within 10", topic: "g1_wp_add_pictures_10" },
          { name: "Addition word problems - sums within 10", topic: "g1_wp_add_numeric_10" },
          { name: "Model and write addition sentences for word problems - sums within 10", topic: "g1_wp_add_sentence_pictures_10" },
          { name: "Addition sentences for word problems - sums within 10", topic: "g1_wp_add_sentence_10" },
        ],
      },
    ],
  },
  {
    domain: "I",
    name: "Understand subtraction",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "I",
        desc: "Understand subtraction.",
        skills: [
          { name: "Subtract using cubes - within 10", topic: "g1_wp_sub_pictures_10" },
          { name: "Subtract using pictures - within 10", topic: "g1_sub_pictures_visual_10" },
          { name: "Subtraction sentences within 10: which model fits?", topic: "pk_subtraction_sentences_which_matches_10" },
          { name: "Subtraction sentences within 10: what does the model represent?", topic: "pk_subtraction_sentences_what_shows_10" },
          { name: "Subtraction sentences within 10: what does the cube train represent?", topic: "pk_subtraction_sentences_cube_train_10" },
          { name: "Write a subtraction sentence for pictures - within 10", topic: "g1_sub_write_sentence_10" },
          { name: "Subtraction sentences using number lines - within 10", topic: "g1_number_line_subtract_10" },
          { name: "Translate words into a subtraction sentence - within 10", topic: "g1_sub_translate_words_10" },
          { name: "Subtract zero and all", topic: "g1_add_subtract_zero" },
        ],
      },
    ],
  },
  {
    domain: "J",
    name: "Subtraction strategies within 10",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "J",
        desc: "Subtraction strategies within 10.",
        skills: [
          { name: "Subtract by counting back - within 10", topic: "g1_subtract_facts_10" },
          { name: "Subtract by counting on - within 10", topic: "g1_subtract_facts_10" },
          { name: "Relate addition and subtraction sentences - within 10", topic: "g1_related_addition_facts" },
          { name: "Use addition to subtract - within 10", topic: "g1_related_addition_facts" },
        ],
      },
    ],
  },
  {
    domain: "K",
    name: "Subtraction within 10",
    color: "bg-brand-coral text-white",
    standards: [
      {
        code: "K",
        desc: "Subtraction within 10.",
        skills: [
          { name: "Subtraction facts - within 10", topic: "g1_subtract_facts_10" },
          { name: "Make a number by subtracting - within 10", topic: "g1_missing_sub_10" },
          { name: "Different ways to make a number - subtraction sentences within 10", topic: "g1_missing_sub_10" },
          { name: "Ways to subtract from a number - subtraction sentences within 10", topic: "g1_missing_sub_10" },
        ],
      },
    ],
  },
  {
    domain: "L",
    name: "Subtraction word problems within 10",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "L",
        desc: "Subtraction word problems within 10.",
        skills: [
          { name: "Subtraction word problems using pictures - within 10", topic: "g1_wp_sub_pictures_10" },
          { name: "Write a subtraction sentence for word problems using pictures - within 10", topic: "g1_wp_sub_sentence_pictures_10" },
          { name: "Use cube trains to solve subtraction word problems - within 10", topic: "g1_wp_sub_pictures_10" },
          { name: "Subtraction word problems - within 10", topic: "g1_wp_sub_numeric_10" },
          { name: "Model and write subtraction sentences for word problems - within 10", topic: "g1_wp_sub_sentence_pictures_10" },
          { name: "Subtraction sentences for \"take apart\" word problems - within 10", topic: "g1_wp_take_apart_10" },
          { name: "Subtraction sentences for word problems - within 10", topic: "g1_wp_sub_sentence_10" },
        ],
      },
    ],
  },
  {
    domain: "M",
    name: "Addition and subtraction within 10",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "M",
        desc: "Addition and subtraction within 10.",
        skills: [
          { name: "Fact families - within 10", topic: "g1_fact_families" },
          { name: "Add and subtract with 0", topic: "g1_add_subtract_zero" },
          { name: "Addition and subtraction facts - within 10", topic: "g1_add_subtract_facts_10" },
          { name: "Sorting addition and subtraction facts - within 10", topic: "g1_add_subtract_facts_10" },
        ],
      },
    ],
  },
  {
    domain: "N",
    name: "Comparison word problems within 10",
    color: "bg-brand-green",
    standards: [
      {
        code: "N",
        desc: "Comparison word problems within 10.",
        skills: [
          { name: "Comparison word problems within 10: how many more?", topic: "g1_wp_cmp_more_10" },
          { name: "Subtraction sentences for comparison word problems within 10: how many more?", topic: "g1_wp_cmp_more_sentence_10" },
          { name: "Comparison word problems within 10: how many fewer?", topic: "g1_wp_cmp_fewer_10" },
          { name: "Subtraction sentences for comparison word problems within 10: how many fewer?", topic: "g1_wp_cmp_fewer_sentence_10" },
          { name: "Comparison word problems within 10: how many more or fewer?", topic: "g1_wp_cmp_more_fewer_10" },
          { name: "Subtraction sentences for comparison word problems within 10: how many more or fewer?", topic: "g1_wp_cmp_more_fewer_sentence_10" },
          { name: "Comparison word problems within 10: what is the larger amount?", topic: "g1_wp_cmp_larger_10" },
          { name: "Comparison word problems within 10: what is the smaller amount?", topic: "g1_wp_cmp_smaller_10" },
          { name: "Comparison word problems within 10", topic: "g1_wp_cmp_general_10" },
        ],
      },
    ],
  },
  {
    domain: "O",
    name: "Addition and subtraction word problems within 10",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "O",
        desc: "Addition and subtraction word problems within 10.",
        skills: [
          { name: "Addition and subtraction word problems using pictures - within 10", topic: "g1_wp_mixed_pictures_10" },
          { name: "Use cube trains to solve addition and subtraction word problems - within 10", topic: "g1_wp_mixed_pictures_10" },
          { name: "Word problems with unknown sums and differences - within 10", topic: "g1_wp_mixed_numeric_10" },
          { name: "Addition and subtraction sentences for word problems - within 10", topic: "g1_wp_mixed_sentence_10" },
          { name: "Word problems with change unknown - within 10", topic: "g1_wp_change_unknown_10" },
          { name: "Word problems with start unknown - within 10", topic: "g1_wp_start_unknown_10" },
          { name: "Word problems with one addend unknown - within 10", topic: "g1_wp_addend_unknown_10" },
          { name: "Word problems with both addends unknown - within 10", topic: "g1_wp_take_apart_10" },
          { name: "Word problems involving addition and subtraction - within 10", topic: "g1_wp_mixed_numeric_10" },
          { name: "Match word problems to addition and subtraction sentences - within 10", topic: "g1_wp_mixed_sentence_10" },
        ],
      },
    ],
  },
  {
    domain: "P",
    name: "Addition strategies within 20",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "P",
        desc: "Addition strategies within 20.",
        skills: [
          { name: "Related addition facts", topic: "g1_related_addition_facts" },
          { name: "Addition sentences using number lines - sums within 20", topic: "g1_number_line_add" },
          { name: "Add by counting on - sums within 20", topic: "g1_add_counting_on_20" },
          { name: "Add doubles - using models", topic: "g1_add_doubles_model" },
          { name: "Add doubles", topic: "g1_add_doubles" },
          { name: "Add doubles - complete the sentence", topic: "g1_add_doubles_sentence" },
          { name: "Add using doubles plus one", topic: "g1_doubles_plus_one" },
          { name: "Add using doubles minus one", topic: "g1_doubles_minus_one" },
          { name: "Add near doubles", topic: "g1_near_doubles" },
          { name: "Add three numbers - use doubles", topic: "g1_add_three_doubles" },
          { name: "Use ten frames to add", topic: "g1_add_ten_frames" },
          { name: "Make ten to add", topic: "g1_make_ten_add" },
          { name: "Add three numbers - make ten", topic: "g1_add_three_make_ten" },
        ],
      },
    ],
  },
  {
    domain: "Q",
    name: "Addition within 20",
    color: "bg-brand-coral text-white",
    standards: [
      {
        code: "Q",
        desc: "Addition within 20.",
        skills: [
          { name: "Adding 1", topic: "g1_add_1" },
          { name: "Adding 2", topic: "g1_add_2" },
          { name: "Adding 3", topic: "g1_add_3" },
          { name: "Adding 4", topic: "g1_add_4" },
          { name: "Adding 5", topic: "g1_add_5" },
          { name: "Adding 6", topic: "g1_add_6" },
          { name: "Adding 7", topic: "g1_add_7" },
          { name: "Adding 8", topic: "g1_add_8" },
          { name: "Adding 9", topic: "g1_add_9" },
          { name: "Adding 10", topic: "g1_add_10" },
          { name: "Adding 0", topic: "g1_add_0" },
          { name: "Addition facts - sums within 20", topic: "g1_add_facts_20" },
          { name: "Sorting addition facts - sums within 20", topic: "g1_add_facts_20" },
          { name: "Make a number by adding - sums within 20", topic: "g1_missing_addend" },
          { name: "Add three numbers", topic: "addition" },
        ],
      },
    ],
  },
  {
    domain: "R",
    name: "Addition word problems within 20",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "R",
        desc: "Addition word problems within 20.",
        skills: [
          { name: "Addition word problems using models - sums within 20", topic: "g1_wp_add_pictures_20" },
          { name: "Addition word problems - sums within 20", topic: "g1_wp_add_numeric_20" },
          { name: "Addition sentences for word problems - sums within 20", topic: "g1_wp_add_sentence_20" },
          { name: "Add three numbers - word problems", topic: "g1_add_three_wp" },
        ],
      },
    ],
  },
  {
    domain: "S",
    name: "Subtraction strategies within 20",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "S",
        desc: "Subtraction strategies within 20.",
        skills: [
          { name: "Related subtraction facts", topic: "g1_related_addition_facts" },
          { name: "Relate addition and subtraction sentences - within 20", topic: "g1_related_addition_facts" },
          { name: "Subtraction sentences using number lines - within 20", topic: "g1_number_line_subtract" },
          { name: "Subtract by counting back - within 20", topic: "g1_subtract_facts_20" },
          { name: "Subtracting to and from 10", topic: "g1_subtract_facts_10" },
          { name: "Use ten frames to subtract", topic: "g1_subtract_ten_frames" },
          { name: "Use ten to subtract", topic: "g1_subtract_facts_20" },
          { name: "Use addition to subtract - within 20", topic: "g1_related_addition_facts" },
          { name: "Subtract by counting on - within 20", topic: "g1_subtract_facts_20" },
          { name: "Count on and use ten to subtract - within 20", topic: "g1_subtract_facts_20" },
          { name: "Subtract doubles", topic: "g1_subtract_facts_20" },
        ],
      },
    ],
  },
  {
    domain: "T",
    name: "Subtraction within 20",
    color: "bg-brand-green",
    standards: [
      {
        code: "T",
        desc: "Subtraction within 20.",
        skills: [
          { name: "Subtracting 1", topic: "g1_subtract_1" },
          { name: "Subtracting 2", topic: "g1_subtract_2" },
          { name: "Subtracting 3", topic: "g1_subtract_3" },
          { name: "Subtracting 4", topic: "g1_subtract_4" },
          { name: "Subtracting 5", topic: "g1_subtract_5" },
          { name: "Subtracting 6", topic: "g1_subtract_6" },
          { name: "Subtracting 7", topic: "g1_subtract_7" },
          { name: "Subtracting 8", topic: "g1_subtract_8" },
          { name: "Subtracting 9", topic: "g1_subtract_9" },
          { name: "Subtracting 10", topic: "g1_subtract_10" },
          { name: "Subtracting 0", topic: "g1_subtract_0" },
          { name: "Subtraction facts - within 20", topic: "g1_subtract_facts_20" },
          { name: "Sorting subtraction facts - within 20", topic: "g1_subtract_facts_20" },
          { name: "Make a number by subtracting - within 20", topic: "g1_missing_sub_20" },
        ],
      },
    ],
  },
  {
    domain: "U",
    name: "Subtraction word problems within 20",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "U",
        desc: "Subtraction word problems within 20.",
        skills: [
          { name: "Subtraction word problems using models - within 20", topic: "g1_wp_sub_pictures_20" },
          { name: "Subtraction word problems - within 20", topic: "g1_wp_sub_numeric_20" },
          { name: "Subtraction sentences for word problems - within 20", topic: "g1_wp_sub_sentence_20" },
        ],
      },
    ],
  },
  {
    domain: "V",
    name: "Addition and subtraction within 20",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "V",
        desc: "Addition and subtraction within 20.",
        skills: [
          { name: "Addition and subtraction facts - within 20", topic: "g1_add_subtract_facts_20" },
          { name: "Addition and subtraction - ways to make a number", topic: "g1_add_subtract_facts_20" },
        ],
      },
    ],
  },
  {
    domain: "W",
    name: "Comparison word problems within 20",
    color: "bg-brand-coral text-white",
    standards: [
      {
        code: "W",
        desc: "Comparison word problems within 20.",
        skills: [
          { name: "Comparison word problems within 20: how many more or fewer?", topic: "g1_wp_cmp_more_fewer_20" },
          { name: "Comparison word problems within 20: what is the larger amount?", topic: "g1_wp_cmp_larger_20" },
          { name: "Comparison word problems within 20: what is the smaller amount?", topic: "g1_wp_cmp_smaller_20" },
          { name: "Comparison word problems within 20: part 1", topic: "g1_wp_cmp_more_fewer_20" },
          { name: "Comparison word problems within 20: part 2", topic: "g1_wp_cmp_larger_20" },
          { name: "Write number sentences for comparison word problems - within 20", topic: "g1_wp_cmp_sentence_20" },
        ],
      },
    ],
  },
  {
    domain: "X",
    name: "Addition and subtraction word problems within 20",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "X",
        desc: "Addition and subtraction word problems within 20.",
        skills: [
          { name: "Word problems with sum or difference unknown - within 20", topic: "g1_wp_mixed_numeric_20" },
          { name: "Addition and subtraction sentences for word problems - within 20", topic: "g1_wp_mixed_sentence_20" },
          { name: "Word problems with change unknown - within 20", topic: "g1_wp_change_unknown_20" },
          { name: "Write number sentences for word problems with change unknown - within 20", topic: "g1_wp_change_unknown_sentence_20" },
          { name: "Word problems with start unknown - within 20", topic: "g1_wp_start_unknown_20" },
          { name: "Write number sentences for word problems with start unknown - within 20", topic: "g1_wp_start_unknown_sentence_20" },
          { name: "Word problems with one addend unknown - within 20", topic: "g1_wp_addend_unknown_20" },
          { name: "Write number sentences for word problems with one addend unknown - within 20", topic: "g1_wp_addend_unknown_sentence_20" },
          { name: "Word problems with both addends unknown - within 20", topic: "g1_wp_addend_unknown_20" },
          { name: "Use models to solve word problems involving addition and subtraction - within 20", topic: "g1_wp_mixed_pictures_10" },
          { name: "Word problems involving addition and subtraction without comparisons - within 20", topic: "g1_wp_mixed_numeric_20" },
          { name: "Write number sentences for word problems without comparisons - within 20", topic: "g1_wp_mixed_sentence_20" },
          { name: "Word problems involving addition and subtraction - within 20", topic: "g1_wp_mixed_numeric_20" },
          { name: "Write number sentences for word problems - within 20", topic: "g1_wp_mixed_sentence_20" },
          { name: "Match word problems to addition and subtraction sentences - within 20", topic: "g1_wp_mixed_sentence_20" },
        ],
      },
    ],
  },
  {
    domain: "Y",
    name: "True or false equations",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "Y",
        desc: "True or false equations.",
        skills: [
          { name: "Addition and subtraction sentences to 10: which is true?", topic: "g1_which_true_mixed_10" },
          { name: "Which sign makes the number sentence true? - within 10", topic: "g1_which_sign_10" },
          { name: "Addition sentences: true or false?", topic: "g1_tf_addition" },
          { name: "Addition sentences: which is true?", topic: "g1_which_true_addition" },
          { name: "Subtraction sentences: true or false?", topic: "g1_tf_subtraction" },
          { name: "Subtraction sentences: which is true?", topic: "g1_which_true_subtraction" },
          { name: "Addition and subtraction sentences to 20: which is true?", topic: "g1_which_true_mixed_20" },
          { name: "Which operation sign makes the equation true?", topic: "g1_which_sign_20" },
        ],
      },
    ],
  },
  {
    domain: "Z",
    name: "Complete the equation",
    color: "bg-brand-green",
    standards: [
      {
        code: "Z",
        desc: "Complete the equation.",
        skills: [
          { name: "Complete the addition sentence - sums within 10", topic: "g1_missing_add_10" },
          { name: "Complete the subtraction sentence - within 10", topic: "g1_missing_sub_10" },
          { name: "Complete the addition sentence - sums within 20", topic: "g1_missing_addend" },
          { name: "Complete the subtraction sentence - within 20", topic: "g1_missing_sub_20" },
          { name: "Complete the addition or subtraction sentence - within 20", topic: "g1_missing_mixed_20" },
          { name: "Complete addition and subtraction equations - within 20", topic: "g1_missing_mixed_20" },
        ],
      },
    ],
  },
  {
    domain: "AA",
    name: "Place value",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "AA",
        desc: "Place value.",
        skills: [
          { name: "Build and break apart teen numbers", topic: "teen_numbers_place_value" },
          { name: "Place value models within 20", topic: "teen_numbers_place_value" },
          { name: "Build and break apart multiples of ten", topic: "g1_place_value_blocks" },
          { name: "Build two-digit numbers", topic: "g1_place_value_blocks" },
          { name: "Build and break apart two-digit numbers - using models", topic: "g1_place_value_blocks" },
          { name: "Place value models for two-digit numbers", topic: "g1_place_value_blocks" },
          { name: "Build and break apart two-digit numbers - without models", topic: "g1_place_value_blocks" },
          { name: "Different ways to make a number - tens and ones", topic: "g1_place_value_blocks" },
          { name: "Regroup tens and ones - ways to make a number", topic: "g1_place_value_blocks" },
          { name: "Regroup tens and ones", topic: "g1_place_value_blocks" },
        ],
      },
    ],
  },
  {
    domain: "BB",
    name: "Comparing within 100",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "BB",
        desc: "Comparing within 100.",
        skills: [
          { name: "Use place value to compare numbers within 100", topic: "g1_compare_2digit" },
          { name: "Compare numbers within 100 using words", topic: "g1_compare_2digit" },
          { name: "Compare numbers within 100 using symbols - using models", topic: "g1_compare_2digit" },
          { name: "Compare numbers using number lines", topic: "g1_number_line_compare" },
          { name: "Compare numbers within 100 using symbols", topic: "g1_compare_2digit" },
          { name: "Compare numbers within 100: word problems", topic: "g1_compare_2digit" },
          { name: "Write a number greater than or less than a given number - within 100", topic: "g1_compare_2digit" },
          { name: "Put numbers in order using number lines", topic: "g1_number_line_order" },
          { name: "Put numbers in order - within 100", topic: "g1_compare_2digit" },
        ],
      },
    ],
  },
  {
    domain: "CC",
    name: "Addition and subtraction: multiples of ten",
    color: "bg-brand-coral text-white",
    standards: [
      {
        code: "CC",
        desc: "Addition and subtraction: multiples of ten.",
        skills: [
          { name: "Ten more or less", topic: "g1_ten_more_less" },
          { name: "Add and subtract ten", topic: "g1_add_subtract_ten" },
          { name: "Add multiples of ten", topic: "g1_add_multiples_ten" },
          { name: "Subtract multiples of ten", topic: "g1_subtract_multiples_ten" },
          { name: "Add and subtract multiples of ten", topic: "g1_add_sub_multiples_ten" },
        ],
      },
    ],
  },
  {
    domain: "DD",
    name: "Addition: two digits",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "DD",
        desc: "Addition: two digits.",
        skills: [
          { name: "Use models to add a multiple of ten and a one-digit number", topic: "g1_add_ten_onedigit_model" },
          { name: "Add a multiple of ten and a one-digit number", topic: "g1_add_ten_onedigit" },
          { name: "Use models to add a multiple of ten and a two-digit number", topic: "g1_add_ten_twodigit_model" },
          { name: "Add a multiple of ten and a two-digit number", topic: "g1_add_ten_twodigit" },
          { name: "Use models to add a two-digit and a one-digit number - without regrouping", topic: "g1_add_2d1d_noregroup_model" },
          { name: "Add a two-digit and a one-digit number - without regrouping", topic: "g1_add_2d1d_noregroup" },
          { name: "Add by counting on - sums to 100", topic: "g1_add_counting_on_100" },
          { name: "Use a hundred chart to add tens or ones - without regrouping", topic: "g1_add_hundred_chart_tens_ones" },
          { name: "Use a hundred chart to add ones - with regrouping", topic: "g1_add_hundred_chart_ones_regroup" },
          { name: "Use models to add a two-digit and a one-digit number - with regrouping", topic: "g1_add_2d1d_regroup_model" },
          { name: "Add a two-digit and a one-digit number - with regrouping", topic: "g1_add_2d1d_regroup" },
          { name: "Use models to add two-digit numbers - without regrouping", topic: "g1_add_2d2d_noregroup_model" },
          { name: "Use place value to add two-digit numbers - without regrouping", topic: "g1_add_2d2d_noregroup_placevalue" },
          { name: "Add two-digit numbers without regrouping - sums to 100", topic: "g1_add_2d2d_noregroup" },
          { name: "Use models to add two-digit numbers - with regrouping", topic: "g1_add_2d2d_regroup_model" },
          { name: "Use place value to add two-digit numbers - with regrouping", topic: "g1_add_2d2d_regroup_placevalue" },
          { name: "Use place value to add numbers within two digits", topic: "g1_add_mixed_upto2digit" },
          { name: "Add two-digit numbers with regrouping - sums to 100", topic: "g1_add_2d2d_regroup" },
          { name: "Add numbers within two digits", topic: "g1_add_mixed_upto2digit" },
          { name: "Addition word problems - two-digit plus one-digit numbers", topic: "g1_add_word_problem_2d1d" },
          { name: "Addition sentences for word problems - two-digit plus one-digit numbers", topic: "g1_add_sentence_word_problem_2d1d" },
          { name: "Addition word problems - within two-digit numbers", topic: "g1_add_word_problem_upto2digit" },
        ],
      },
    ],
  },
  {
    domain: "EE",
    name: "Measurement",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "EE",
        desc: "Measurement.",
        skills: [
          { name: "Compare objects: length and height", topic: "measurement" },
          { name: "Order objects: length and height", topic: "measurement" },
          { name: "Indirect comparison", topic: "measurement" },
          { name: "Wide and narrow", topic: "measurement" },
          { name: "Light and heavy", topic: "measurement" },
          { name: "Holds more or less", topic: "measurement" },
          { name: "Compare size, weight, and capacity", topic: "measurement" },
          { name: "Measure length with objects", topic: "measurement" },
          { name: "Build cube trains to measure length", topic: "measurement" },
          { name: "Build cube trains to measure height", topic: "measurement" },
          { name: "Measure using an inch ruler", topic: "g2_ruler_length" },
          { name: "Customary units of length: word problems", topic: "g2_ruler_length" },
          { name: "Measure using a centimeter ruler", topic: "g2_ruler_length" },
          { name: "Metric units of length: word problems", topic: "g2_ruler_length" },
        ],
      },
    ],
  },
  {
    domain: "FF",
    name: "Time",
    color: "bg-brand-green",
    standards: [
      {
        code: "FF",
        desc: "Time.",
        skills: [
          { name: "Match digital clocks and times", topic: "g1_time_match_digital" },
          { name: "Match analog and digital clocks: to the hour", topic: "g1_time_match_analog_digital_hour" },
          { name: "Match analog clocks and times with words: to the hour", topic: "g1_time_match_words_hour" },
          { name: "Match analog clocks and times with words: to the half hour", topic: "g1_time_match_words_half" },
          { name: "Match analog clocks and times: to the half hour", topic: "g1_time_match_analog_half" },
          { name: "Match analog and digital clocks: to the half hour", topic: "g1_time_match_analog_digital_half" },
          { name: "Read clocks and write times", topic: "g1_time_hour_half" },
          { name: "Times of everyday events", topic: "g1_time_everyday_events" },
          { name: "Compare clocks", topic: "g1_time_compare_clocks" },
          { name: "Time and clocks: word problems", topic: "g1_time_word_problems" },
        ],
      },
    ],
  },
  {
    domain: "GG",
    name: "Days, months, and seasons",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "GG",
        desc: "Days, months, and seasons.",
        skills: [
          { name: "Days of the week", topic: "g1_days_of_week" },
          { name: "Months of the year", topic: "g1_months_of_year" },
          { name: "Seasons of the year", topic: "g1_seasons_of_year" },
          { name: "Read a calendar", topic: "g1_read_calendar" },
        ],
      },
    ],
  },
  {
    domain: "HH",
    name: "Money",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "HH",
        desc: "Money.",
        skills: [
          { name: "Names and values of common coins", topic: "g1_coin_names_values" },
          { name: "Names and values of all coins", topic: "g1_coin_names_values" },
          { name: "Count groups of like coins: pennies, nickels, or dimes", topic: "g1_count_like_coins_no_q" },
          { name: "Count groups of like coins: pennies, nickels, dimes, or quarters", topic: "g1_count_like_coins" },
          { name: "Count pennies, nickels, and dimes", topic: "g1_count_mixed_coins_no_q" },
          { name: "Count pennies, nickels, dimes, and quarters", topic: "g2_count_coins" },
          { name: "Equivalent groups of coins", topic: "g1_coin_equivalent" },
          { name: "Exchanging coins - using pictures", topic: "g1_coin_exchange_pictures" },
          { name: "Exchanging coins", topic: "g1_coin_exchange" },
          { name: "Money - word problems", topic: "g1_money_word_problem" },
          { name: "Purchases: do you have enough money?", topic: "g1_money_enough" },
          { name: "Compare money amounts", topic: "g1_money_compare" },
        ],
      },
    ],
  },
  {
    domain: "II",
    name: "Data and graphs",
    color: "bg-brand-coral text-white",
    standards: [
      {
        code: "II",
        desc: "Data and graphs.",
        skills: [
          { name: "Which picture graph is correct?", topic: "k_picture_graphs" },
          { name: "Make picture graphs using pictures", topic: "k_picture_graphs" },
          { name: "Interpret picture graphs", topic: "k_picture_graphs" },
          { name: "Make picture graphs with symbols", topic: "k_picture_graphs" },
          { name: "Tally marks - within 10", topic: "g1_tally_marks" },
          { name: "Which tally chart is correct?", topic: "g1_tally_which_correct" },
          { name: "Interpret tally charts", topic: "g1_tally_interpret" },
          { name: "Record data in tables", topic: "k_picture_graphs" },
          { name: "Interpret data in tables", topic: "k_picture_graphs" },
          { name: "Show data in different ways", topic: "k_picture_graphs" },
          { name: "Interpret picture graphs, tally charts, and tables", topic: "g1_data_interpret_mixed" },
          { name: "Interpret bar graphs: find the number in one category", topic: "g1_bar_find_category" },
          { name: "Interpret bar graphs", topic: "g1_bar_interpret" },
          { name: "Which bar graph is correct?", topic: "g1_bar_which_correct" },
        ],
      },
    ],
  },
  {
    domain: "JJ",
    name: "Patterns",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "JJ",
        desc: "Patterns.",
        skills: [
          { name: "Complete a pattern", topic: "patterns" },
          { name: "Make a pattern", topic: "patterns" },
          { name: "Growing patterns", topic: "patterns" },
          { name: "Find the next shape in a growing pattern", topic: "patterns" },
          { name: "Find the next row in a growing pattern", topic: "patterns" },
        ],
      },
    ],
  },
  {
    domain: "KK",
    name: "Two-dimensional shapes",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "KK",
        desc: "Two-dimensional shapes.",
        skills: [
          { name: "Select two-dimensional shapes", topic: "shapes" },
          { name: "Name the two-dimensional shape", topic: "shapes" },
          { name: "Rhombuses", topic: "shapes" },
          { name: "Count sides and corners", topic: "k_sides_corners" },
          { name: "Compare sides and corners", topic: "k_sides_corners" },
          { name: "Sort two-dimensional shapes", topic: "k_classify_by_shape" },
          { name: "Square corners", topic: "k_square_corners" },
          { name: "Equal sides", topic: "k_equal_sides" },
          { name: "Open and closed shapes", topic: "shapes" },
          { name: "Attributes of two-dimensional shapes", topic: "k_compare_shape_attributes" },
          { name: "Compare shapes using attributes", topic: "k_compare_shape_attributes" },
          { name: "Name the two-dimensional shape and explain your answer", topic: "shapes" },
          { name: "Compose two-dimensional shapes", topic: "k_compose_2d_shapes" },
          { name: "Compose two-dimensional shapes: which shapes can you make?", topic: "k_compose_2d_shapes" },
          { name: "Two-dimensional shapes in real-world objects", topic: "k_shapes_real_world" },
        ],
      },
    ],
  },
  {
    domain: "LL",
    name: "Three-dimensional shapes",
    color: "bg-brand-green",
    standards: [
      {
        code: "LL",
        desc: "Three-dimensional shapes.",
        skills: [
          { name: "Two-dimensional and three-dimensional shapes", topic: "k_2d_3d_shapes" },
          { name: "Cubes and rectangular prisms", topic: "k_name_2d_3d_shapes" },
          { name: "Select three-dimensional shapes", topic: "k_name_2d_3d_shapes" },
          { name: "Name the three-dimensional shape", topic: "k_name_2d_3d_shapes" },
          { name: "Count vertices, edges, and faces", topic: "k_describe_solid_shapes" },
          { name: "Compare vertices, edges, and faces", topic: "k_describe_solid_shapes" },
          { name: "Identify faces of three-dimensional shapes", topic: "k_describe_solid_shapes" },
          { name: "Identify shapes traced from solids", topic: "pk_traced_shapes" },
          { name: "Attributes of three-dimensional shapes", topic: "k_describe_solid_shapes" },
          { name: "Name the three-dimensional shape and explain your answer", topic: "k_name_2d_3d_shapes" },
          { name: "Sort three-dimensional shapes", topic: "k_flat_solid_shapes" },
          { name: "Compose three-dimensional shapes", topic: "k_build_shapes" },
          { name: "Compose bigger three-dimensional shapes", topic: "k_build_shapes" },
          { name: "Three-dimensional shapes in the real world I", topic: "k_shapes_real_world" },
          { name: "Three-dimensional shapes in the real world II", topic: "k_shapes_real_world" },
          { name: "Three-dimensional shapes in real-world objects", topic: "k_shapes_real_world" },
        ],
      },
    ],
  },
  {
    domain: "MM",
    name: "Venn diagrams",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "MM",
        desc: "Venn diagrams.",
        skills: [
          { name: "Count shapes in a Venn diagram", topic: "k_venn_diagram" },
          { name: "Sort shapes into a Venn diagram", topic: "k_venn_diagram" },
        ],
      },
    ],
  },
  {
    domain: "NN",
    name: "Fractions",
    color: "bg-brand-orange text-white",
    standards: [
      {
        code: "NN",
        desc: "Fractions.",
        skills: [
          { name: "Equal parts - 2 and 4 equal parts", topic: "g1_halves_fourths" },
          { name: "Equal parts - 2, 3, and 4 equal parts", topic: "g1_halves_fourths" },
          { name: "Identify halves", topic: "g1_halves_fourths" },
          { name: "Identify fourths", topic: "g1_halves_fourths" },
          { name: "Identify halves and fourths", topic: "g1_halves_fourths" },
          { name: "Make halves", topic: "g1_halves_fourths" },
          { name: "Make fourths", topic: "g1_halves_fourths" },
          { name: "Make halves and fourths", topic: "g1_halves_fourths" },
          { name: "Make halves and fourths in different ways", topic: "g1_halves_fourths" },
          { name: "Compare halves and fourths", topic: "g1_halves_fourths" },
        ],
      },
    ],
  },
  {
    domain: "OO",
    name: "Financial literacy",
    color: "bg-brand-coral text-white",
    standards: [
      {
        code: "OO",
        desc: "Financial literacy.",
        skills: [
          { name: "Income", topic: "money" },
          { name: "Spending and saving", topic: "money" },
          { name: "Giving to charity", topic: "money" },
        ],
      },
    ],
  },
];

// ─── CA CCSS Grade 2 skill plan ──────────────────────────────────────────────
const G2_CCSS_PLAN = [
  {
    domain: "2.OA",
    name: "Operations and Algebraic Thinking",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "2.OA.A.1",
        desc: "Use addition and subtraction within 100 to solve one- and two-step word problems.",
        skills: [
          { name: "One-step word problems – within 100", topic: "g2_word_problem_1step_100" },
          { name: "Two-step word problems – within 100", topic: "g2_word_problem_2step_100" },
          { name: "Multi-step challenge problems",        topic: "g2_challenge_multistep_100" },
        ],
      },
      {
        code: "2.OA.B.2",
        desc: "Fluently add and subtract within 20 using mental strategies.",
        skills: [
          { name: "Addition facts – within 20",    topic: "g2_addition_facts_20" },
          { name: "Subtraction facts – within 20", topic: "g2_subtraction_facts_20" },
          { name: "Mixed fact fluency",             topic: "g2_mixed_fact_fluency_20" },
        ],
      },
      {
        code: "2.OA.C.3",
        desc: "Determine whether a group of objects has an odd or even number of members.",
        skills: [
          { name: "Even or odd – with objects", topic: "g2_even_odd" },
          { name: "Even or odd – with numbers", topic: "g2_even_odd" },
        ],
      },
      {
        code: "2.OA.C.4",
        desc: "Use addition to find the total number of objects arranged in rectangular arrays.",
        skills: [
          { name: "Count an array with addition",       topic: "g2_arrays" },
          { name: "Write a repeated-addition sentence",  topic: "g2_arrays" },
        ],
      },
    ],
  },
  {
    domain: "2.NBT",
    name: "Number and Operations in Base Ten",
    color: "bg-brand-green",
    standards: [
      {
        code: "2.NBT.A.1",
        desc: "Understand that the three digits of a three-digit number represent hundreds, tens, and ones.",
        skills: [
          { name: "Hundreds, tens, and ones – blocks", topic: "g2_place_value_1000" },
          { name: "Place value charts",                 topic: "g2_place_value_1000" },
        ],
      },
      {
        code: "2.NBT.A.2",
        desc: "Count within 1000; skip-count by 5s, 10s, and 100s.",
        skills: [
          { name: "Skip-count by 5s",   topic: "g2_skip_count_5" },
          { name: "Skip-count by 10s",  topic: "g2_skip_count_10" },
          { name: "Skip-count by 100s", topic: "g2_skip_count_100" },
        ],
      },
      {
        code: "2.NBT.A.3",
        desc: "Read and write numbers to 1000 using numerals, number names, and expanded form.",
        skills: [
          { name: "Read numbers to 1000",    topic: "g2_read_numbers_1000" },
          { name: "Write numbers to 1000",   topic: "g2_write_numbers_1000" },
          { name: "Expanded form to 1000",   topic: "g2_expanded_form_1000" },
        ],
      },
      {
        code: "2.NBT.A.4",
        desc: "Compare two three-digit numbers based on hundreds, tens, and ones digits, using >, =, and < symbols.",
        skills: [
          { name: "Compare three-digit numbers", topic: "g2_compare_3digit" },
          { name: "Order three numbers",          topic: "g2_order_3numbers" },
        ],
      },
      {
        code: "2.NBT.B.5",
        desc: "Fluently add and subtract within 100 using strategies based on place value.",
        skills: [
          { name: "Add within 100 – no regrouping",   topic: "addition" },
          { name: "Add within 100 – with regrouping", topic: "addition" },
          { name: "Subtract within 100",              topic: "subtraction" },
        ],
      },
      {
        code: "2.NBT.B.6",
        desc: "Add up to four two-digit numbers using strategies based on place value.",
        skills: [
          { name: "Add three two-digit numbers", topic: "addition" },
          { name: "Add four two-digit numbers",  topic: "addition" },
        ],
      },
      {
        code: "2.NBT.B.7",
        desc: "Add and subtract within 1000 using concrete models, drawings, and strategies based on place value.",
        skills: [
          { name: "Add within 1000 – models",      topic: "g2_add_models_1000" },
          { name: "Subtract within 1000 – models", topic: "g2_subtract_models_1000" },
        ],
      },
      {
        code: "2.NBT.B.8",
        desc: "Mentally add 10 or 100 to a given number 100–900, and mentally subtract 10 or 100.",
        skills: [
          { name: "Add 10 or 100 mentally",      topic: "addition" },
          { name: "Subtract 10 or 100 mentally", topic: "subtraction" },
        ],
      },
      {
        code: "2.NBT.B.9",
        desc: "Explain why addition and subtraction strategies work, using place value and the properties of operations.",
        skills: [
          { name: "Explain an addition strategy", topic: "addition" },
        ],
      },
    ],
  },
  {
    domain: "2.MD",
    name: "Measurement and Data",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "2.MD.A.1",
        desc: "Measure the length of an object using rulers, yardsticks, meter sticks, and measuring tapes.",
        skills: [
          { name: "Measure with a ruler", topic: "g2_ruler_length" },
        ],
      },
      {
        code: "2.MD.A.2",
        desc: "Measure the length of an object twice using length units of different lengths; describe how the two measurements relate.",
        skills: [
          { name: "Measure with two different units", topic: "g2_ruler_length" },
        ],
      },
      {
        code: "2.MD.A.3",
        desc: "Estimate lengths using inches, feet, centimeters, and meters.",
        skills: [
          { name: "Estimate then measure", topic: "g2_ruler_length" },
        ],
      },
      {
        code: "2.MD.A.4",
        desc: "Measure to determine how much longer one object is than another, expressing the difference in length units.",
        skills: [
          { name: "Find the difference in length", topic: "g2_ruler_length" },
        ],
      },
      {
        code: "2.MD.B.5",
        desc: "Use addition and subtraction within 100 to solve word problems involving lengths given in the same units.",
        skills: [
          { name: "Length word problems", topic: "g2_length_word_problems" },
        ],
      },
      {
        code: "2.MD.B.6",
        desc: "Represent whole numbers as lengths on a number line; show sums and differences within 100 on the line.",
        skills: [
          { name: "Locate numbers on a number line", topic: "g1_number_line_add" },
        ],
      },
      {
        code: "2.MD.C.7",
        desc: "Tell and write time from analog and digital clocks to the nearest 5 minutes.",
        skills: [
          { name: "Tell time to 5 minutes", topic: "g2_time_5min" },
          { name: "Match clocks to times",  topic: "g2_time_5min" },
        ],
      },
      {
        code: "2.MD.C.8",
        desc: "Solve word problems involving dollar bills, quarters, dimes, nickels, and pennies.",
        skills: [
          { name: "Count a collection of coins", topic: "g2_count_coins" },
        ],
      },
      {
        code: "2.MD.D.9",
        desc: "Generate measurement data by measuring several objects; show the measurements by making a line plot.",
        skills: [
          { name: "Make a line plot", topic: "measurement" },
          { name: "Read a line plot", topic: "measurement" },
        ],
      },
      {
        code: "2.MD.D.10",
        desc: "Draw a picture graph and a bar graph to represent a data set; solve problems using the graphs.",
        skills: [
          { name: "Read picture graphs", topic: "g2_read_picture_graphs" },
          { name: "Read bar graphs",     topic: "g2_read_bar_graphs" },
        ],
      },
    ],
  },
  {
    domain: "2.G",
    name: "Geometry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "2.G.A.1",
        desc: "Recognize and draw shapes with specified attributes; identify triangles, quadrilaterals, pentagons, hexagons, and cubes.",
        skills: [
          { name: "Identify shapes by attributes", topic: "shapes" },
          { name: "Draw shapes with given attributes", topic: "shapes" },
        ],
      },
      {
        code: "2.G.A.2",
        desc: "Partition a rectangle into rows and columns of same-size squares; count to find the total number.",
        skills: [
          { name: "Partition into rows and columns", topic: "shapes" },
          { name: "Count total squares in a grid",    topic: "shapes" },
        ],
      },
      {
        code: "2.G.A.3",
        desc: "Partition circles and rectangles into two, three, or four equal shares; describe the shares.",
        skills: [
          { name: "Partition into equal shares",   topic: "fractions" },
          { name: "Describe shares with fractions", topic: "fractions" },
        ],
      },
    ],
  },
];

// ─── CA CCSS Grade 3 skill plan ──────────────────────────────────────────────
const G3_CCSS_PLAN = [
  {
    domain: "3.OA",
    name: "Operations and Algebraic Thinking",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "3.OA.A.1",
        desc: "Interpret products of whole numbers as the total number of objects in equal groups.",
        skills: [
          { name: "Multiplication as equal groups", topic: "g3_multiplication_equal_groups" },
          { name: "Write a multiplication equation", topic: "g3_write_multiplication_equation" },
        ],
      },
      {
        code: "3.OA.A.2",
        desc: "Interpret quotients as the number of groups or the number of objects in each group.",
        skills: [
          { name: "Division as equal sharing",    topic: "g3_division_equal_sharing" },
          { name: "Write a division equation",     topic: "g3_write_division_equation" },
        ],
      },
      {
        code: "3.OA.A.3",
        desc: "Use multiplication and division within 100 to solve word problems.",
        skills: [
          { name: "Multiplication word problems", topic: "g3_multiplication_word_problems" },
          { name: "Division word problems",        topic: "g3_division_word_problems" },
          { name: "Mixed multiplication/division word problems", topic: "g3_mixed_muldiv_word_problems" },
        ],
      },
      {
        code: "3.OA.A.4",
        desc: "Determine the unknown whole number in a multiplication or division equation.",
        skills: [
          { name: "Find the missing factor",  topic: "alg_one_step_eq" },
          { name: "Find the missing divisor or quotient", topic: "alg_one_step_eq" },
        ],
      },
      {
        code: "3.OA.B.5",
        desc: "Apply the commutative, associative, and distributive properties as strategies to multiply and divide.",
        skills: [
          { name: "Commutative property of multiplication", topic: "multiplication" },
          { name: "Distributive property",                   topic: "multiplication" },
        ],
      },
      {
        code: "3.OA.B.6",
        desc: "Understand division as an unknown-factor problem.",
        skills: [
          { name: "Division as an unknown factor", topic: "division" },
          { name: "Fact family triangles",           topic: "multiplication" },
        ],
      },
      {
        code: "3.OA.C.7",
        desc: "Fluently multiply and divide within 100, using strategies and knowing facts from memory.",
        skills: [
          { name: "Multiplication facts – within 100", topic: "multiplication" },
          { name: "Division facts – within 100",         topic: "division" },
        ],
      },
      {
        code: "3.OA.D.8",
        desc: "Solve two-step word problems using the four operations.",
        skills: [
          { name: "Two-step word problems", topic: "g3_two_step_word_problems" },
        ],
      },
      {
        code: "3.OA.D.9",
        desc: "Identify arithmetic patterns and explain them using properties of operations.",
        skills: [
          { name: "Identify and extend patterns", topic: "counting" },
        ],
      },
    ],
  },
  {
    domain: "3.NBT",
    name: "Number and Operations in Base Ten",
    color: "bg-brand-green",
    standards: [
      {
        code: "3.NBT.A.1",
        desc: "Use place value understanding to round whole numbers to the nearest 10 or 100.",
        skills: [
          { name: "Round to the nearest 10",  topic: "num_rounding" },
          { name: "Round to the nearest 100", topic: "num_rounding" },
        ],
      },
      {
        code: "3.NBT.A.2",
        desc: "Fluently add and subtract within 1000 using strategies based on place value.",
        skills: [
          { name: "Add within 1000",      topic: "addition" },
          { name: "Subtract within 1000", topic: "subtraction" },
        ],
      },
      {
        code: "3.NBT.A.3",
        desc: "Multiply one-digit whole numbers by multiples of 10 in the range 10–90.",
        skills: [
          { name: "Multiply by multiples of ten", topic: "num_multiply_by_tens" },
        ],
      },
    ],
  },
  {
    domain: "3.NF",
    name: "Number and Operations—Fractions",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "3.NF.A.1",
        desc: "Understand a fraction 1/b as one part of a whole partitioned into b equal parts, and a/b as a parts of size 1/b.",
        skills: [
          { name: "Identify unit fractions",       topic: "num_compare_fractions" },
          { name: "Model fractions with shapes",   topic: "g3_model_fractions_shapes" },
        ],
      },
      {
        code: "3.NF.A.2",
        desc: "Understand a fraction as a number on the number line.",
        skills: [
          { name: "Plot unit fractions on a number line", topic: "num_compare_fractions" },
          { name: "Plot non-unit fractions on a number line", topic: "num_compare_fractions" },
        ],
      },
      {
        code: "3.NF.A.3",
        desc: "Explain equivalence of fractions, and compare fractions by reasoning about their size.",
        skills: [
          { name: "Equivalent fractions – visual models", topic: "g3_equivalent_fractions_visual" },
          { name: "Compare fractions",                     topic: "num_compare_fractions" },
        ],
      },
    ],
  },
  {
    domain: "3.MD",
    name: "Measurement and Data",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "3.MD.A.1",
        desc: "Tell and write time to the nearest minute; solve word problems involving elapsed time.",
        skills: [
          { name: "Tell time to the minute",   topic: "g3_time_minute" },
          { name: "Elapsed time word problems", topic: "num_elapsed_time" },
        ],
      },
      {
        code: "3.MD.A.2",
        desc: "Measure and estimate liquid volumes and masses of objects; solve one-step word problems.",
        skills: [
          { name: "Measure liquid volume and mass", topic: "measurement" },
        ],
      },
      {
        code: "3.MD.B.3",
        desc: "Draw a scaled picture graph and a scaled bar graph; solve problems using the graphs.",
        skills: [
          { name: "Read scaled bar graphs",    topic: "g3_read_scaled_bar_graphs" },
          { name: "Read scaled picture graphs", topic: "g3_read_scaled_picture_graphs" },
        ],
      },
      {
        code: "3.MD.B.4",
        desc: "Measure lengths to the nearest half and fourth inch; show the data on a line plot.",
        skills: [
          { name: "Measure to the nearest half/fourth inch", topic: "measurement" },
        ],
      },
      {
        code: "3.MD.C.5",
        desc: "Recognize area as an attribute of plane figures, and understand concepts of area measurement.",
        skills: [
          { name: "Recognize area as an attribute", topic: "geometry" },
        ],
      },
      {
        code: "3.MD.C.6",
        desc: "Measure areas by counting unit squares.",
        skills: [
          { name: "Count unit squares to find area", topic: "geometry" },
        ],
      },
      {
        code: "3.MD.C.7",
        desc: "Relate area to the operations of multiplication and addition.",
        skills: [
          { name: "Find area by multiplying side lengths", topic: "geometry" },
          { name: "Find the area of composite figures",     topic: "geometry" },
        ],
      },
      {
        code: "3.MD.D.8",
        desc: "Solve real-world problems involving perimeters of polygons.",
        skills: [
          { name: "Find the perimeter of a polygon", topic: "geometry" },
          { name: "Find a missing side length",       topic: "geometry" },
        ],
      },
    ],
  },
  {
    domain: "3.G",
    name: "Geometry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "3.G.A.1",
        desc: "Understand that shapes in different categories may share attributes, and that this can define a larger category.",
        skills: [
          { name: "Classify quadrilaterals",      topic: "geo_classify_quadrilateral" },
          { name: "Shape category hierarchies",   topic: "geo_classify_quadrilateral" },
        ],
      },
      {
        code: "3.G.A.2",
        desc: "Partition shapes into parts with equal areas; express the area of each part as a unit fraction.",
        skills: [
          { name: "Partition shapes into equal areas", topic: "num_equivalent_fractions" },
        ],
      },
    ],
  },
];

// ─── CA CCSS Grade 4 skill plan ──────────────────────────────────────────────
const G4_CCSS_PLAN = [
  {
    domain: "4.OA",
    name: "Operations and Algebraic Thinking",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "4.OA.A.1",
        desc: "Interpret a multiplication equation as a comparison (e.g. 35 = 5 × 7 as 35 is 5 times as many as 7).",
        skills: [
          { name: "Interpret multiplicative comparisons", topic: "multiplication" },
          { name: "Write a comparison equation",           topic: "multiplication" },
        ],
      },
      {
        code: "4.OA.A.2",
        desc: "Multiply or divide to solve word problems involving multiplicative comparison.",
        skills: [
          { name: "Multiplicative comparison word problems", topic: "multiplication" },
        ],
      },
      {
        code: "4.OA.A.3",
        desc: "Solve multi-step word problems with whole numbers; interpret remainders.",
        skills: [
          { name: "Multi-step word problems",  topic: "multiplication" },
          { name: "Interpret the remainder",    topic: "num_long_division" },
        ],
      },
      {
        code: "4.OA.B.4",
        desc: "Find all factor pairs for a whole number; determine if a number is prime or composite.",
        skills: [
          { name: "Find factor pairs",           topic: "multiplication" },
          { name: "Prime or composite?",         topic: "multiplication" },
        ],
      },
      {
        code: "4.OA.C.5",
        desc: "Generate and analyze a number or shape pattern that follows a given rule.",
        skills: [
          { name: "Extend a number pattern",  topic: "counting" },
          { name: "Extend a shape pattern",    topic: "counting" },
        ],
      },
    ],
  },
  {
    domain: "4.NBT",
    name: "Number and Operations in Base Ten",
    color: "bg-brand-green",
    standards: [
      {
        code: "4.NBT.A.1",
        desc: "Recognize that a digit in one place represents 10 times what it represents in the place to its right.",
        skills: [
          { name: "Place value relationships", topic: "num_place_value" },
        ],
      },
      {
        code: "4.NBT.A.2",
        desc: "Read and write multi-digit whole numbers using base-ten numerals, number names, and expanded form.",
        skills: [
          { name: "Read multi-digit numbers",  topic: "num_place_value" },
          { name: "Write in expanded form",     topic: "num_place_value" },
        ],
      },
      {
        code: "4.NBT.A.3",
        desc: "Round multi-digit whole numbers to any place.",
        skills: [
          { name: "Round to any place value", topic: "num_rounding" },
        ],
      },
      {
        code: "4.NBT.B.4",
        desc: "Fluently add and subtract multi-digit whole numbers using the standard algorithm.",
        skills: [
          { name: "Add multi-digit numbers",      topic: "addition" },
          { name: "Subtract multi-digit numbers", topic: "subtraction" },
        ],
      },
      {
        code: "4.NBT.B.5",
        desc: "Multiply a whole number of up to four digits by a one-digit number, and multiply two two-digit numbers.",
        skills: [
          { name: "Multiply up to 4 digits by 1 digit", topic: "num_multidigit_multiply" },
          { name: "Multiply two two-digit numbers",       topic: "num_multidigit_multiply" },
        ],
      },
      {
        code: "4.NBT.B.6",
        desc: "Find whole-number quotients and remainders with up to four-digit dividends and one-digit divisors.",
        skills: [
          { name: "Long division with remainders", topic: "num_long_division" },
        ],
      },
    ],
  },
  {
    domain: "4.NF",
    name: "Number and Operations—Fractions",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "4.NF.A.1",
        desc: "Explain fraction equivalence using visual models; generate equivalent fractions.",
        skills: [
          { name: "Generate equivalent fractions", topic: "num_equivalent_fractions" },
        ],
      },
      {
        code: "4.NF.A.2",
        desc: "Compare two fractions with different numerators and denominators.",
        skills: [
          { name: "Compare fractions – unlike terms", topic: "num_compare_fractions" },
          { name: "Compare using benchmark fractions", topic: "num_compare_fractions" },
        ],
      },
      {
        code: "4.NF.B.3",
        desc: "Understand addition and subtraction of fractions with like denominators.",
        skills: [
          { name: "Add fractions – like denominators",      topic: "num_add_sub_fractions" },
          { name: "Subtract fractions – like denominators", topic: "num_add_sub_fractions" },
          { name: "Add and subtract mixed numbers",          topic: "num_mixed_numbers" },
        ],
      },
      {
        code: "4.NF.B.4",
        desc: "Multiply a fraction by a whole number.",
        skills: [
          { name: "Multiply a fraction by a whole number", topic: "num_multiply_fractions" },
        ],
      },
      {
        code: "4.NF.C.5",
        desc: "Express a fraction with denominator 10 as an equivalent fraction with denominator 100, and add the two fractions.",
        skills: [
          { name: "Convert tenths to hundredths", topic: "num_equivalent_fractions" },
        ],
      },
      {
        code: "4.NF.C.6",
        desc: "Use decimal notation for fractions with denominators 10 or 100.",
        skills: [
          { name: "Write fractions as decimals", topic: "num_decimal_fraction" },
        ],
      },
      {
        code: "4.NF.C.7",
        desc: "Compare two decimals to hundredths by reasoning about their size.",
        skills: [
          { name: "Compare decimals to hundredths", topic: "num_compare_decimals" },
        ],
      },
    ],
  },
  {
    domain: "4.MD",
    name: "Measurement and Data",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "4.MD.A.1",
        desc: "Know relative sizes of measurement units; convert measurements within one system.",
        skills: [
          { name: "Convert measurement units", topic: "geo_unit_conversion" },
        ],
      },
      {
        code: "4.MD.A.2",
        desc: "Use the four operations to solve word problems involving distances, time, volumes, masses, and money.",
        skills: [
          { name: "Measurement word problems", topic: "measurement" },
        ],
      },
      {
        code: "4.MD.A.3",
        desc: "Apply the area and perimeter formulas for rectangles to solve real-world problems.",
        skills: [
          { name: "Find area using the formula",      topic: "geometry" },
          { name: "Find perimeter using the formula", topic: "geometry" },
        ],
      },
      {
        code: "4.MD.B.4",
        desc: "Make a line plot to display a data set of measurements in fractions of a unit; solve problems using the data.",
        skills: [
          { name: "Make a line plot with fractions", topic: "measurement" },
        ],
      },
      {
        code: "4.MD.C.5",
        desc: "Recognize angles as geometric shapes formed by two rays; understand concepts of angle measurement.",
        skills: [
          { name: "Identify and classify angles", topic: "geo_angle_relationships" },
        ],
      },
      {
        code: "4.MD.C.6",
        desc: "Measure angles in whole-number degrees using a protractor; sketch angles of a specified measure.",
        skills: [
          { name: "Measure an angle with a protractor", topic: "geo_angle_relationships" },
          { name: "Sketch an angle of a given measure",  topic: "geo_angle_relationships" },
        ],
      },
      {
        code: "4.MD.C.7",
        desc: "Recognize angle measure as additive; solve addition and subtraction problems to find unknown angles.",
        skills: [
          { name: "Find an unknown angle measure", topic: "geo_angle_solve_x" },
        ],
      },
    ],
  },
  {
    domain: "4.G",
    name: "Geometry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "4.G.A.1",
        desc: "Draw and identify points, lines, line segments, rays, angles, and perpendicular/parallel lines.",
        skills: [
          { name: "Identify points, lines, and rays",       topic: "geo_polygon_vocab" },
          { name: "Identify parallel and perpendicular lines", topic: "geo_polygon_vocab" },
        ],
      },
      {
        code: "4.G.A.2",
        desc: "Classify two-dimensional figures based on the presence of parallel/perpendicular lines and angles.",
        skills: [
          { name: "Classify shapes by their lines and angles", topic: "geo_classify_quadrilateral" },
        ],
      },
      {
        code: "4.G.A.3",
        desc: "Recognize a line of symmetry; identify line-symmetric figures and draw lines of symmetry.",
        skills: [
          { name: "Identify a line of symmetry",   topic: "geo_symmetry" },
          { name: "Draw lines of symmetry",        topic: "geo_symmetry" },
        ],
      },
    ],
  },
];

const G5_CCSS_PLAN = [
  {
    domain: "5.OA",
    name: "Operations and Algebraic Thinking",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "5.OA.A.1",
        desc: "Use parentheses, brackets, or braces in numerical expressions, and evaluate expressions with these symbols.",
        skills: [
          { name: "Evaluate expressions with parentheses", topic: "num_order_of_operations" },
        ],
      },
      {
        code: "5.OA.A.2",
        desc: "Write simple expressions that record calculations, and interpret numerical expressions without evaluating them.",
        skills: [
          { name: "Write a numerical expression",      topic: "alg_write_expression" },
          { name: "Interpret a numerical expression",   topic: "num_order_of_operations" },
        ],
      },
      {
        code: "5.OA.B.3",
        desc: "Generate two numerical patterns using two given rules, and form ordered pairs from the results.",
        skills: [
          { name: "Generate a pattern from a rule", topic: "counting" },
          { name: "Form ordered pairs from two patterns", topic: "geo_distance" },
        ],
      },
    ],
  },
  {
    domain: "5.NBT",
    name: "Number and Operations in Base Ten",
    color: "bg-brand-green",
    standards: [
      {
        code: "5.NBT.A.1",
        desc: "Recognize that a digit in one place represents 10 times what it represents to its right and 1/10 of what it represents to its left.",
        skills: [
          { name: "Place value relationships", topic: "num_place_value" },
        ],
      },
      {
        code: "5.NBT.A.2",
        desc: "Explain patterns in the placement of the decimal point when multiplying or dividing by powers of 10.",
        skills: [
          { name: "Multiply by powers of ten", topic: "num_multiply_by_tens" },
          { name: "Divide by powers of ten",    topic: "division" },
        ],
      },
      {
        code: "5.NBT.A.3",
        desc: "Read, write, and compare decimals to thousandths.",
        skills: [
          { name: "Read and write decimals to thousandths", topic: "decimals" },
          { name: "Compare decimals to thousandths",         topic: "num_compare_decimals" },
        ],
      },
      {
        code: "5.NBT.A.4",
        desc: "Use place value understanding to round decimals to any place.",
        skills: [
          { name: "Round decimals to any place", topic: "decimals" },
        ],
      },
      {
        code: "5.NBT.B.5",
        desc: "Fluently multiply multi-digit whole numbers using the standard algorithm.",
        skills: [
          { name: "Multiply multi-digit numbers", topic: "num_multidigit_multiply" },
        ],
      },
      {
        code: "5.NBT.B.6",
        desc: "Find whole-number quotients with up to four-digit dividends and two-digit divisors.",
        skills: [
          { name: "Divide by two-digit numbers", topic: "num_long_division" },
        ],
      },
      {
        code: "5.NBT.B.7",
        desc: "Add, subtract, multiply, and divide decimals to hundredths.",
        skills: [
          { name: "Add and subtract decimals", topic: "num_decimal_ops" },
          { name: "Multiply decimals",           topic: "num_decimal_ops" },
          { name: "Divide decimals",             topic: "decimals" },
        ],
      },
    ],
  },
  {
    domain: "5.NF",
    name: "Number and Operations—Fractions",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "5.NF.A.1",
        desc: "Add and subtract fractions with unlike denominators.",
        skills: [
          { name: "Add fractions – unlike denominators",      topic: "num_add_sub_fractions" },
          { name: "Subtract fractions – unlike denominators", topic: "num_add_sub_fractions" },
        ],
      },
      {
        code: "5.NF.A.2",
        desc: "Solve word problems involving addition and subtraction of fractions.",
        skills: [
          { name: "Fraction addition and subtraction word problems", topic: "num_add_sub_fractions" },
        ],
      },
      {
        code: "5.NF.B.3",
        desc: "Interpret a fraction as the division of the numerator by the denominator.",
        skills: [
          { name: "Interpret fractions as division", topic: "fractions" },
        ],
      },
      {
        code: "5.NF.B.4",
        desc: "Multiply a fraction or whole number by a fraction.",
        skills: [
          { name: "Multiply fractions",                     topic: "num_multiply_fractions" },
          { name: "Multiply a whole number by a fraction",  topic: "num_multiply_fractions" },
        ],
      },
      {
        code: "5.NF.B.5",
        desc: "Interpret multiplication as scaling by comparing the size of a product to the size of its factors.",
        skills: [
          { name: "Scaling by a fraction greater than 1", topic: "num_multiply_fractions" },
          { name: "Scaling by a fraction less than 1",    topic: "num_multiply_fractions" },
        ],
      },
      {
        code: "5.NF.B.6",
        desc: "Solve real-world problems involving multiplication of fractions and mixed numbers.",
        skills: [
          { name: "Fraction multiplication word problems", topic: "num_multiply_fractions" },
        ],
      },
      {
        code: "5.NF.B.7",
        desc: "Divide unit fractions by whole numbers and whole numbers by unit fractions.",
        skills: [
          { name: "Divide a unit fraction by a whole number", topic: "num_divide_fractions" },
          { name: "Divide a whole number by a unit fraction", topic: "num_divide_fractions" },
        ],
      },
    ],
  },
  {
    domain: "5.MD",
    name: "Measurement and Data",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "5.MD.A.1",
        desc: "Convert among different-sized standard measurement units within a given measurement system.",
        skills: [
          { name: "Convert measurement units", topic: "geo_unit_conversion" },
        ],
      },
      {
        code: "5.MD.B.2",
        desc: "Make a line plot with fractional measurements and use fraction operations to solve problems from the data.",
        skills: [
          { name: "Make a line plot with fractions",     topic: "measurement" },
          { name: "Solve problems using line plot data", topic: "fractions" },
        ],
      },
      {
        code: "5.MD.C.3",
        desc: "Recognize volume as an attribute of solid figures; understand concepts of volume measurement.",
        skills: [
          { name: "Understand volume as an attribute", topic: "geo_surface_area_volume" },
        ],
      },
      {
        code: "5.MD.C.4",
        desc: "Measure volume by counting unit cubes using cubic cm, cubic in, cubic ft, and improvised units.",
        skills: [
          { name: "Count unit cubes to find volume", topic: "geo_surface_area_volume" },
        ],
      },
      {
        code: "5.MD.C.5",
        desc: "Relate volume to multiplication and addition, and apply the formula V = l × w × h for rectangular prisms.",
        skills: [
          { name: "Find volume using the formula",      topic: "geo_surface_area_volume" },
          { name: "Find volume of composite figures",   topic: "geo_surface_area_volume" },
        ],
      },
    ],
  },
  {
    domain: "5.G",
    name: "Geometry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "5.G.A.1",
        desc: "Use a pair of perpendicular number lines to define a coordinate system; understand x- and y-coordinates.",
        skills: [
          { name: "Plot points on a coordinate plane", topic: "geo_distance" },
        ],
      },
      {
        code: "5.G.A.2",
        desc: "Represent real-world and mathematical problems by graphing points in the first quadrant.",
        skills: [
          { name: "Graph real-world problems", topic: "geo_distance" },
        ],
      },
      {
        code: "5.G.B.3",
        desc: "Understand that attributes belonging to a category of two-dimensional figures also belong to all subcategories.",
        skills: [
          { name: "Identify shape category attributes", topic: "geo_classify_quadrilateral" },
        ],
      },
      {
        code: "5.G.B.4",
        desc: "Classify two-dimensional figures in a hierarchy based on properties.",
        skills: [
          { name: "Classify figures in a hierarchy", topic: "geo_classify_quadrilateral" },
        ],
      },
    ],
  },
];

const G6_CCSS_PLAN = [
  {
    domain: "6.RP",
    name: "Ratios and Proportional Relationships",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "6.RP.A.1",
        desc: "Understand the concept of a ratio and use ratio language to describe a ratio relationship between two quantities.",
        skills: [
          { name: "Understand and write ratios", topic: "num_ratio_unit_rate" },
        ],
      },
      {
        code: "6.RP.A.2",
        desc: "Understand the concept of a unit rate and use rate language in the context of a ratio relationship.",
        skills: [
          { name: "Find a unit rate",        topic: "num_ratio_unit_rate" },
          { name: "Compare unit rates",       topic: "num_ratio_unit_rate" },
        ],
      },
      {
        code: "6.RP.A.3",
        desc: "Use ratio and rate reasoning to solve real-world and mathematical problems.",
        skills: [
          { name: "Solve ratio and rate word problems",           topic: "num_ratio_unit_rate" },
          { name: "Find a percent of a number",                    topic: "num_percent_of_number" },
          { name: "Convert measurement units using ratios",       topic: "geo_unit_conversion" },
        ],
      },
    ],
  },
  {
    domain: "6.NS",
    name: "The Number System",
    color: "bg-brand-green",
    standards: [
      {
        code: "6.NS.A.1",
        desc: "Interpret and compute quotients of fractions, and solve word problems involving division of fractions by fractions.",
        skills: [
          { name: "Divide fractions by fractions", topic: "num_divide_fractions" },
          { name: "Divide mixed numbers",           topic: "num_divide_fractions" },
        ],
      },
      {
        code: "6.NS.B.2",
        desc: "Fluently divide multi-digit numbers using the standard algorithm.",
        skills: [
          { name: "Divide multi-digit numbers", topic: "num_long_division" },
        ],
      },
      {
        code: "6.NS.B.3",
        desc: "Fluently add, subtract, multiply, and divide multi-digit decimals using the standard algorithm.",
        skills: [
          { name: "Add and subtract multi-digit decimals", topic: "num_decimal_ops" },
          { name: "Multiply and divide multi-digit decimals", topic: "num_decimal_ops" },
        ],
      },
      {
        code: "6.NS.B.4",
        desc: "Find the greatest common factor and least common multiple; use the distributive property to express a sum with a common factor.",
        skills: [
          { name: "Find the GCF of two numbers", topic: "alg_gcf_lcm_monomials" },
          { name: "Find the LCM of two numbers", topic: "alg_gcf_lcm_monomials" },
        ],
      },
      {
        code: "6.NS.C.5",
        desc: "Understand that positive and negative numbers describe quantities with opposite directions or values; explain the meaning of zero.",
        skills: [
          { name: "Understand positive and negative numbers in context", topic: "num_integer_compare" },
        ],
      },
      {
        code: "6.NS.C.6",
        desc: "Understand a rational number as a point on the number line; extend number line diagrams and coordinate axes.",
        skills: [
          { name: "Plot integers on a number line",         topic: "num_integer_compare" },
          { name: "Graph points in all four quadrants",     topic: "geo_distance" },
        ],
      },
      {
        code: "6.NS.C.7",
        desc: "Understand ordering and absolute value of rational numbers.",
        skills: [
          { name: "Order rational numbers",        topic: "num_integer_compare" },
          { name: "Interpret absolute value",       topic: "num_integer_compare" },
        ],
      },
      {
        code: "6.NS.C.8",
        desc: "Solve real-world and mathematical problems by graphing points in all four quadrants; find distances between points that share a coordinate.",
        skills: [
          { name: "Find distance between points on a coordinate plane", topic: "geo_distance" },
        ],
      },
    ],
  },
  {
    domain: "6.EE",
    name: "Expressions and Equations",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "6.EE.A.1",
        desc: "Write and evaluate numerical expressions involving whole-number exponents.",
        skills: [
          { name: "Evaluate expressions with exponents", topic: "alg_exponent_eval" },
        ],
      },
      {
        code: "6.EE.A.2",
        desc: "Write, read, and evaluate expressions in which letters stand for numbers.",
        skills: [
          { name: "Write algebraic expressions",    topic: "alg_write_expression" },
          { name: "Evaluate algebraic expressions", topic: "alg_eval_variable_expr" },
        ],
      },
      {
        code: "6.EE.A.3",
        desc: "Apply the properties of operations to generate equivalent expressions.",
        skills: [
          { name: "Use the distributive property", topic: "alg_distributive" },
          { name: "Combine like terms",              topic: "alg_combine_like_terms" },
        ],
      },
      {
        code: "6.EE.A.4",
        desc: "Identify when two expressions are equivalent, regardless of which value is substituted into them.",
        skills: [
          { name: "Identify equivalent expressions", topic: "alg_combine_like_terms" },
        ],
      },
      {
        code: "6.EE.B.5",
        desc: "Understand solving an equation or inequality as finding which values from a set make it true; use substitution to check.",
        skills: [
          { name: "Identify solutions of an equation or inequality", topic: "alg_num_solutions" },
        ],
      },
      {
        code: "6.EE.B.6",
        desc: "Use variables to represent numbers and write expressions for real-world or mathematical problems.",
        skills: [
          { name: "Write an expression for a real-world problem", topic: "alg_write_expression" },
        ],
      },
      {
        code: "6.EE.B.7",
        desc: "Solve real-world and mathematical problems by writing and solving one-step equations.",
        skills: [
          { name: "Solve one-step equations", topic: "alg_one_step_eq" },
        ],
      },
      {
        code: "6.EE.B.8",
        desc: "Write an inequality to represent a constraint; recognize that inequalities have infinitely many solutions.",
        skills: [
          { name: "Write and graph inequalities", topic: "alg_inequality" },
        ],
      },
      {
        code: "6.EE.C.9",
        desc: "Use variables to represent two related quantities; write an equation and analyze the relationship using graphs and tables.",
        skills: [
          { name: "Analyze dependent and independent variables", topic: "alg_direct_variation" },
        ],
      },
    ],
  },
  {
    domain: "6.G",
    name: "Geometry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "6.G.A.1",
        desc: "Find the area of triangles, quadrilaterals, and other polygons by composing into or decomposing into shapes.",
        skills: [
          { name: "Find the area of triangles and quadrilaterals", topic: "geo_area_polygons" },
        ],
      },
      {
        code: "6.G.A.2",
        desc: "Find the volume of a right rectangular prism with fractional edge lengths.",
        skills: [
          { name: "Find volume with fractional edge lengths", topic: "geo_surface_area_volume" },
        ],
      },
      {
        code: "6.G.A.3",
        desc: "Draw polygons in the coordinate plane given coordinates for the vertices; use coordinates to find the length of a side.",
        skills: [
          { name: "Draw polygons in the coordinate plane", topic: "geo_distance" },
        ],
      },
      {
        code: "6.G.A.4",
        desc: "Represent three-dimensional figures using nets; use nets to find the surface area of these figures.",
        skills: [
          { name: "Use nets to find surface area", topic: "geo_surface_area_volume" },
        ],
      },
    ],
  },
  {
    domain: "6.SP",
    name: "Statistics and Probability",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "6.SP.A.1",
        desc: "Recognize a statistical question as one that anticipates variability in the data related to the question.",
        skills: [
          { name: "Identify statistical questions", topic: "num_mean_mode_range" },
        ],
      },
      {
        code: "6.SP.A.2",
        desc: "Understand that a set of data has a distribution which can be described by its center, spread, and overall shape.",
        skills: [
          { name: "Describe the center, spread, and shape of a distribution", topic: "alg_quartiles" },
        ],
      },
      {
        code: "6.SP.A.3",
        desc: "Recognize that a measure of center summarizes data with one number, while a measure of variation describes how values vary.",
        skills: [
          { name: "Distinguish measures of center from measures of variation", topic: "num_mean_mode_range" },
        ],
      },
      {
        code: "6.SP.B.4",
        desc: "Display numerical data in plots on a number line, including dot plots, histograms, and box plots.",
        skills: [
          { name: "Create and read dot plots, histograms, and box plots", topic: "alg_quartiles" },
        ],
      },
      {
        code: "6.SP.B.5",
        desc: "Summarize numerical data sets in relation to their context, such as by reporting the number of observations and describing measures of center and variability.",
        skills: [
          { name: "Find the mean",                        topic: "num_mean_mode_range" },
          { name: "Find the median, mode, and range",     topic: "num_mean_mode_range" },
        ],
      },
    ],
  },
];

const G7_CCSS_PLAN = [
  {
    domain: "7.RP",
    name: "Ratios and Proportional Relationships",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "7.RP.A.1",
        desc: "Compute unit rates associated with ratios of fractions, including ratios of lengths, areas, and other quantities.",
        skills: [
          { name: "Compute unit rates with fractions", topic: "num_ratio_unit_rate" },
        ],
      },
      {
        code: "7.RP.A.2",
        desc: "Recognize and represent proportional relationships between quantities.",
        skills: [
          { name: "Identify a proportional relationship",     topic: "num_proportion" },
          { name: "Find the constant of proportionality",     topic: "num_proportion" },
          { name: "Graph a proportional relationship",         topic: "alg_direct_variation" },
        ],
      },
      {
        code: "7.RP.A.3",
        desc: "Use proportional relationships to solve multistep ratio and percent problems.",
        skills: [
          { name: "Solve percent problems",              topic: "num_percent_of_number" },
          { name: "Find percent increase and decrease",  topic: "num_percent_change" },
          { name: "Solve tax, tip, discount, and markup problems", topic: "num_percent_change" },
        ],
      },
    ],
  },
  {
    domain: "7.NS",
    name: "The Number System",
    color: "bg-brand-green",
    standards: [
      {
        code: "7.NS.A.1",
        desc: "Apply properties of operations to add and subtract rational numbers, including on a number line.",
        skills: [
          { name: "Add and subtract integers",         topic: "alg_integer_ops" },
          { name: "Add and subtract rational numbers",  topic: "num_add_sub_fractions" },
        ],
      },
      {
        code: "7.NS.A.2",
        desc: "Apply properties of operations to multiply and divide rational numbers.",
        skills: [
          { name: "Multiply and divide integers",        topic: "alg_integer_ops" },
          { name: "Multiply and divide rational numbers", topic: "num_multiply_fractions" },
        ],
      },
      {
        code: "7.NS.A.3",
        desc: "Solve real-world and mathematical problems involving the four operations with rational numbers.",
        skills: [
          { name: "Solve real-world rational number problems", topic: "num_fraction_of_number" },
        ],
      },
    ],
  },
  {
    domain: "7.EE",
    name: "Expressions and Equations",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "7.EE.A.1",
        desc: "Apply properties of operations to add, subtract, factor, and expand linear expressions with rational coefficients.",
        skills: [
          { name: "Expand linear expressions", topic: "alg_distributive" },
          { name: "Factor linear expressions",  topic: "alg_factor_gcf" },
        ],
      },
      {
        code: "7.EE.A.2",
        desc: "Understand that rewriting an expression in a different form can reveal how quantities in a problem are related.",
        skills: [
          { name: "Rewrite an expression in a different form", topic: "alg_combine_like_terms" },
        ],
      },
      {
        code: "7.EE.B.3",
        desc: "Solve multi-step real-world and mathematical problems posed with positive and negative rational numbers.",
        skills: [
          { name: "Solve multi-step equations with rational numbers", topic: "alg_two_step_eq" },
        ],
      },
      {
        code: "7.EE.B.4",
        desc: "Use variables to represent quantities and construct simple equations and inequalities to solve problems.",
        skills: [
          { name: "Solve two-step equations",    topic: "alg_two_step_eq" },
          { name: "Solve two-step inequalities", topic: "alg_inequality" },
        ],
      },
    ],
  },
  {
    domain: "7.G",
    name: "Geometry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "7.G.A.1",
        desc: "Solve problems involving scale drawings of geometric figures, including finding actual lengths and areas.",
        skills: [
          { name: "Interpret a scale drawing", topic: "geo_similar_figures" },
          { name: "Find actual measurements from a scale drawing", topic: "geo_similar_figures" },
        ],
      },
      {
        code: "7.G.A.2",
        desc: "Draw geometric shapes with given conditions and determine whether they form a unique, more than one, or no triangle.",
        skills: [
          { name: "Draw a triangle from given conditions", topic: "geo_triangle_inequality" },
          { name: "Determine unique, many, or no triangle", topic: "geo_triangle_inequality" },
        ],
      },
      {
        code: "7.G.A.3",
        desc: "Describe the two-dimensional figures that result from slicing three-dimensional figures.",
        skills: [
          { name: "Identify cross sections of 3D figures", topic: "geo_cross_section" },
        ],
      },
      {
        code: "7.G.B.4",
        desc: "Know the formulas for the area and circumference of a circle and use them to solve problems.",
        skills: [
          { name: "Find the circumference of a circle", topic: "geo_circle_area_circumference" },
          { name: "Find the area of a circle",           topic: "geo_circle_area_circumference" },
        ],
      },
      {
        code: "7.G.B.5",
        desc: "Use facts about supplementary, complementary, vertical, and adjacent angles to write and solve equations for an unknown angle.",
        skills: [
          { name: "Identify angle relationships",              topic: "geo_angle_relationships" },
          { name: "Write and solve for an unknown angle",       topic: "geo_angle_solve_x" },
        ],
      },
      {
        code: "7.G.B.6",
        desc: "Solve real-world and mathematical problems involving area, volume, and surface area of two- and three-dimensional objects.",
        skills: [
          { name: "Find surface area of prisms", topic: "geo_surface_area_volume" },
          { name: "Find volume of composite figures", topic: "geo_surface_area_volume" },
        ],
      },
    ],
  },
  {
    domain: "7.SP",
    name: "Statistics and Probability",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "7.SP.A.1",
        desc: "Understand that statistics can be used to gain information about a population by examining a sample of it.",
        skills: [
          { name: "Identify sampling methods", topic: "alg_quartiles" },
        ],
      },
      {
        code: "7.SP.A.2",
        desc: "Use data from a random sample to draw inferences about a population with an unknown characteristic of interest.",
        skills: [
          { name: "Draw an inference from a random sample", topic: "alg_quartiles" },
        ],
      },
      {
        code: "7.SP.B.3",
        desc: "Informally assess the degree of visual overlap of two numerical data distributions.",
        skills: [
          { name: "Compare two data distributions", topic: "alg_quartiles" },
        ],
      },
      {
        code: "7.SP.B.4",
        desc: "Use measures of center and variability from random samples to draw informal comparative inferences about two populations.",
        skills: [
          { name: "Compare two populations using center and variability", topic: "alg_quartiles" },
        ],
      },
      {
        code: "7.SP.C.5",
        desc: "Understand that the probability of a chance event is a number between 0 and 1 that expresses its likelihood.",
        skills: [
          { name: "Identify the probability scale", topic: "geo_probability" },
          { name: "Calculate simple probability",    topic: "geo_probability" },
        ],
      },
      {
        code: "7.SP.C.6",
        desc: "Approximate the probability of a chance event by collecting data and observing its long-run relative frequency.",
        skills: [
          { name: "Find experimental probability", topic: "geo_probability" },
        ],
      },
      {
        code: "7.SP.C.7",
        desc: "Develop a probability model and use it to find probabilities of events; compare probabilities from a model to observed frequencies.",
        skills: [
          { name: "Develop and use a probability model", topic: "geo_probability" },
        ],
      },
      {
        code: "7.SP.C.8",
        desc: "Find probabilities of compound events using organized lists, tables, and tree diagrams.",
        skills: [
          { name: "Find compound probability using tree diagrams", topic: "geo_permutations_combinations" },
        ],
      },
    ],
  },
];

const G8_CCSS_PLAN = [
  {
    domain: "8.NS",
    name: "The Number System",
    color: "bg-brand-green",
    standards: [
      {
        code: "8.NS.A.1",
        desc: "Know that numbers that are not rational are called irrational; approximate irrational numbers on a number line.",
        skills: [
          { name: "Classify rational and irrational numbers", topic: "alg_classify_number" },
          { name: "Approximate irrational numbers on a number line", topic: "alg_simplify_radical" },
        ],
      },
      {
        code: "8.NS.A.2",
        desc: "Use rational approximations of irrational numbers to compare their size, locate them on a number line, and estimate expressions.",
        skills: [
          { name: "Estimate the value of an irrational number", topic: "alg_square_cube_roots" },
          { name: "Compare irrational numbers",                  topic: "alg_square_cube_roots" },
        ],
      },
    ],
  },
  {
    domain: "8.EE",
    name: "Expressions and Equations",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "8.EE.A.1",
        desc: "Know and apply the properties of integer exponents to generate equivalent numerical expressions.",
        skills: [
          { name: "Apply properties of integer exponents", topic: "alg_exponent_rules" },
          { name: "Simplify expressions with negative exponents", topic: "alg_negative_exponent" },
        ],
      },
      {
        code: "8.EE.A.2",
        desc: "Use square root and cube root symbols to represent solutions to equations of the form x²=p and x³=p.",
        skills: [
          { name: "Solve equations with square roots", topic: "alg_solve_quad_square_roots" },
          { name: "Solve equations with cube roots",    topic: "alg_square_cube_roots" },
        ],
      },
      {
        code: "8.EE.A.3",
        desc: "Use numbers expressed in scientific notation and standard notation to estimate very large or very small quantities.",
        skills: [
          { name: "Convert to and from scientific notation", topic: "alg_scientific_notation" },
          { name: "Compare quantities in scientific notation", topic: "alg_scientific_notation" },
        ],
      },
      {
        code: "8.EE.A.4",
        desc: "Perform operations with numbers expressed in scientific notation, including problems with different units.",
        skills: [
          { name: "Add and subtract in scientific notation",   topic: "alg_scientific_notation" },
          { name: "Multiply and divide in scientific notation", topic: "alg_scientific_notation" },
        ],
      },
      {
        code: "8.EE.B.5",
        desc: "Graph proportional relationships, interpreting the unit rate as the slope of the graph.",
        skills: [
          { name: "Graph a proportional relationship", topic: "alg_direct_variation" },
          { name: "Interpret slope as a unit rate",     topic: "alg_rate_of_change" },
        ],
      },
      {
        code: "8.EE.B.6",
        desc: "Use similar triangles to explain why the slope is the same between any two points on a line; derive y=mx and y=mx+b.",
        skills: [
          { name: "Explain slope using similar triangles", topic: "geo_slope" },
          { name: "Derive the equation of a line",          topic: "alg_slope_intercept" },
        ],
      },
      {
        code: "8.EE.C.7",
        desc: "Solve linear equations in one variable, including equations with variables on both sides.",
        skills: [
          { name: "Solve multi-step linear equations",      topic: "alg_two_step_eq" },
          { name: "Solve equations with variables on both sides", topic: "alg_vars_both_sides" },
        ],
      },
      {
        code: "8.EE.C.8",
        desc: "Analyze and solve pairs of simultaneous linear equations (systems of equations).",
        skills: [
          { name: "Solve a system by graphing",     topic: "alg_system" },
          { name: "Solve a system by substitution",   topic: "alg_system" },
        ],
      },
    ],
  },
  {
    domain: "8.F",
    name: "Functions",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "8.F.A.1",
        desc: "Understand that a function assigns exactly one output to each input.",
        skills: [
          { name: "Identify a function from a table or graph", topic: "alg_evaluate_function" },
        ],
      },
      {
        code: "8.F.A.2",
        desc: "Compare properties of two functions represented in different ways (tables, graphs, equations, or descriptions).",
        skills: [
          { name: "Compare functions across representations", topic: "alg_evaluate_function" },
        ],
      },
      {
        code: "8.F.A.3",
        desc: "Interpret the equation y=mx+b as defining a linear function; give examples of nonlinear functions.",
        skills: [
          { name: "Identify linear and nonlinear functions", topic: "alg_slope_intercept" },
        ],
      },
      {
        code: "8.F.B.4",
        desc: "Construct a function to model a linear relationship, determining the rate of change and initial value.",
        skills: [
          { name: "Find rate of change and initial value", topic: "alg_rate_of_change" },
          { name: "Construct a function from a table or description", topic: "alg_slope_intercept" },
        ],
      },
      {
        code: "8.F.B.5",
        desc: "Describe qualitatively the functional relationship between two quantities by analyzing a graph.",
        skills: [
          { name: "Describe a functional relationship from a graph", topic: "alg_rate_of_change" },
        ],
      },
    ],
  },
  {
    domain: "8.G",
    name: "Geometry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "8.G.A.1",
        desc: "Verify experimentally the properties of rotations, reflections, and translations.",
        skills: [
          { name: "Identify a transformation",  topic: "geo_translation" },
          { name: "Perform a transformation",   topic: "geo_reflection" },
        ],
      },
      {
        code: "8.G.A.2",
        desc: "Understand that a figure is congruent to another if it can be obtained by a sequence of rotations, reflections, and translations.",
        skills: [
          { name: "Determine congruence using a sequence of transformations", topic: "geo_congruence_criteria" },
        ],
      },
      {
        code: "8.G.A.3",
        desc: "Describe the effect of dilations, translations, rotations, and reflections on two-dimensional figures using coordinates.",
        skills: [
          { name: "Find coordinates after a transformation", topic: "geo_rotation" },
        ],
      },
      {
        code: "8.G.A.4",
        desc: "Understand that a figure is similar to another if it can be obtained by a sequence of rotations, reflections, translations, and dilations.",
        skills: [
          { name: "Determine similarity using a sequence of transformations", topic: "geo_similar_figures" },
        ],
      },
      {
        code: "8.G.A.5",
        desc: "Use informal arguments to establish facts about the angle sum and exterior angle of triangles and angles formed by a transversal.",
        skills: [
          { name: "Find the angle sum of a triangle",        topic: "geo_triangle_angles" },
          { name: "Find angles formed by a transversal",     topic: "geo_transversal_angles" },
        ],
      },
      {
        code: "8.G.B.6",
        desc: "Explain a proof of the Pythagorean Theorem and its converse.",
        skills: [
          { name: "Explain a proof of the Pythagorean Theorem", topic: "geo_pythagorean" },
        ],
      },
      {
        code: "8.G.B.7",
        desc: "Apply the Pythagorean Theorem to determine unknown side lengths in right triangles.",
        skills: [
          { name: "Find a missing leg",     topic: "geo_pythagorean" },
          { name: "Find the hypotenuse",    topic: "geo_pythagorean" },
        ],
      },
      {
        code: "8.G.B.8",
        desc: "Apply the Pythagorean Theorem to find the distance between two points in a coordinate system.",
        skills: [
          { name: "Find the distance between two coordinate points", topic: "geo_distance" },
        ],
      },
      {
        code: "8.G.C.9",
        desc: "Know and use the formulas for volumes of cones, cylinders, and spheres to solve real-world problems.",
        skills: [
          { name: "Find the volume of a cylinder", topic: "geo_surface_area_volume" },
          { name: "Find the volume of a cone or sphere", topic: "geo_surface_area_volume" },
        ],
      },
    ],
  },
  {
    domain: "8.SP",
    name: "Statistics and Probability",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "8.SP.A.1",
        desc: "Construct and interpret scatter plots for bivariate measurement data to investigate patterns of association.",
        skills: [
          { name: "Construct a scatter plot",        topic: "alg_scatter_correlation" },
          { name: "Interpret clusters and outliers", topic: "alg_scatter_correlation" },
        ],
      },
      {
        code: "8.SP.A.2",
        desc: "Know that straight lines are widely used to model relationships between two quantitative variables; informally fit a line.",
        skills: [
          { name: "Draw a line of best fit", topic: "alg_scatter_correlation" },
        ],
      },
      {
        code: "8.SP.A.3",
        desc: "Use the equation of a linear model to solve problems, interpreting the slope and intercept in context.",
        skills: [
          { name: "Interpret slope and intercept in context", topic: "alg_rate_of_change" },
          { name: "Use a linear model to make a prediction",   topic: "alg_evaluate_function" },
        ],
      },
      {
        code: "8.SP.A.4",
        desc: "Understand that patterns of association in bivariate categorical data can be seen in a two-way table of frequencies.",
        skills: [
          { name: "Build a two-way frequency table",       topic: "geo_probability" },
          { name: "Interpret relative frequencies",         topic: "geo_probability" },
        ],
      },
    ],
  },
];

// Algebra 1 (grade "9"). The stepper mirrors the full A–LL section structure of an
// Algebra 1 skill plan, in alphabetical order, with every listed skill wired to a
// generator (algebra-specific alg_* generators, or reused geometry/statistics
// generators) so the practice problems match the skill's topic.
const ALG1_PLAN = [
  {
    domain: "A",
    name: "Numbers and Operations",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "A",
        desc: "Numbers and Operations.",
        skills: [
          { name: "Add, subtract, multiply, and divide integers", topic: "alg_integer_ops" },
          { name: "Evaluate numerical expressions involving integers", topic: "alg_eval_numeric" },
          { name: "Convert between decimals and fractions", topic: "decimals" },
          { name: "Add and subtract rational numbers", topic: "fractions" },
          { name: "Multiply and divide rational numbers", topic: "fractions" },
          { name: "Simplify complex fractions", topic: "fractions" },
          { name: "Evaluate numerical expressions involving rational numbers", topic: "alg_eval_numeric" },
          { name: "Square roots", topic: "alg_square_cube_roots" },
          { name: "Cube roots", topic: "alg_square_cube_roots" },
          { name: "Sort rational and irrational numbers", topic: "alg_classify_number" },
          { name: "Classify rational and irrational numbers", topic: "alg_classify_number" },
          { name: "Properties of operations on rational and irrational numbers", topic: "alg_classify_number" },
          { name: "Classify numbers", topic: "alg_classify_number" },
        ],
      },
    ],
  },
  {
    domain: "B",
    name: "Expressions",
    color: "bg-brand-green",
    standards: [
      {
        code: "B",
        desc: "Expressions.",
        skills: [
          { name: "Write variable expressions", topic: "alg_write_expression" },
          { name: "Evaluate variable expressions involving integers", topic: "alg_eval_variable_expr" },
          { name: "Evaluate variable expressions involving rational numbers", topic: "alg_eval_variable_expr" },
          { name: "Evaluate rational expressions", topic: "alg_rational_expression" },
          { name: "Properties of addition and multiplication", topic: "alg_distributive" },
          { name: "Distributive property", topic: "alg_distributive" },
          { name: "Simplify linear expressions using properties", topic: "alg_combine_like_terms" },
          { name: "Sort factors of variable expressions", topic: "alg_factor_gcf" },
          { name: "Simplify variable expressions involving like terms and the distributive property", topic: "alg_combine_like_terms" },
          { name: "Identify equivalent linear expressions", topic: "alg_combine_like_terms" },
        ],
      },
    ],
  },
  {
    domain: "C",
    name: "Solve Linear Equations",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "C",
        desc: "Solve Linear Equations.",
        skills: [
          { name: "Write variable equations", topic: "alg_one_step_eq" },
          { name: "Does x satisfy the equation?", topic: "alg_two_step_eq" },
          { name: "Which x satisfies an equation?", topic: "alg_two_step_eq" },
          { name: "Properties of equality", topic: "alg_one_step_eq" },
          { name: "Identify equivalent equations", topic: "alg_two_step_eq" },
          { name: "Model and solve linear equations using algebra tiles", topic: "alg_two_step_eq" },
          { name: "Solve one-step linear equations", topic: "alg_one_step_eq" },
          { name: "Solve two-step linear equations", topic: "alg_two_step_eq" },
          { name: "Solve one-step and two-step linear equations: word problems", topic: "alg_two_step_eq" },
          { name: "Solve linear equations with variables on one side", topic: "alg_two_step_eq" },
          { name: "Consecutive integer problems", topic: "alg_consecutive_integers" },
          { name: "Solve linear equations with variables on both sides", topic: "alg_vars_both_sides" },
          { name: "Solve linear equations: mixed review", topic: "alg_vars_both_sides" },
          { name: "Solve linear equations: complete the solution", topic: "alg_two_step_eq" },
          { name: "Find the number of solutions to a linear equation", topic: "alg_num_solutions" },
          { name: "Create linear equations with no solutions or infinitely many solutions", topic: "alg_num_solutions" },
          { name: "Solve linear equations with variables on both sides: word problems", topic: "alg_vars_both_sides" },
          { name: "Rearrange multi-variable equations", topic: "alg_rearrange_formula" },
        ],
      },
    ],
  },
  {
    domain: "D",
    name: "Geometry and Measurement",
    color: "bg-brand-orange",
    standards: [
      {
        code: "D",
        desc: "Geometry and Measurement.",
        skills: [
          { name: "Area and perimeter: word problems", topic: "geo_area_polygons" },
          { name: "Volume", topic: "geo_surface_area_volume" },
          { name: "Surface area", topic: "geo_surface_area_volume" },
          { name: "Precision", topic: "geo_unit_conversion" },
          { name: "Greatest possible error", topic: "geo_unit_conversion" },
          { name: "Minimum and maximum area and volume", topic: "geo_area_polygons" },
          { name: "Percent error", topic: "geo_unit_conversion" },
          { name: "Percent error: area and volume", topic: "geo_area_polygons" },
        ],
      },
    ],
  },
  {
    domain: "E",
    name: "Problem Solving with Units",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "E",
        desc: "Problem Solving with Units.",
        skills: [
          { name: "Scale drawings: word problems", topic: "geo_similar_figures" },
          { name: "Convert rates and measurements: customary units", topic: "geo_unit_conversion" },
          { name: "Convert rates and measurements: metric units", topic: "geo_unit_conversion" },
          { name: "Unit prices with unit conversions", topic: "geo_unit_conversion" },
          { name: "Multi-step problems with unit conversions", topic: "geo_unit_conversion" },
          { name: "Rate of travel: word problems", topic: "geo_unit_conversion" },
          { name: "Weighted averages: word problems", topic: "statistics" },
        ],
      },
    ],
  },
  {
    domain: "F",
    name: "Solve Linear Inequalities",
    color: "bg-brand-coral",
    standards: [
      {
        code: "F",
        desc: "Solve Linear Inequalities.",
        skills: [
          { name: "Graph inequalities", topic: "alg_inequality" },
          { name: "Write inequalities from graphs", topic: "alg_inequality" },
          { name: "Identify solutions to inequalities", topic: "alg_inequality" },
          { name: "Solve one-step linear inequalities: addition and subtraction", topic: "alg_inequality" },
          { name: "Solve one-step linear inequalities: multiplication and division", topic: "alg_inequality" },
          { name: "Solve one-step linear inequalities", topic: "alg_inequality" },
          { name: "Graph solutions to one-step linear inequalities", topic: "alg_inequality" },
          { name: "Solve two-step linear inequalities", topic: "alg_inequality" },
          { name: "Graph solutions to two-step linear inequalities", topic: "alg_inequality" },
          { name: "Solve multi-step linear inequalities", topic: "alg_inequality" },
          { name: "Graph solutions to multi-step linear inequalities", topic: "alg_inequality" },
          { name: "Graph compound inequalities", topic: "alg_compound_inequality" },
          { name: "Write compound inequalities from graphs", topic: "alg_compound_inequality" },
          { name: "Solve compound inequalities", topic: "alg_compound_inequality" },
          { name: "Graph solutions to compound inequalities", topic: "alg_compound_inequality" },
        ],
      },
    ],
  },
  {
    domain: "G",
    name: "Sets",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "G",
        desc: "Sets.",
        skills: [
          { name: "Sets, subsets, and complements", topic: "alg_set_operations" },
          { name: "Set-builder notation", topic: "alg_interval_notation" },
          { name: "Interval notation", topic: "alg_interval_notation" },
          { name: "Unions and intersections of sets", topic: "alg_set_operations" },
        ],
      },
    ],
  },
  {
    domain: "H",
    name: "Absolute Value Equations and Inequalities",
    color: "bg-brand-green",
    standards: [
      {
        code: "H",
        desc: "Absolute Value Equations and Inequalities.",
        skills: [
          { name: "Solve absolute value equations", topic: "alg_abs_value_equation" },
          { name: "Graph solutions to absolute value equations", topic: "alg_abs_value_equation" },
          { name: "Write absolute value equations from graphs", topic: "alg_abs_value_equation" },
          { name: "Solve absolute value inequalities", topic: "alg_abs_value_inequality" },
          { name: "Graph solutions to absolute value inequalities", topic: "alg_abs_value_inequality" },
        ],
      },
    ],
  },
  {
    domain: "I",
    name: "Coordinate Plane",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "I",
        desc: "Coordinate Plane.",
        skills: [
          { name: "Coordinate plane review", topic: "geo_midpoint" },
          { name: "Midpoint formula: find the midpoint", topic: "geo_midpoint" },
          { name: "Midpoint formula: find the endpoint", topic: "geo_midpoint" },
          { name: "Distance between two points", topic: "geo_distance" },
          { name: "Midpoint and distance formulas: mixed practice", topic: "geo_distance" },
        ],
      },
    ],
  },
  {
    domain: "J",
    name: "Direct and Inverse Variation",
    color: "bg-brand-orange",
    standards: [
      {
        code: "J",
        desc: "Direct and Inverse Variation.",
        skills: [
          { name: "Find the constant of variation", topic: "alg_direct_variation" },
          { name: "Write direct variation equations", topic: "alg_direct_variation" },
          { name: "Write and solve direct variation equations", topic: "alg_direct_variation" },
          { name: "Identify direct variation and inverse variation", topic: "alg_inverse_variation" },
          { name: "Write inverse variation equations", topic: "alg_inverse_variation" },
          { name: "Write and solve inverse variation equations", topic: "alg_inverse_variation" },
        ],
      },
    ],
  },
  {
    domain: "K",
    name: "Slope",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "K",
        desc: "Slope.",
        skills: [
          { name: "Find the slope of a graph", topic: "geo_slope" },
          { name: "Find the slope from two points", topic: "geo_slope" },
          { name: "Find the slope from a table", topic: "geo_slope" },
          { name: "Find a missing coordinate using slope", topic: "geo_slope" },
          { name: "Slope: mixed practice", topic: "geo_slope" },
        ],
      },
    ],
  },
  {
    domain: "L",
    name: "Linear Equations",
    color: "bg-brand-coral",
    standards: [
      {
        code: "L",
        desc: "Linear Equations.",
        skills: [
          { name: "Does (x, y) satisfy the linear equation?", topic: "geo_line_equation" },
          { name: "Relate the graph of a linear equation to its solutions", topic: "geo_line_equation" },
          { name: "Slope-intercept form: find the slope and y-intercept", topic: "alg_slope_intercept" },
          { name: "Slope-intercept form: graph an equation", topic: "alg_slope_intercept" },
          { name: "Slope-intercept form: write an equation from a graph", topic: "geo_line_equation" },
          { name: "Slope-intercept form: write an equation", topic: "geo_line_equation" },
          { name: "Slope-intercept form: write an equation from a table", topic: "geo_line_equation" },
          { name: "Linear equations: solve for y", topic: "alg_rearrange_formula" },
          { name: "Write linear equations in standard form", topic: "alg_standard_form_intercepts" },
          { name: "Standard form: find x- and y-intercepts", topic: "alg_standard_form_intercepts" },
          { name: "Standard form: graph a line from an equation", topic: "alg_standard_form_intercepts" },
          { name: "Equations of horizontal and vertical lines", topic: "alg_slope_intercept" },
          { name: "Graph a horizontal or vertical line", topic: "alg_slope_intercept" },
          { name: "Point-slope form: graph an equation", topic: "alg_point_slope" },
          { name: "Point-slope form: write an equation", topic: "alg_point_slope" },
          { name: "Point-slope form: write an equation from a graph", topic: "alg_point_slope" },
          { name: "Find the slope and intercepts from an equation", topic: "alg_slope_intercept" },
          { name: "Graph a linear equation: mixed practice", topic: "geo_line_equation" },
          { name: "Slopes of parallel and perpendicular lines", topic: "alg_parallel_perp" },
          { name: "Write an equation for a parallel or perpendicular line", topic: "alg_parallel_perp" },
        ],
      },
    ],
  },
  {
    domain: "M",
    name: "Function Concepts",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "M",
        desc: "Function Concepts.",
        skills: [
          { name: "Relations: convert between tables, graphs, mappings, and lists of points", topic: "alg_evaluate_function" },
          { name: "Domain and range of relations", topic: "alg_evaluate_function" },
          { name: "Identify independent and dependent variables", topic: "alg_evaluate_function" },
          { name: "Identify functions", topic: "alg_evaluate_function" },
          { name: "Identify functions: vertical line test", topic: "alg_evaluate_function" },
          { name: "Find values using function graphs", topic: "alg_evaluate_function" },
          { name: "Evaluate a function", topic: "alg_evaluate_function" },
          { name: "Evaluate a function: plug in an expression", topic: "alg_evaluate_function" },
          { name: "Complete a function table from a graph", topic: "alg_evaluate_function" },
          { name: "Complete a function table from an equation", topic: "alg_evaluate_function" },
          { name: "Find solutions using a table", topic: "alg_evaluate_function" },
          { name: "Approximate solutions using a table", topic: "alg_evaluate_function" },
          { name: "Interpret functions using everyday language", topic: "alg_evaluate_function" },
          { name: "Identify graphs: word problems", topic: "alg_evaluate_function" },
          { name: "Rate of change: tables", topic: "alg_rate_of_change" },
          { name: "Rate of change: graphs", topic: "alg_rate_of_change" },
          { name: "Estimate average rate of change from a graph", topic: "alg_rate_of_change" },
        ],
      },
    ],
  },
  {
    domain: "N",
    name: "Linear Functions",
    color: "bg-brand-green",
    standards: [
      {
        code: "N",
        desc: "Linear Functions.",
        skills: [
          { name: "Identify linear functions from graphs and equations", topic: "alg_slope_intercept" },
          { name: "Identify linear functions from tables", topic: "alg_rate_of_change" },
          { name: "Complete a table and graph a linear function", topic: "alg_evaluate_function" },
          { name: "Evaluate a linear function from its graph: word problems", topic: "alg_evaluate_function" },
          { name: "Rate of change of a linear function: tables", topic: "alg_rate_of_change" },
          { name: "Rate of change of a linear function: graphs", topic: "alg_rate_of_change" },
          { name: "Interpret the slope and y-intercept of a linear function", topic: "alg_slope_intercept" },
          { name: "Write a linear function: word problems", topic: "geo_line_equation" },
          { name: "Domain and range of linear functions: graphs", topic: "alg_evaluate_function" },
          { name: "Domain and range of linear functions: word problems", topic: "alg_evaluate_function" },
          { name: "Compare linear functions: graphs and equations", topic: "alg_slope_intercept" },
          { name: "Compare linear functions: tables, graphs, and equations", topic: "alg_rate_of_change" },
          { name: "Transformations of linear functions", topic: "alg_slope_intercept" },
        ],
      },
    ],
  },
  {
    domain: "O",
    name: "Systems of Linear Equations",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "O",
        desc: "Systems of Linear Equations.",
        skills: [
          { name: "Is (x, y) a solution to the system of equations?", topic: "alg_system" },
          { name: "Solve a system of equations by graphing", topic: "alg_system" },
          { name: "Solve a system of equations by graphing: word problems", topic: "alg_system" },
          { name: "Find the number of solutions to a system of equations by graphing", topic: "alg_system" },
          { name: "Find the number of solutions to a system of equations", topic: "alg_system" },
          { name: "Classify a system of equations by graphing", topic: "alg_system" },
          { name: "Classify a system of equations", topic: "alg_system" },
          { name: "Solve a system of equations using substitution", topic: "alg_system" },
          { name: "Solve a system of equations using substitution: word problems", topic: "alg_system" },
          { name: "Solve a system of equations using elimination", topic: "alg_system" },
          { name: "Solve a system of equations using elimination: word problems", topic: "alg_system" },
          { name: "Solve a system of equations using augmented matrices", topic: "alg_system" },
          { name: "Solve a system of equations using augmented matrices: word problems", topic: "alg_system" },
          { name: "Solve a system of equations using any method", topic: "alg_system" },
          { name: "Solve a system of equations using any method: word problems", topic: "alg_system" },
        ],
      },
    ],
  },
  {
    domain: "P",
    name: "Two-Variable Linear Inequalities",
    color: "bg-brand-orange",
    standards: [
      {
        code: "P",
        desc: "Two-Variable Linear Inequalities.",
        skills: [
          { name: "Does (x, y) satisfy the inequality?", topic: "alg_two_var_inequality" },
          { name: "Linear inequalities: solve for y", topic: "alg_rearrange_formula" },
          { name: "Graph a two-variable linear inequality", topic: "alg_two_var_inequality" },
          { name: "Write a linear inequality from a graph", topic: "alg_two_var_inequality" },
          { name: "Write two-variable inequalities: word problems", topic: "alg_two_var_inequality" },
          { name: "Is (x, y) a solution to the system of linear inequalities?", topic: "alg_two_var_inequality" },
          { name: "Solve systems of linear inequalities by graphing", topic: "alg_two_var_inequality" },
        ],
      },
    ],
  },
  {
    domain: "Q",
    name: "Piecewise-Defined Functions",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "Q",
        desc: "Piecewise-Defined Functions.",
        skills: [
          { name: "Evaluate piecewise-defined functions", topic: "alg_piecewise_eval" },
          { name: "Graph piecewise-defined functions", topic: "alg_piecewise_eval" },
        ],
      },
    ],
  },
  {
    domain: "R",
    name: "Integer Exponents",
    color: "bg-brand-coral",
    standards: [
      {
        code: "R",
        desc: "Integer Exponents.",
        skills: [
          { name: "Powers with integer bases", topic: "alg_exponent_eval" },
          { name: "Powers with decimal and fractional bases", topic: "alg_exponent_eval" },
          { name: "Negative exponents", topic: "alg_negative_exponent" },
          { name: "Multiplication rule for exponents", topic: "alg_exponent_rules" },
          { name: "Division rule for exponents", topic: "alg_exponent_rules" },
          { name: "Power rule for exponents", topic: "alg_exponent_rules" },
          { name: "Simplify exponential expressions using the multiplication rule", topic: "alg_exponent_rules" },
          { name: "Simplify exponential expressions using the division rule", topic: "alg_exponent_rules" },
          { name: "Simplify exponential expressions using the multiplication and division rules", topic: "alg_exponent_rules" },
          { name: "Simplify exponential expressions using the power rule", topic: "alg_exponent_rules" },
          { name: "Simplify exponential expressions using exponent rules", topic: "alg_exponent_rules" },
          { name: "Evaluate expressions using exponent rules", topic: "alg_exponent_rules" },
          { name: "Identify equivalent exponential expressions I", topic: "alg_exponent_rules" },
          { name: "Identify equivalent exponential expressions II", topic: "alg_exponent_rules" },
        ],
      },
    ],
  },
  {
    domain: "S",
    name: "Rational Exponents",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "S",
        desc: "Rational Exponents.",
        skills: [
          { name: "Evaluate integers raised to positive rational exponents", topic: "alg_rational_exponent" },
          { name: "Evaluate integers raised to rational exponents", topic: "alg_rational_exponent" },
          { name: "Multiplication with rational exponents", topic: "alg_rational_exponent" },
          { name: "Division with rational exponents", topic: "alg_rational_exponent" },
          { name: "Power rule with rational exponents", topic: "alg_rational_exponent" },
          { name: "Simplify expressions involving rational exponents", topic: "alg_rational_exponent" },
        ],
      },
    ],
  },
  {
    domain: "T",
    name: "Scientific Notation",
    color: "bg-brand-green",
    standards: [
      {
        code: "T",
        desc: "Scientific Notation.",
        skills: [
          { name: "Convert between standard and scientific notation", topic: "alg_scientific_notation" },
          { name: "Compare numbers written in scientific notation", topic: "alg_scientific_notation" },
          { name: "Add and subtract numbers written in scientific notation", topic: "alg_scientific_notation" },
          { name: "Multiply numbers written in scientific notation", topic: "alg_scientific_notation" },
          { name: "Divide numbers written in scientific notation", topic: "alg_scientific_notation" },
          { name: "Add, subtract, multiply, and divide numbers written in scientific notation", topic: "alg_scientific_notation" },
          { name: "Add, subtract, multiply, and divide numbers written in scientific notation: word problems", topic: "alg_scientific_notation" },
        ],
      },
    ],
  },
  {
    domain: "U",
    name: "Sequences",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "U",
        desc: "Sequences.",
        skills: [
          { name: "Identify arithmetic and geometric sequences", topic: "alg_arithmetic_sequence" },
          { name: "Arithmetic sequences", topic: "alg_arithmetic_sequence" },
          { name: "Geometric sequences", topic: "alg_geometric_sequence" },
          { name: "Evaluate variable expressions for number sequences", topic: "alg_arithmetic_sequence" },
          { name: "Evaluate recursive formulas for sequences", topic: "alg_recursive_sequence" },
          { name: "Identify a sequence as explicit or recursive", topic: "alg_recursive_sequence" },
          { name: "Write a formula for an arithmetic sequence", topic: "alg_arithmetic_sequence" },
          { name: "Write variable expressions for geometric sequences", topic: "alg_geometric_sequence" },
          { name: "Write a formula for a recursive sequence", topic: "alg_recursive_sequence" },
          { name: "Convert a recursive formula to an explicit formula", topic: "alg_recursive_sequence" },
          { name: "Convert an explicit formula to a recursive formula", topic: "alg_recursive_sequence" },
          { name: "Sequences: mixed review", topic: "alg_arithmetic_sequence" },
        ],
      },
    ],
  },
  {
    domain: "V",
    name: "Exponential Functions",
    color: "bg-brand-orange",
    standards: [
      {
        code: "V",
        desc: "Exponential Functions.",
        skills: [
          { name: "Evaluate an exponential function", topic: "alg_exponential_eval" },
          { name: "Graph exponential functions", topic: "alg_exponential_eval" },
          { name: "Match exponential functions and graphs I", topic: "alg_exponential_eval" },
          { name: "Match exponential functions and graphs II", topic: "alg_exponential_eval" },
          { name: "Domain and range of exponential functions: graphs", topic: "alg_exponential_eval" },
          { name: "Domain and range of exponential functions: equations", topic: "alg_exponential_eval" },
          { name: "Write exponential functions from tables and graphs", topic: "alg_exponential_eval" },
          { name: "Write exponential functions: word problems", topic: "alg_exponential_growth_decay" },
          { name: "Exponential growth and decay", topic: "alg_exponential_growth_decay" },
          { name: "Exponential growth and decay: word problems", topic: "alg_exponential_growth_decay" },
          { name: "Compound interest: word problems", topic: "alg_compound_interest" },
        ],
      },
    ],
  },
  {
    domain: "W",
    name: "Polynomials",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "W",
        desc: "Polynomials.",
        skills: [
          { name: "Polynomial vocabulary", topic: "alg_poly_add_subtract" },
          { name: "Model polynomials with algebra tiles", topic: "alg_poly_add_subtract" },
          { name: "Add and subtract polynomials using algebra tiles", topic: "alg_poly_add_subtract" },
          { name: "Add and subtract polynomials", topic: "alg_poly_add_subtract" },
          { name: "Add polynomials to find perimeter", topic: "alg_poly_add_subtract" },
          { name: "Multiply a polynomial by a monomial", topic: "alg_poly_multiply" },
          { name: "Multiply two binomials using algebra tiles", topic: "alg_poly_multiply" },
          { name: "Multiply two binomials", topic: "alg_poly_multiply" },
          { name: "Multiply two binomials: special cases", topic: "alg_poly_multiply" },
          { name: "Multiply polynomials using area models", topic: "alg_poly_multiply" },
          { name: "Multiply polynomials", topic: "alg_poly_multiply" },
          { name: "Multiply polynomials to find area", topic: "alg_poly_multiply" },
          { name: "Add, subtract, and multiply polynomials", topic: "alg_poly_multiply" },
          { name: "Divide polynomials by monomials", topic: "alg_poly_divide_monomial" },
          { name: "Divide polynomials using long division", topic: "alg_poly_divide_monomial" },
        ],
      },
    ],
  },
  {
    domain: "X",
    name: "Factor Polynomials",
    color: "bg-brand-coral",
    standards: [
      {
        code: "X",
        desc: "Factor Polynomials.",
        skills: [
          { name: "GCF of monomials", topic: "alg_gcf_lcm_monomials" },
          { name: "LCM of monomials", topic: "alg_gcf_lcm_monomials" },
          { name: "Factor out a monomial", topic: "alg_factor_gcf" },
          { name: "Factor quadratics using algebra tiles", topic: "alg_factor_quadratic" },
          { name: "Factor quadratics with leading coefficient 1", topic: "alg_factor_quadratic" },
          { name: "Factor quadratics with other leading coefficients", topic: "alg_factor_quadratic" },
          { name: "Factor quadratics: special cases", topic: "alg_factor_quadratic" },
          { name: "Factor by grouping", topic: "alg_factor_quadratic" },
          { name: "Factor using a quadratic pattern", topic: "alg_factor_quadratic" },
          { name: "Factor polynomials", topic: "alg_factor_quadratic" },
        ],
      },
    ],
  },
  {
    domain: "Y",
    name: "Quadratic Functions",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "Y",
        desc: "Quadratic Functions.",
        skills: [
          { name: "Characteristics of quadratic functions: graphs", topic: "alg_quadratic_vertex" },
          { name: "Characteristics of quadratic functions: equations", topic: "alg_quadratic_vertex" },
          { name: "Complete a function table: quadratic functions", topic: "alg_evaluate_function" },
          { name: "Transformations of quadratic functions", topic: "alg_quadratic_vertex" },
          { name: "Graph quadratic functions in vertex form", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function in vertex form", topic: "alg_quadratic_vertex" },
          { name: "Graph quadratic functions in standard form", topic: "alg_quadratic_vertex" },
          { name: "Match quadratic functions and graphs", topic: "alg_quadratic_vertex" },
          { name: "Domain and range of quadratic functions: graphs", topic: "alg_quadratic_vertex" },
          { name: "Domain and range of quadratic functions: equations", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function in vertex form from its vertex and another point", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function in standard form from its vertex and another point", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function in factored form from its x-intercepts and another point", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function in standard form from its x-intercepts and another point", topic: "alg_quadratic_vertex" },
          { name: "Interpret parts of quadratic expressions: word problems", topic: "alg_quadratic_vertex" },
        ],
      },
    ],
  },
  {
    domain: "Z",
    name: "Quadratic Equations",
    color: "bg-brand-green",
    standards: [
      {
        code: "Z",
        desc: "Quadratic Equations.",
        skills: [
          { name: "Solve a quadratic equation using square roots", topic: "alg_solve_quad_square_roots" },
          { name: "Solve a quadratic equation using the zero product property", topic: "alg_solve_quad_factoring" },
          { name: "Solve a quadratic equation by factoring", topic: "alg_solve_quad_factoring" },
          { name: "Complete the square", topic: "alg_quadratic_formula" },
          { name: "Solve a quadratic equation by completing the square", topic: "alg_quadratic_formula" },
          { name: "Solve a quadratic equation using the quadratic formula", topic: "alg_quadratic_formula" },
          { name: "Solve quadratic equations: word problems", topic: "alg_quadratic_formula" },
          { name: "Using the discriminant", topic: "alg_discriminant" },
          { name: "Solve a system of linear and quadratic equations by graphing", topic: "alg_quadratic_formula" },
          { name: "Solve a system of linear and quadratic equations", topic: "alg_quadratic_formula" },
        ],
      },
    ],
  },
  {
    domain: "AA",
    name: "Linear, Quadratic, and Exponential Functions",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "AA",
        desc: "Linear, Quadratic, and Exponential Functions.",
        skills: [
          { name: "Identify linear and exponential functions from graphs", topic: "alg_rate_of_change" },
          { name: "Identify linear, quadratic, and exponential functions from graphs", topic: "alg_rate_of_change" },
          { name: "Identify linear and exponential functions from tables", topic: "alg_rate_of_change" },
          { name: "Identify linear, quadratic, and exponential functions from tables", topic: "alg_rate_of_change" },
          { name: "Write linear and exponential functions: word problems", topic: "alg_exponential_growth_decay" },
          { name: "Write linear and exponential functions from tables", topic: "alg_exponential_growth_decay" },
          { name: "Write linear, quadratic, and exponential functions from tables", topic: "alg_exponential_growth_decay" },
          { name: "Linear functions over unit intervals", topic: "alg_rate_of_change" },
          { name: "Exponential functions over unit intervals", topic: "alg_exponential_eval" },
          { name: "Describe linear and exponential growth and decay", topic: "alg_exponential_growth_decay" },
          { name: "Compare linear and exponential growth", topic: "alg_exponential_growth_decay" },
          { name: "Compare linear, exponential, and quadratic growth", topic: "alg_exponential_growth_decay" },
        ],
      },
    ],
  },
  {
    domain: "BB",
    name: "Absolute Value Functions",
    color: "bg-brand-orange",
    standards: [
      {
        code: "BB",
        desc: "Absolute Value Functions.",
        skills: [
          { name: "Complete a function table: absolute value functions", topic: "alg_abs_value_equation" },
          { name: "Graph an absolute value function", topic: "alg_abs_value_equation" },
          { name: "Domain and range of absolute value functions: graphs", topic: "alg_abs_value_equation" },
          { name: "Domain and range of absolute value functions: equations", topic: "alg_abs_value_equation" },
          { name: "Transformations of absolute value functions: translations and reflections", topic: "alg_abs_value_inequality" },
          { name: "Transformations of absolute value functions: translations, reflections, and dilations", topic: "alg_abs_value_inequality" },
        ],
      },
    ],
  },
  {
    domain: "CC",
    name: "Function Operations",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "CC",
        desc: "Function Operations.",
        skills: [
          { name: "Add and subtract functions", topic: "alg_function_operations" },
          { name: "Multiply functions", topic: "alg_function_operations" },
          { name: "Find the inverse of a linear function", topic: "alg_inverse_function" },
        ],
      },
    ],
  },
  {
    domain: "DD",
    name: "Function Transformations",
    color: "bg-brand-coral",
    standards: [
      {
        code: "DD",
        desc: "Function Transformations.",
        skills: [
          { name: "Function transformation rules", topic: "alg_quadratic_vertex" },
          { name: "Translations of functions", topic: "alg_quadratic_vertex" },
          { name: "Reflections of functions", topic: "alg_quadratic_vertex" },
          { name: "Dilations of functions", topic: "alg_quadratic_vertex" },
          { name: "Transformations of functions", topic: "alg_quadratic_vertex" },
          { name: "Describe function transformations", topic: "alg_quadratic_vertex" },
        ],
      },
    ],
  },
  {
    domain: "EE",
    name: "Radical Expressions",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "EE",
        desc: "Radical Expressions.",
        skills: [
          { name: "Simplify square roots", topic: "alg_simplify_radical" },
          { name: "Simplify radical expressions with variables: square roots", topic: "alg_simplify_radical" },
          { name: "Add and subtract radical expressions: square roots", topic: "alg_radical_operations" },
          { name: "Multiply radical expressions: square roots", topic: "alg_radical_operations" },
          { name: "Simplify radical expressions involving fractions: square roots", topic: "alg_simplify_radical" },
          { name: "Simplify radical expressions using the distributive property: square roots", topic: "alg_radical_operations" },
          { name: "Simplify radical expressions using conjugates: square roots", topic: "alg_radical_operations" },
          { name: "Simplify radical expressions: mixed review", topic: "alg_simplify_radical" },
          { name: "Convert between rational exponents and radicals", topic: "alg_rational_exponent" },
        ],
      },
    ],
  },
  {
    domain: "FF",
    name: "Radical Functions and Equations",
    color: "bg-brand-green",
    standards: [
      {
        code: "FF",
        desc: "Radical Functions and Equations.",
        skills: [
          { name: "Evaluate square root functions", topic: "alg_radical_equation" },
          { name: "Domain and range of square root functions: graphs", topic: "alg_radical_equation" },
          { name: "Domain and range of square root functions: equations", topic: "alg_radical_equation" },
          { name: "Graph square root functions", topic: "alg_radical_equation" },
          { name: "Graph cube root functions", topic: "alg_radical_equation" },
          { name: "Solve radical equations I", topic: "alg_radical_equation" },
          { name: "Solve radical equations II", topic: "alg_radical_equation" },
        ],
      },
    ],
  },
  {
    domain: "GG",
    name: "Rational Expressions",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "GG",
        desc: "Rational Expressions.",
        skills: [
          { name: "Simplify rational expressions", topic: "alg_rational_expression" },
          { name: "Multiply and divide rational expressions", topic: "alg_rational_expression" },
          { name: "Add and subtract rational expressions", topic: "alg_rational_expression" },
          { name: "Add, subtract, multiply, and divide rational expressions", topic: "alg_rational_expression" },
          { name: "Solve rational equations", topic: "alg_rational_equation" },
        ],
      },
    ],
  },
  {
    domain: "HH",
    name: "Data and Graphs",
    color: "bg-brand-orange",
    standards: [
      {
        code: "HH",
        desc: "Data and Graphs.",
        skills: [
          { name: "Create dot plots", topic: "statistics" },
          { name: "Interpret dot plots", topic: "statistics" },
          { name: "Create histograms", topic: "statistics" },
          { name: "Interpret histograms", topic: "statistics" },
          { name: "Create bar graphs, line graphs, and histograms", topic: "statistics" },
          { name: "Interpret bar graphs, line graphs, and histograms", topic: "statistics" },
          { name: "Interpret circle graphs", topic: "statistics" },
          { name: "Interpret stem-and-leaf plots", topic: "statistics" },
          { name: "Box plots", topic: "alg_quartiles" },
          { name: "Interpret graphics I", topic: "statistics" },
          { name: "Interpret graphics II", topic: "statistics" },
        ],
      },
    ],
  },
  {
    domain: "II",
    name: "Single-Variable Statistics",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "II",
        desc: "Single-Variable Statistics.",
        skills: [
          { name: "Identify biased samples", topic: "statistics" },
          { name: "Mean, median, mode, and range", topic: "statistics" },
          { name: "Calculate quartiles and interquartile range", topic: "alg_quartiles" },
          { name: "Identify an outlier", topic: "alg_quartiles" },
          { name: "Identify an outlier and describe the effect of removing it", topic: "alg_quartiles" },
          { name: "Standard deviation of a population", topic: "statistics" },
          { name: "Standard deviation of a sample", topic: "statistics" },
          { name: "Choose appropriate measures of center and variation", topic: "statistics" },
        ],
      },
    ],
  },
  {
    domain: "JJ",
    name: "Bivariate Statistics",
    color: "bg-brand-coral",
    standards: [
      {
        code: "JJ",
        desc: "Bivariate Statistics.",
        skills: [
          { name: "Create and interpret two-way frequency tables", topic: "statistics" },
          { name: "Interpret a scatter plot", topic: "alg_scatter_correlation" },
          { name: "Outliers in scatter plots", topic: "alg_scatter_correlation" },
          { name: "Match correlation coefficients to scatter plots", topic: "alg_scatter_correlation" },
          { name: "Calculate correlation coefficients", topic: "alg_scatter_correlation" },
          { name: "Write equations for lines of best fit", topic: "geo_line_equation" },
          { name: "Interpret lines of best fit: word problems", topic: "alg_scatter_correlation" },
          { name: "Find the equation of a regression line", topic: "geo_line_equation" },
          { name: "Interpret regression lines", topic: "alg_scatter_correlation" },
          { name: "Analyze a regression line of a data set", topic: "alg_scatter_correlation" },
          { name: "Exponential regression", topic: "alg_exponential_growth_decay" },
          { name: "Correlation and causation", topic: "alg_scatter_correlation" },
        ],
      },
    ],
  },
  {
    domain: "KK",
    name: "Probability",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "KK",
        desc: "Probability.",
        skills: [
          { name: "Theoretical probability", topic: "geo_probability" },
          { name: "Experimental probability", topic: "geo_probability" },
          { name: "Outcomes of compound events", topic: "geo_permutations_combinations" },
          { name: "Identify independent and dependent events", topic: "geo_probability" },
          { name: "Probability of independent and dependent events", topic: "geo_probability" },
          { name: "Counting principle", topic: "geo_permutations_combinations" },
          { name: "Permutations", topic: "geo_permutations_combinations" },
          { name: "Permutation and combination notation", topic: "geo_permutations_combinations" },
        ],
      },
    ],
  },
  {
    domain: "LL",
    name: "Matrices",
    color: "bg-brand-green",
    standards: [
      {
        code: "LL",
        desc: "Matrices.",
        skills: [
          { name: "Matrix vocabulary", topic: "alg_matrix_add" },
          { name: "Matrix operation rules", topic: "alg_matrix_add" },
          { name: "Add and subtract matrices", topic: "alg_matrix_add" },
          { name: "Multiply a matrix by a scalar", topic: "alg_matrix_scalar" },
          { name: "Add and subtract scalar multiples of matrices", topic: "alg_matrix_scalar" },
          { name: "Multiply two matrices", topic: "alg_matrix_scalar" },
          { name: "Properties of matrices", topic: "alg_matrix_add" },
        ],
      },
    ],
  },
];

// Geometry (grade "10"). The stepper mirrors the full A–BB section structure of a
// Geometry skill plan, in alphabetical order, with every listed skill wired to a
// dedicated geometry generator so the practice problems match the skill's topic.
const GEO_PLAN = [
  {
    domain: "A",
    name: "Logic",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "A",
        desc: "Conditionals, converses, negations, and truth values.",
        skills: [
          { name: "Identify hypotheses and conclusions", topic: "geo_logic" },
          { name: "Counterexamples", topic: "geo_logic" },
          { name: "Conditionals", topic: "geo_logic" },
          { name: "Negations", topic: "geo_logic" },
          { name: "Converses, inverses, and contrapositives", topic: "geo_logic" },
          { name: "Biconditionals", topic: "geo_logic" },
          { name: "Truth tables", topic: "geo_logic" },
          { name: "Truth values", topic: "geo_logic" },
        ],
      },
    ],
  },
  {
    domain: "B",
    name: "Points, Lines, Segments, and Planes",
    color: "bg-brand-green",
    standards: [
      {
        code: "B",
        desc: "Segment lengths, midpoints, partitions, and distance.",
        skills: [
          { name: "Lines, line segments, and rays", topic: "geo_segment_addition" },
          { name: "Properties of planes, lines, and points", topic: "geo_segment_addition" },
          { name: "Describe intersections in a plane", topic: "geo_segment_addition" },
          { name: "Lengths of segments on number lines", topic: "geo_segment_addition" },
          { name: "Additive property of length", topic: "geo_segment_addition" },
          { name: "Midpoints", topic: "geo_midpoint" },
          { name: "Weighted averages", topic: "geo_partition_segment" },
          { name: "Congruent line segments", topic: "geo_segment_addition" },
          { name: "Construct a congruent segment", topic: "geo_segment_addition" },
          { name: "Perpendicular Bisector Theorem", topic: "geo_midpoint" },
          { name: "Midpoint formula: find the midpoint", topic: "geo_midpoint" },
          { name: "Partition a line segment in a given ratio", topic: "geo_partition_segment" },
          { name: "Midpoint formula: find the endpoint", topic: "geo_midpoint" },
          { name: "Distance formula", topic: "geo_distance" },
          { name: "Midpoint and distance formulas: mixed practice", topic: "geo_distance" },
          { name: "Distance to the origin in three dimensions", topic: "geo_distance_3d" },
          { name: "Construct the midpoint or perpendicular bisector of a segment", topic: "geo_midpoint" },
        ],
      },
    ],
  },
  {
    domain: "C",
    name: "Angles",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "C",
        desc: "Angle relationships, solving for a variable, and bisectors.",
        skills: [
          { name: "Angle vocabulary", topic: "geo_angle_relationships" },
          { name: "Angle measures", topic: "geo_angle_relationships" },
          { name: "Identify complementary, supplementary, vertical, adjacent, and congruent angles", topic: "geo_angle_relationships" },
          { name: "Find measures of complementary, supplementary, vertical, and adjacent angles", topic: "geo_angle_relationships" },
          { name: "Angle diagrams: solve for the variable", topic: "geo_angle_solve_x" },
          { name: "Angle bisectors", topic: "geo_angle_bisector" },
          { name: "Construct an angle bisector", topic: "geo_angle_bisector" },
          { name: "Construct a congruent angle", topic: "geo_angle_relationships" },
          { name: "Proofs involving angles", topic: "geo_angle_relationships" },
        ],
      },
    ],
  },
  {
    domain: "D",
    name: "Parallel and Perpendicular Lines",
    color: "bg-brand-orange",
    standards: [
      {
        code: "D",
        desc: "Transversals of parallel lines and angle measures.",
        skills: [
          { name: "Identify parallel, intersecting, and skew lines and planes", topic: "geo_transversal_angles" },
          { name: "Construct a perpendicular line", topic: "geo_slope" },
          { name: "Transversals: name angle pairs", topic: "geo_transversal_angles" },
          { name: "Transversals of parallel lines: find angle measures", topic: "geo_transversal_angles" },
          { name: "Transversals of parallel lines: solve for x", topic: "geo_transversal_solve_x" },
          { name: "Construct parallel lines", topic: "geo_slope" },
          { name: "Proofs involving parallel lines I", topic: "geo_transversal_angles" },
          { name: "Proofs involving parallel lines II", topic: "geo_transversal_angles" },
        ],
      },
    ],
  },
  {
    domain: "E",
    name: "Lines in the Coordinate Plane",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "E",
        desc: "Slope, equations of lines, and parallel/perpendicular lines.",
        skills: [
          { name: "Slopes of lines", topic: "geo_slope" },
          { name: "Graph a linear equation", topic: "geo_line_equation" },
          { name: "Equations of lines", topic: "geo_line_equation" },
          { name: "Slopes of parallel and perpendicular lines", topic: "geo_slope" },
          { name: "Equations of parallel and perpendicular lines", topic: "geo_line_equation" },
          { name: "Find the distance between a point and a line", topic: "geo_distance" },
          { name: "Find the distance between two parallel lines", topic: "geo_distance" },
        ],
      },
    ],
  },
  {
    domain: "F",
    name: "Triangles Review",
    color: "bg-brand-coral",
    standards: [
      {
        code: "F",
        desc: "Classifying triangles and angle theorems.",
        skills: [
          { name: "Classify triangles", topic: "geo_classify_triangle" },
          { name: "Triangle Angle-Sum Theorem", topic: "geo_triangle_angles" },
          { name: "Exterior Angle Theorem", topic: "geo_triangle_angles" },
          { name: "Exterior Angle Inequality", topic: "geo_triangle_angles" },
        ],
      },
    ],
  },
  {
    domain: "G",
    name: "Polygons",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "G",
        desc: "Polygon vocabulary and interior/exterior angles.",
        skills: [
          { name: "Polygon vocabulary", topic: "geo_polygon_vocab" },
          { name: "Interior angles of polygons", topic: "geo_polygon_angles" },
          { name: "Exterior angles of polygons", topic: "geo_polygon_angles" },
          { name: "Interior and exterior angles of polygons: mixed practice", topic: "geo_polygon_angles" },
          { name: "Construct an equilateral triangle or regular hexagon", topic: "geo_polygon_vocab" },
          { name: "Construct a square", topic: "geo_polygon_vocab" },
        ],
      },
    ],
  },
  {
    domain: "H",
    name: "Congruence Transformations",
    color: "bg-brand-green",
    standards: [
      {
        code: "H",
        desc: "Translations, reflections, and rotations.",
        skills: [
          { name: "Classify congruence transformations", topic: "geo_translation" },
          { name: "Translations: graph the image", topic: "geo_translation" },
          { name: "Translations: find the coordinates", topic: "geo_translation" },
          { name: "Translations: write the rule", topic: "geo_translation" },
          { name: "Reflections: graph the image", topic: "geo_reflection" },
          { name: "Reflections: find the coordinates", topic: "geo_reflection" },
          { name: "Rotate polygons about a point", topic: "geo_rotation" },
          { name: "Rotations: graph the image", topic: "geo_rotation" },
          { name: "Rotations: find the coordinates", topic: "geo_rotation" },
          { name: "Reflections and rotations: write the rule", topic: "geo_reflection" },
          { name: "Congruence transformations: graph the image", topic: "geo_translation" },
          { name: "Congruence transformations: write the rule", topic: "geo_rotation" },
          { name: "Glide reflections: graph the image", topic: "geo_reflection" },
          { name: "Sequences of congruence transformations: graph the image", topic: "geo_rotation" },
          { name: "Sequences of congruence transformations: choose the sequence", topic: "geo_translation" },
          { name: "Sequences of congruence transformations: find the rules", topic: "geo_reflection" },
          { name: "Transformations that carry a polygon onto itself", topic: "geo_symmetry" },
          { name: "Congruence transformations: mixed review", topic: "geo_translation" },
        ],
      },
    ],
  },
  {
    domain: "I",
    name: "Dilations",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "I",
        desc: "Dilating figures and finding the scale factor.",
        skills: [
          { name: "Dilations: graph the image", topic: "geo_dilation" },
          { name: "Dilations: find the coordinates", topic: "geo_dilation" },
          { name: "Dilations: find length, perimeter, and area", topic: "geo_similar_figures" },
          { name: "Dilations: find the scale factor", topic: "geo_dilation_scale_factor" },
          { name: "Dilations: write the rule", topic: "geo_dilation" },
          { name: "Dilations: find the scale factor and center of the dilation", topic: "geo_dilation_scale_factor" },
          { name: "Dilations and parallel lines", topic: "geo_dilation" },
        ],
      },
    ],
  },
  {
    domain: "J",
    name: "Symmetry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "J",
        desc: "Line symmetry and rotational symmetry.",
        skills: [
          { name: "Line symmetry", topic: "geo_symmetry" },
          { name: "Rotational symmetry", topic: "geo_symmetry" },
          { name: "Draw lines of symmetry", topic: "geo_symmetry" },
          { name: "Count lines of symmetry", topic: "geo_symmetry" },
        ],
      },
    ],
  },
  {
    domain: "K",
    name: "Congruent Figures Review",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "K",
        desc: "Congruence statements and corresponding parts.",
        skills: [
          { name: "Congruence statements and corresponding parts", topic: "geo_corresponding_parts" },
          { name: "Solve problems involving corresponding parts", topic: "geo_corresponding_parts" },
          { name: "Identify congruent figures", topic: "geo_congruence_criteria" },
          { name: "Determine if two figures are congruent: justify your answer", topic: "geo_congruence_criteria" },
        ],
      },
    ],
  },
  {
    domain: "L",
    name: "Congruent Triangles",
    color: "bg-brand-coral",
    standards: [
      {
        code: "L",
        desc: "SSS, SAS, ASA, AAS, and HL congruence.",
        skills: [
          { name: "Prove triangles are congruent using rigid motions", topic: "geo_congruence_criteria" },
          { name: "SSS and SAS Theorems", topic: "geo_congruence_criteria" },
          { name: "Proving triangles congruent by SSS and SAS", topic: "geo_congruence_criteria" },
          { name: "ASA and AAS Theorems", topic: "geo_congruence_criteria" },
          { name: "Proving triangles congruent by ASA and AAS", topic: "geo_congruence_criteria" },
          { name: "SSS, SAS, ASA, and AAS Theorems", topic: "geo_congruence_criteria" },
          { name: "SSS Theorem in the coordinate plane", topic: "geo_distance" },
          { name: "Proving triangles congruent by SSS, SAS, ASA, and AAS", topic: "geo_congruence_criteria" },
          { name: "Proofs involving corresponding parts of congruent triangles", topic: "geo_corresponding_parts" },
          { name: "Congruency in isosceles and equilateral triangles", topic: "geo_classify_triangle" },
          { name: "Proofs involving isosceles triangles", topic: "geo_classify_triangle" },
          { name: "Hypotenuse-Leg Theorem", topic: "geo_congruence_criteria" },
        ],
      },
    ],
  },
  {
    domain: "M",
    name: "Similar Figures",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "M",
        desc: "Similarity ratios, proportionality, and areas.",
        skills: [
          { name: "Ratios in similar figures", topic: "geo_similar_figures" },
          { name: "Similarity statements", topic: "geo_corresponding_parts" },
          { name: "Identify similar figures", topic: "geo_similar_figures" },
          { name: "Side lengths and angle measures in similar figures", topic: "geo_similar_figures" },
          { name: "Similar triangles and indirect measurement", topic: "geo_similar_figures" },
          { name: "Perimeters of similar figures", topic: "geo_similar_figures" },
          { name: "Angle-angle criterion for similar triangles", topic: "geo_similar_figures" },
          { name: "Similarity rules for triangles", topic: "geo_similar_figures" },
          { name: "Similar triangles and similarity transformations", topic: "geo_similar_figures" },
          { name: "Determine if two figures are similar: justify your answer", topic: "geo_similar_figures" },
          { name: "Similarity of circles", topic: "geo_similar_figures" },
          { name: "Triangle Proportionality Theorem", topic: "geo_triangle_proportionality" },
          { name: "Similarity and altitudes in right triangles", topic: "geo_triangle_proportionality" },
          { name: "Areas of similar figures", topic: "geo_similar_figures" },
          { name: "Prove similarity statements", topic: "geo_similar_figures" },
          { name: "Prove proportions or angle congruences using similarity", topic: "geo_triangle_proportionality" },
          { name: "Proofs involving similarity in right triangles", topic: "geo_triangle_proportionality" },
        ],
      },
    ],
  },
  {
    domain: "N",
    name: "Properties of Triangles",
    color: "bg-brand-green",
    standards: [
      {
        code: "N",
        desc: "Midsegments, bisectors, and the Triangle Inequality.",
        skills: [
          { name: "Midsegments of triangles", topic: "geo_midsegment" },
          { name: "Triangles and bisectors", topic: "geo_angle_bisector" },
          { name: "Identify medians, altitudes, angle bisectors, and perpendicular bisectors", topic: "geo_midsegment" },
          { name: "Angle-side relationships in triangles", topic: "geo_triangle_angles" },
          { name: "Triangle Inequality Theorem", topic: "geo_triangle_inequality" },
          { name: "Construct the circumcenter or incenter of a triangle", topic: "geo_midsegment" },
          { name: "Construct the centroid or orthocenter of a triangle", topic: "geo_midsegment" },
          { name: "Find the centroid of a triangle", topic: "geo_midpoint" },
          { name: "Proofs involving triangles I", topic: "geo_triangle_angles" },
          { name: "Proofs involving triangles II", topic: "geo_triangle_angles" },
        ],
      },
    ],
  },
  {
    domain: "O",
    name: "Quadrilaterals",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "O",
        desc: "Classifying quadrilaterals and their angles.",
        skills: [
          { name: "Identify trapezoids", topic: "geo_classify_quadrilateral" },
          { name: "Classify quadrilaterals I", topic: "geo_classify_quadrilateral" },
          { name: "Classify quadrilaterals II", topic: "geo_classify_quadrilateral" },
          { name: "Find missing angles in quadrilaterals", topic: "geo_quadrilateral_angles" },
          { name: "Graph quadrilaterals", topic: "geo_classify_quadrilateral" },
          { name: "Properties of parallelograms", topic: "geo_classify_quadrilateral" },
          { name: "Proving a quadrilateral is a parallelogram", topic: "geo_classify_quadrilateral" },
          { name: "Properties of rhombuses", topic: "geo_classify_quadrilateral" },
          { name: "Properties of squares and rectangles", topic: "geo_classify_quadrilateral" },
          { name: "Properties of trapezoids", topic: "geo_classify_quadrilateral" },
          { name: "Properties of kites", topic: "geo_classify_quadrilateral" },
          { name: "Properties of quadrilaterals: mixed practice", topic: "geo_classify_quadrilateral" },
          { name: "Classify shapes on the coordinate plane: justify your answer", topic: "geo_classify_quadrilateral" },
          { name: "Proofs involving triangles and quadrilaterals", topic: "geo_quadrilateral_angles" },
          { name: "Proofs involving quadrilaterals", topic: "geo_quadrilateral_angles" },
        ],
      },
    ],
  },
  {
    domain: "P",
    name: "Pythagorean Theorem",
    color: "bg-brand-orange",
    standards: [
      {
        code: "P",
        desc: "The Pythagorean theorem and its converse.",
        skills: [
          { name: "Pythagorean theorem", topic: "geo_pythagorean" },
          { name: "Prove the Pythagorean theorem", topic: "geo_pythagorean" },
          { name: "Converse of the Pythagorean theorem", topic: "geo_pythagorean_converse" },
          { name: "Pythagorean Inequality Theorems", topic: "geo_pythagorean_converse" },
        ],
      },
    ],
  },
  {
    domain: "Q",
    name: "Right Triangle Trigonometry",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "Q",
        desc: "Trig ratios, special right triangles, and solving triangles.",
        skills: [
          { name: "Trigonometric ratios: sin, cos, and tan", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios with radicals: sin, cos, and tan", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: csc, sec, and cot", topic: "geo_trig_reciprocal" },
          { name: "Trigonometric ratios in similar right triangles", topic: "geo_trig_ratio" },
          { name: "Special right triangles", topic: "geo_special_right" },
          { name: "Find trigonometric functions of special angles", topic: "geo_special_right" },
          { name: "Solve a special right triangle", topic: "geo_special_right" },
          { name: "Find trigonometric functions using a calculator", topic: "geo_trig_ratio" },
          { name: "Sine and cosine of complementary angles", topic: "geo_trig_ratio" },
          { name: "Find inverses of trigonometric functions: special angles", topic: "geo_trig_angle" },
          { name: "Find inverses of trigonometric functions using a calculator", topic: "geo_trig_angle" },
          { name: "Trigonometric ratios: find a side length", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: find an angle measure", topic: "geo_trig_angle" },
          { name: "Solve a right triangle", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: word problems", topic: "geo_trig_ratio" },
        ],
      },
    ],
  },
  {
    domain: "R",
    name: "Laws of Sines and Cosines",
    color: "bg-brand-coral",
    standards: [
      {
        code: "R",
        desc: "Solving triangles and area with the sine formula.",
        skills: [
          { name: "Law of Sines", topic: "geo_law_of_sines" },
          { name: "Law of Cosines", topic: "geo_law_of_cosines" },
          { name: "Solve a triangle", topic: "geo_law_of_cosines" },
          { name: "Area of a triangle: sine formula", topic: "geo_triangle_area_sine" },
          { name: "Area of a triangle: Law of Sines", topic: "geo_triangle_area_sine" },
        ],
      },
    ],
  },
  {
    domain: "S",
    name: "Area and Perimeter",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "S",
        desc: "Perimeter and area of polygons and circles.",
        skills: [
          { name: "Perimeter", topic: "geo_perimeter" },
          { name: "Area of rectangles and squares", topic: "geo_area_polygons" },
          { name: "Area of parallelograms and triangles", topic: "geo_area_polygons" },
          { name: "Area of trapezoids", topic: "geo_area_polygons" },
          { name: "Area of rhombuses", topic: "geo_area_polygons" },
          { name: "Area of regular polygons", topic: "geo_regular_polygon_area" },
          { name: "Area and perimeter in the coordinate plane I", topic: "geo_perimeter" },
          { name: "Area and perimeter in the coordinate plane II", topic: "geo_area_polygons" },
          { name: "Area and circumference of circles", topic: "geo_circle_area_circumference" },
          { name: "Area of compound figures", topic: "geo_area_polygons" },
          { name: "Area between two shapes", topic: "geo_area_polygons" },
          { name: "Area and perimeter of similar figures", topic: "geo_similar_figures" },
          { name: "Perimeter and area: changes in scale", topic: "geo_similar_figures" },
          { name: "Heron's formula", topic: "geo_herons" },
          { name: "Area and perimeter mixed review", topic: "geo_area_polygons" },
        ],
      },
    ],
  },
  {
    domain: "T",
    name: "Three-Dimensional Figures",
    color: "bg-brand-green",
    standards: [
      {
        code: "T",
        desc: "3D figure parts and cross sections.",
        skills: [
          { name: "Parts of three-dimensional figures", topic: "geo_cross_section" },
          { name: "Three-dimensional figure vocabulary", topic: "geo_cross_section" },
          { name: "Nets and drawings of three-dimensional figures", topic: "geo_surface_area_volume" },
          { name: "Cross sections of three-dimensional figures", topic: "geo_cross_section" },
          { name: "Solids of revolution", topic: "geo_cross_section" },
        ],
      },
    ],
  },
  {
    domain: "U",
    name: "Surface Area and Volume",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "U",
        desc: "Surface area and volume of solids and similar solids.",
        skills: [
          { name: "Lateral area of prisms and cylinders", topic: "geo_surface_area_volume" },
          { name: "Lateral area of pyramids and cones", topic: "geo_surface_area_volume" },
          { name: "Surface area and volume of rectangular prisms", topic: "geo_surface_area_volume" },
          { name: "Surface area of prisms and cylinders", topic: "geo_surface_area_volume" },
          { name: "Surface area of pyramids and cones", topic: "geo_surface_area_volume" },
          { name: "Surface area of spheres", topic: "geo_surface_area_volume" },
          { name: "Surface area: mixed practice", topic: "geo_surface_area_volume" },
          { name: "Volume of prisms and cylinders", topic: "geo_surface_area_volume" },
          { name: "Volume of pyramids and cones", topic: "geo_surface_area_volume" },
          { name: "Volume of spheres", topic: "geo_surface_area_volume" },
          { name: "Volume: mixed practice", topic: "geo_surface_area_volume" },
          { name: "Volume of compound figures", topic: "geo_surface_area_volume" },
          { name: "Similar solids: find the missing length", topic: "geo_similar_solids" },
          { name: "Surface area and volume of similar solids", topic: "geo_similar_solids" },
          { name: "Surface area and volume: changes in scale", topic: "geo_similar_solids" },
          { name: "Perimeter, area, and volume: changes in scale", topic: "geo_similar_solids" },
          { name: "Surface area and volume review", topic: "geo_surface_area_volume" },
        ],
      },
    ],
  },
  {
    domain: "V",
    name: "Measurement",
    color: "bg-brand-orange",
    standards: [
      {
        code: "V",
        desc: "Unit conversion, error, and density.",
        skills: [
          { name: "Convert rates and measurements: customary units", topic: "geo_unit_conversion" },
          { name: "Convert rates and measurements: metric units", topic: "geo_unit_conversion" },
          { name: "Convert square and cubic units of length", topic: "geo_unit_conversion" },
          { name: "Precision", topic: "geo_unit_conversion" },
          { name: "Greatest possible error", topic: "geo_unit_conversion" },
          { name: "Minimum and maximum area and volume", topic: "geo_area_polygons" },
          { name: "Percent error", topic: "geo_unit_conversion" },
          { name: "Percent error: area and volume", topic: "geo_area_polygons" },
          { name: "Calculate density, mass, and volume", topic: "geo_density" },
        ],
      },
    ],
  },
  {
    domain: "W",
    name: "Circles",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "W",
        desc: "Arcs, sectors, radians, and inscribed angles.",
        skills: [
          { name: "Parts of a circle", topic: "geo_central_angle" },
          { name: "Central angles and arc measures", topic: "geo_central_angle" },
          { name: "Understand arc length and sector area of a circle", topic: "geo_arc_sector" },
          { name: "Arc length", topic: "geo_arc_sector" },
          { name: "Convert between radians and degrees", topic: "geo_radians_degrees" },
          { name: "Radians and arc length", topic: "geo_arc_sector" },
          { name: "Area of sectors", topic: "geo_arc_sector" },
          { name: "Circle measurements: mixed review", topic: "geo_arc_sector" },
          { name: "Arcs and chords", topic: "geo_central_angle" },
          { name: "Tangent lines", topic: "geo_central_angle" },
          { name: "Perimeter of polygons with an inscribed circle", topic: "geo_perimeter" },
          { name: "Inscribed angles", topic: "geo_inscribed_angle" },
          { name: "Angles in inscribed right triangles", topic: "geo_inscribed_angle" },
          { name: "Angles in inscribed quadrilaterals I", topic: "geo_inscribed_angle" },
          { name: "Angles in inscribed quadrilaterals II", topic: "geo_inscribed_angle" },
          { name: "Angles formed by chords, secants, and tangents", topic: "geo_inscribed_angle" },
          { name: "Segments formed by chords, secants, and tangents", topic: "geo_central_angle" },
        ],
      },
    ],
  },
  {
    domain: "X",
    name: "Constructions with Circles",
    color: "bg-brand-coral",
    standards: [
      {
        code: "X",
        desc: "Inscribed polygons and circumscribed circles.",
        skills: [
          { name: "Construct a tangent line to a circle", topic: "geo_central_angle" },
          { name: "Construct an equilateral triangle inscribed in a circle", topic: "geo_polygon_vocab" },
          { name: "Construct a square inscribed in a circle", topic: "geo_polygon_vocab" },
          { name: "Construct a regular hexagon inscribed in a circle", topic: "geo_polygon_vocab" },
          { name: "Construct the inscribed or circumscribed circle of a triangle", topic: "geo_circle_from_center_radius" },
        ],
      },
    ],
  },
  {
    domain: "Y",
    name: "Circles in the Coordinate Plane",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "Y",
        desc: "Center, radius, and equations of circles.",
        skills: [
          { name: "Find the center of a circle", topic: "geo_circle_equation" },
          { name: "Find the radius or diameter of a circle", topic: "geo_circle_equation" },
          { name: "Determine if a point lies on a circle", topic: "geo_circle_equation" },
          { name: "Derive equations of circles using the Pythagorean theorem", topic: "geo_circle_from_center_radius" },
          { name: "Write equations of circles in standard form from graphs", topic: "geo_circle_from_center_radius" },
          { name: "Write equations of circles in standard form using properties", topic: "geo_circle_from_center_radius" },
          { name: "Convert equations of circles from general to standard form", topic: "geo_circle_equation" },
          { name: "Find properties of circles from equations in general form", topic: "geo_circle_equation" },
          { name: "Graph circles from equations in standard form", topic: "geo_circle_equation" },
          { name: "Graph circles from equations in general form", topic: "geo_circle_equation" },
        ],
      },
    ],
  },
  {
    domain: "Z",
    name: "Parabolas in the Coordinate Plane",
    color: "bg-brand-green",
    standards: [
      {
        code: "Z",
        desc: "Vertex, focus, directrix, and axis of symmetry.",
        skills: [
          { name: "Find the vertex of a parabola", topic: "geo_parabola_vertex" },
          { name: "Find the focus or directrix of a parabola", topic: "geo_parabola_focus" },
          { name: "Find the axis of symmetry of a parabola", topic: "geo_parabola_vertex" },
          { name: "Write equations of parabolas in vertex form from graphs", topic: "geo_parabola_vertex" },
          { name: "Write equations of parabolas in vertex form using the focus and directrix", topic: "geo_parabola_focus" },
          { name: "Write equations of parabolas in vertex form using properties", topic: "geo_parabola_vertex" },
          { name: "Convert equations of parabolas from general to vertex form", topic: "geo_parabola_vertex" },
          { name: "Find properties of a parabola from equations in general form", topic: "geo_parabola_focus" },
          { name: "Graph parabolas", topic: "geo_parabola_vertex" },
        ],
      },
    ],
  },
  {
    domain: "AA",
    name: "Probability",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "AA",
        desc: "Theoretical probability, permutations, and combinations.",
        skills: [
          { name: "Theoretical and experimental probability", topic: "geo_probability" },
          { name: "Outcomes of compound events", topic: "geo_permutations_combinations" },
          { name: "Identify independent and dependent events", topic: "geo_probability" },
          { name: "Probability of independent and dependent events", topic: "geo_probability" },
          { name: "Counting principle", topic: "geo_permutations_combinations" },
          { name: "Permutations", topic: "geo_permutations_combinations" },
          { name: "Permutation and combination notation", topic: "geo_permutations_combinations" },
          { name: "Find probabilities using combinations and permutations", topic: "geo_permutations_combinations" },
          { name: "Find probabilities using two-way frequency tables", topic: "geo_probability" },
          { name: "Identify independent events", topic: "geo_probability" },
          { name: "Find conditional probabilities", topic: "geo_probability" },
          { name: "Find conditional probabilities using two-way frequency tables", topic: "geo_probability" },
          { name: "Independence and conditional probability", topic: "geo_probability" },
          { name: "Geometric probability", topic: "geo_probability" },
          { name: "Find probabilities using the addition rule", topic: "geo_probability" },
        ],
      },
    ],
  },
  {
    domain: "BB",
    name: "Vectors",
    color: "bg-brand-orange",
    standards: [
      {
        code: "BB",
        desc: "Magnitude, component form, and adding and subtracting vectors.",
        skills: [
          { name: "Compass directions and vectors", topic: "geo_vectors" },
          { name: "Find the magnitude of a vector", topic: "geo_vectors" },
          { name: "Find the component form of a vector", topic: "geo_vectors" },
          { name: "Find the component form of a vector given its magnitude and direction angle", topic: "geo_vectors" },
          { name: "Graph a resultant vector using the triangle method", topic: "geo_vectors" },
          { name: "Graph a resultant vector using the parallelogram method", topic: "geo_vectors" },
          { name: "Add vectors", topic: "geo_vectors" },
          { name: "Subtract vectors", topic: "geo_vectors" },
        ],
      },
    ],
  },
];

// Algebra 2 (grade "11"). The stepper mirrors the full A–UU section structure of
// an Algebra 2 skill plan, in alphabetical order, with every listed skill wired to a
// generator (algebra-specific alg_* generators, or reused geometry/statistics
// generators) so the practice problems match the skill's topic.
const ALG2_PLAN = [
  {
    domain: "A",
    name: "Function Concepts",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "A",
        desc: "Function Concepts.",
        skills: [
          { name: "Domain and range", topic: "alg_evaluate_function" },
          { name: "Identify functions", topic: "alg_evaluate_function" },
          { name: "Evaluate functions", topic: "alg_evaluate_function" },
          { name: "Find values using function graphs", topic: "alg_evaluate_function" },
          { name: "Complete a table for a function graph", topic: "alg_evaluate_function" },
          { name: "Identify graphs: word problems", topic: "alg_evaluate_function" },
          { name: "Find solutions using a table", topic: "alg_evaluate_function" },
          { name: "Approximate solutions using a table", topic: "alg_evaluate_function" },
          { name: "Average rate of change", topic: "alg_rate_of_change" },
        ],
      },
    ],
  },
  {
    domain: "B",
    name: "Linear Equations",
    color: "bg-brand-green",
    standards: [
      {
        code: "B",
        desc: "Linear Equations.",
        skills: [
          { name: "Solve linear equations", topic: "alg_two_step_eq" },
          { name: "Solve linear equations: complete the solution", topic: "alg_two_step_eq" },
          { name: "Solve linear equations: word problems", topic: "alg_vars_both_sides" },
          { name: "Rearrange multi-variable equations", topic: "alg_rearrange_formula" },
        ],
      },
    ],
  },
  {
    domain: "C",
    name: "Linear Inequalities",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "C",
        desc: "Linear Inequalities.",
        skills: [
          { name: "Graph inequalities", topic: "alg_inequality" },
          { name: "Write inequalities from graphs", topic: "alg_inequality" },
          { name: "Solve linear inequalities", topic: "alg_inequality" },
          { name: "Graph solutions to linear inequalities", topic: "alg_inequality" },
        ],
      },
    ],
  },
  {
    domain: "D",
    name: "Linear Functions",
    color: "bg-brand-orange",
    standards: [
      {
        code: "D",
        desc: "Linear Functions.",
        skills: [
          { name: "Find the slope of a linear function", topic: "geo_slope" },
          { name: "Graph a linear function", topic: "alg_slope_intercept" },
          { name: "Write the equation of a linear function", topic: "geo_line_equation" },
          { name: "Linear functions over unit intervals", topic: "alg_rate_of_change" },
        ],
      },
    ],
  },
  {
    domain: "E",
    name: "Systems of Linear Equations",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "E",
        desc: "Systems of Linear Equations.",
        skills: [
          { name: "Is (x, y) a solution to the system of equations?", topic: "alg_system" },
          { name: "Solve a system of equations by graphing", topic: "alg_system" },
          { name: "Solve a system of equations by graphing: word problems", topic: "alg_system" },
          { name: "Find the number of solutions to a system of equations", topic: "alg_system" },
          { name: "Classify a system of equations", topic: "alg_system" },
          { name: "Solve a system of equations using substitution", topic: "alg_system" },
          { name: "Solve a system of equations using substitution: word problems", topic: "alg_system" },
          { name: "Solve a system of equations using elimination", topic: "alg_system" },
          { name: "Solve a system of equations using elimination: word problems", topic: "alg_system" },
          { name: "Solve a system of equations using any method", topic: "alg_system" },
          { name: "Solve a system of equations using any method: word problems", topic: "alg_system" },
          { name: "Solve a system of equations in three variables using substitution", topic: "alg_system" },
          { name: "Solve a system of equations in three variables using elimination", topic: "alg_system" },
          { name: "Determine the number of solutions to a system of equations in three variables", topic: "alg_system" },
        ],
      },
    ],
  },
  {
    domain: "F",
    name: "Two-Variable Linear Inequalities",
    color: "bg-brand-coral",
    standards: [
      {
        code: "F",
        desc: "Two-Variable Linear Inequalities.",
        skills: [
          { name: "Write two-variable linear inequalities: word problems", topic: "alg_two_var_inequality" },
          { name: "Graph a two-variable linear inequality", topic: "alg_two_var_inequality" },
          { name: "Is (x, y) a solution to the system of inequalities?", topic: "alg_two_var_inequality" },
          { name: "Solve systems of linear inequalities by graphing", topic: "alg_two_var_inequality" },
          { name: "Find the vertices of a solution set for a system of linear inequalities", topic: "alg_two_var_inequality" },
          { name: "Linear programming", topic: "alg_two_var_inequality" },
        ],
      },
    ],
  },
  {
    domain: "G",
    name: "Absolute Value Equations and Inequalities",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "G",
        desc: "Absolute Value Equations and Inequalities.",
        skills: [
          { name: "Solve absolute value equations", topic: "alg_abs_value_equation" },
          { name: "Graph solutions to absolute value equations", topic: "alg_abs_value_equation" },
          { name: "Write absolute value equations from graphs", topic: "alg_abs_value_equation" },
          { name: "Solve absolute value inequalities", topic: "alg_abs_value_inequality" },
          { name: "Graph solutions to absolute value inequalities", topic: "alg_abs_value_inequality" },
        ],
      },
    ],
  },
  {
    domain: "H",
    name: "Absolute Value Functions",
    color: "bg-brand-green",
    standards: [
      {
        code: "H",
        desc: "Absolute Value Functions.",
        skills: [
          { name: "Domain and range of absolute value functions: graphs", topic: "alg_abs_value_equation" },
          { name: "Domain and range of absolute value functions: equations", topic: "alg_abs_value_equation" },
          { name: "Graph an absolute value function", topic: "alg_abs_value_equation" },
          { name: "Transformations of absolute value functions", topic: "alg_abs_value_equation" },
          { name: "Graph solutions to two-variable absolute value inequalities", topic: "alg_abs_value_equation" },
          { name: "Solve systems of linear and absolute value inequalities by graphing", topic: "alg_abs_value_equation" },
        ],
      },
    ],
  },
  {
    domain: "I",
    name: "Piecewise-Defined Functions",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "I",
        desc: "Piecewise-Defined Functions.",
        skills: [
          { name: "Evaluate piecewise-defined functions", topic: "alg_piecewise_eval" },
          { name: "Graph piecewise-defined functions", topic: "alg_piecewise_eval" },
        ],
      },
    ],
  },
  {
    domain: "J",
    name: "Real Numbers",
    color: "bg-brand-orange",
    standards: [
      {
        code: "J",
        desc: "Real Numbers.",
        skills: [
          { name: "Sort rational and irrational numbers", topic: "alg_classify_number" },
          { name: "Classify rational and irrational numbers", topic: "alg_classify_number" },
          { name: "Properties of operations on rational and irrational numbers", topic: "alg_classify_number" },
        ],
      },
    ],
  },
  {
    domain: "K",
    name: "Complex Numbers",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "K",
        desc: "Complex Numbers.",
        skills: [
          { name: "Introduction to complex numbers", topic: "alg_complex_add_subtract" },
          { name: "Add and subtract complex numbers", topic: "alg_complex_add_subtract" },
          { name: "Complex conjugates", topic: "alg_complex_conjugate" },
          { name: "Multiply complex numbers", topic: "alg_complex_multiply" },
          { name: "Divide complex numbers", topic: "alg_complex_multiply" },
          { name: "Add, subtract, multiply, and divide complex numbers", topic: "alg_complex_multiply" },
          { name: "Absolute values of complex numbers", topic: "alg_complex_abs" },
          { name: "Powers of i", topic: "alg_powers_of_i" },
        ],
      },
    ],
  },
  {
    domain: "L",
    name: "Polynomial Operations",
    color: "bg-brand-coral",
    standards: [
      {
        code: "L",
        desc: "Polynomial Operations.",
        skills: [
          { name: "Polynomial vocabulary", topic: "alg_poly_add_subtract" },
          { name: "Add and subtract polynomials", topic: "alg_poly_add_subtract" },
          { name: "Multiply polynomials", topic: "alg_poly_multiply" },
          { name: "Divide polynomials using long division", topic: "alg_poly_divide_monomial" },
          { name: "Divide polynomials using synthetic division", topic: "alg_poly_divide_monomial" },
          { name: "Add, subtract, multiply, and divide polynomials", topic: "alg_poly_multiply" },
          { name: "Evaluate polynomials using synthetic division", topic: "alg_evaluate_function" },
          { name: "Pascal's triangle", topic: "alg_binomial_coefficient" },
          { name: "Pascal's triangle and the Binomial Theorem", topic: "alg_binomial_coefficient" },
          { name: "Binomial Theorem I", topic: "alg_binomial_coefficient" },
          { name: "Binomial Theorem II", topic: "alg_binomial_coefficient" },
        ],
      },
    ],
  },
  {
    domain: "M",
    name: "Factor Polynomials",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "M",
        desc: "Factor Polynomials.",
        skills: [
          { name: "Sort factors of single-variable expressions", topic: "alg_factor_gcf" },
          { name: "Sort factors of multi-variable expressions", topic: "alg_factor_gcf" },
          { name: "Factor out a monomial", topic: "alg_factor_gcf" },
          { name: "Factor quadratics using algebra tiles", topic: "alg_factor_quadratic" },
          { name: "Factor quadratics", topic: "alg_factor_quadratic" },
          { name: "Factor using a quadratic pattern", topic: "alg_factor_quadratic" },
          { name: "Factor by grouping", topic: "alg_factor_quadratic" },
          { name: "Factor sums and differences of cubes", topic: "alg_factor_cubes" },
          { name: "Factor polynomials", topic: "alg_factor_quadratic" },
        ],
      },
    ],
  },
  {
    domain: "N",
    name: "Quadratic Functions",
    color: "bg-brand-green",
    standards: [
      {
        code: "N",
        desc: "Quadratic Functions.",
        skills: [
          { name: "Characteristics of quadratic functions: graphs", topic: "alg_quadratic_vertex" },
          { name: "Characteristics of quadratic functions: equations", topic: "alg_quadratic_vertex" },
          { name: "Complete a function table: quadratic functions", topic: "alg_evaluate_function" },
          { name: "Transformations of quadratic functions", topic: "alg_quadratic_vertex" },
          { name: "Graph a quadratic function", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function in vertex form", topic: "alg_quadratic_vertex" },
          { name: "Match quadratic functions and graphs", topic: "alg_quadratic_vertex" },
          { name: "Domain and range of quadratic functions: graphs", topic: "alg_quadratic_vertex" },
          { name: "Domain and range of quadratic functions: equations", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function from its zeros", topic: "alg_write_poly_from_roots" },
          { name: "Write a quadratic function in vertex form from its vertex and another point", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function in standard form from its vertex and another point", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function in factored form from its x-intercepts and another point", topic: "alg_write_poly_from_roots" },
          { name: "Write a quadratic function in standard form from its x-intercepts and another point", topic: "alg_write_poly_from_roots" },
          { name: "Write a quadratic function from three points", topic: "alg_quadratic_vertex" },
          { name: "Write a quadratic function: mixed review", topic: "alg_quadratic_vertex" },
          { name: "Interpret parts of quadratic expressions: word problems", topic: "alg_quadratic_vertex" },
        ],
      },
    ],
  },
  {
    domain: "O",
    name: "Quadratic Equations and Inequalities",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "O",
        desc: "Quadratic Equations and Inequalities.",
        skills: [
          { name: "Solve a quadratic equation using square roots", topic: "alg_solve_quad_square_roots" },
          { name: "Solve a quadratic equation using the zero product property", topic: "alg_solve_quad_factoring" },
          { name: "Solve a quadratic equation by factoring", topic: "alg_solve_quad_factoring" },
          { name: "Complete the square", topic: "alg_quadratic_formula" },
          { name: "Solve a quadratic equation by completing the square", topic: "alg_quadratic_formula" },
          { name: "Solve a quadratic equation using the quadratic formula", topic: "alg_quadratic_formula" },
          { name: "Solve quadratic equations: word problems", topic: "alg_quadratic_formula" },
          { name: "Using the discriminant", topic: "alg_discriminant" },
          { name: "Graph solutions to quadratic inequalities", topic: "alg_solve_quad_factoring" },
          { name: "Solve quadratic inequalities", topic: "alg_solve_quad_factoring" },
        ],
      },
    ],
  },
  {
    domain: "P",
    name: "Systems of Linear and Quadratic Equations",
    color: "bg-brand-orange",
    standards: [
      {
        code: "P",
        desc: "Systems of Linear and Quadratic Equations.",
        skills: [
          { name: "Solve a linear-quadratic system of equations by graphing: lines and parabolas", topic: "alg_quadratic_formula" },
          { name: "Solve a linear-quadratic system of equations: lines and parabolas", topic: "alg_quadratic_formula" },
          { name: "Solve a linear-quadratic system of equations: lines and circles", topic: "alg_quadratic_formula" },
          { name: "Solve a quadratic-quadratic system of equations by graphing: parabolas", topic: "alg_quadratic_formula" },
        ],
      },
    ],
  },
  {
    domain: "Q",
    name: "Polynomial Equations and Functions",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "Q",
        desc: "Polynomial Equations and Functions.",
        skills: [
          { name: "Factor theorem", topic: "alg_poly_roots" },
          { name: "Find the roots of factored polynomials", topic: "alg_poly_roots" },
          { name: "Write a polynomial from its roots", topic: "alg_write_poly_from_roots" },
          { name: "Factor a polynomial given a root", topic: "alg_poly_roots" },
          { name: "Solve cubic and quartic polynomial equations by factoring", topic: "alg_poly_roots" },
          { name: "Rational root theorem", topic: "alg_poly_roots" },
          { name: "Complex conjugate theorem", topic: "alg_complex_conjugate" },
          { name: "Conjugate root theorems", topic: "alg_complex_conjugate" },
          { name: "Descartes' Rule of Signs", topic: "alg_poly_roots" },
          { name: "Fundamental Theorem of Algebra", topic: "alg_poly_roots" },
          { name: "Find the roots of polynomials", topic: "alg_poly_roots" },
          { name: "Match polynomial functions and graphs using end behavior", topic: "alg_poly_roots" },
          { name: "Match polynomial functions and graphs using zeros", topic: "alg_poly_roots" },
          { name: "Match polynomial functions and graphs", topic: "alg_poly_roots" },
          { name: "Domain and range of polynomial functions", topic: "alg_evaluate_function" },
          { name: "Even and odd functions", topic: "alg_evaluate_function" },
        ],
      },
    ],
  },
  {
    domain: "R",
    name: "Radical Expressions",
    color: "bg-brand-coral",
    standards: [
      {
        code: "R",
        desc: "Radical Expressions.",
        skills: [
          { name: "Roots of integers", topic: "alg_square_cube_roots" },
          { name: "Roots of rational numbers", topic: "alg_square_cube_roots" },
          { name: "Find roots using a calculator", topic: "alg_square_cube_roots" },
          { name: "Simplify radical expressions with variables I", topic: "alg_simplify_radical" },
          { name: "Simplify radical expressions with variables II", topic: "alg_simplify_radical" },
          { name: "Nth roots", topic: "alg_square_cube_roots" },
          { name: "Multiply radical expressions", topic: "alg_radical_operations" },
          { name: "Simplify radical expressions involving fractions", topic: "alg_simplify_radical" },
          { name: "Add and subtract radical expressions", topic: "alg_radical_operations" },
          { name: "Simplify radical expressions using the distributive property", topic: "alg_radical_operations" },
          { name: "Simplify radical expressions using conjugates", topic: "alg_radical_operations" },
        ],
      },
    ],
  },
  {
    domain: "S",
    name: "Radical Functions and Equations",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "S",
        desc: "Radical Functions and Equations.",
        skills: [
          { name: "Domain and range of radical functions", topic: "alg_radical_equation" },
          { name: "Graph square root functions", topic: "alg_radical_equation" },
          { name: "Graph cube root functions", topic: "alg_radical_equation" },
          { name: "Solve radical equations", topic: "alg_radical_equation" },
        ],
      },
    ],
  },
  {
    domain: "T",
    name: "Rational Exponents",
    color: "bg-brand-green",
    standards: [
      {
        code: "T",
        desc: "Rational Exponents.",
        skills: [
          { name: "Evaluate rational exponents", topic: "alg_rational_exponent" },
          { name: "Convert between rational exponents and radicals", topic: "alg_rational_exponent" },
          { name: "Multiplication with rational exponents", topic: "alg_rational_exponent" },
          { name: "Division with rational exponents", topic: "alg_rational_exponent" },
          { name: "Power rule", topic: "alg_rational_exponent" },
          { name: "Simplify expressions involving rational exponents I", topic: "alg_rational_exponent" },
          { name: "Simplify expressions involving rational exponents II", topic: "alg_rational_exponent" },
          { name: "Solve equations with rational exponents", topic: "alg_rational_exponent" },
        ],
      },
    ],
  },
  {
    domain: "U",
    name: "Function Operations",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "U",
        desc: "Function Operations.",
        skills: [
          { name: "Add and subtract functions", topic: "alg_function_operations" },
          { name: "Multiply functions", topic: "alg_function_operations" },
          { name: "Divide functions", topic: "alg_function_operations" },
          { name: "Add, subtract, multiply, and divide functions", topic: "alg_function_operations" },
          { name: "Composition of linear functions: find a value", topic: "alg_composition" },
          { name: "Composition of linear functions: find an equation", topic: "alg_composition" },
          { name: "Composition of linear and quadratic functions: find a value", topic: "alg_composition" },
          { name: "Composition of linear and quadratic functions: find an equation", topic: "alg_composition" },
        ],
      },
    ],
  },
  {
    domain: "V",
    name: "Inverse Functions",
    color: "bg-brand-orange",
    standards: [
      {
        code: "V",
        desc: "Inverse Functions.",
        skills: [
          { name: "Identify inverse functions", topic: "alg_inverse_function" },
          { name: "Find values of inverse functions from tables", topic: "alg_inverse_function" },
          { name: "Find values of inverse functions from graphs", topic: "alg_inverse_function" },
          { name: "Graphs of inverse functions", topic: "alg_inverse_function" },
          { name: "Find inverse functions and relations", topic: "alg_inverse_function" },
        ],
      },
    ],
  },
  {
    domain: "W",
    name: "Function Transformations",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "W",
        desc: "Function Transformations.",
        skills: [
          { name: "Function transformation rules", topic: "alg_quadratic_vertex" },
          { name: "Translations of functions", topic: "alg_quadratic_vertex" },
          { name: "Reflections of functions", topic: "alg_quadratic_vertex" },
          { name: "Dilations of functions", topic: "alg_quadratic_vertex" },
          { name: "Transformations of functions", topic: "alg_quadratic_vertex" },
          { name: "Describe function transformations", topic: "alg_quadratic_vertex" },
        ],
      },
    ],
  },
  {
    domain: "X",
    name: "Rational Functions and Expressions",
    color: "bg-brand-coral",
    standards: [
      {
        code: "X",
        desc: "Rational Functions and Expressions.",
        skills: [
          { name: "Rational functions: asymptotes and excluded values", topic: "alg_rational_expression" },
          { name: "Domain and range of reciprocal functions", topic: "alg_rational_expression" },
          { name: "Evaluate rational expressions I", topic: "alg_rational_expression" },
          { name: "Evaluate rational expressions II", topic: "alg_rational_expression" },
          { name: "Simplify rational expressions", topic: "alg_rational_expression" },
          { name: "Multiply and divide rational expressions", topic: "alg_rational_expression" },
          { name: "Add and subtract rational expressions", topic: "alg_rational_expression" },
          { name: "Add, subtract, multiply, and divide rational expressions", topic: "alg_rational_expression" },
          { name: "Simplify complex fractions", topic: "alg_rational_expression" },
          { name: "Solve rational equations", topic: "alg_rational_equation" },
        ],
      },
    ],
  },
  {
    domain: "Y",
    name: "Variation",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "Y",
        desc: "Variation.",
        skills: [
          { name: "Write and solve direct variation equations", topic: "alg_direct_variation" },
          { name: "Write and solve inverse variation equations", topic: "alg_inverse_variation" },
          { name: "Classify variation", topic: "alg_inverse_variation" },
          { name: "Write joint and combined variation equations I", topic: "alg_direct_variation" },
          { name: "Find the constant of variation", topic: "alg_direct_variation" },
          { name: "Write joint and combined variation equations II", topic: "alg_inverse_variation" },
          { name: "Solve variation equations", topic: "alg_direct_variation" },
        ],
      },
    ],
  },
  {
    domain: "Z",
    name: "Exponential Functions",
    color: "bg-brand-green",
    standards: [
      {
        code: "Z",
        desc: "Exponential Functions.",
        skills: [
          { name: "Domain and range of exponential functions", topic: "alg_exponential_eval" },
          { name: "Evaluate exponential functions", topic: "alg_exponential_eval" },
          { name: "Write exponential functions: word problems", topic: "alg_exponential_growth_decay" },
          { name: "Graph exponential functions", topic: "alg_exponential_eval" },
          { name: "Match exponential functions and graphs", topic: "alg_exponential_eval" },
          { name: "Exponential functions over unit intervals", topic: "alg_exponential_eval" },
          { name: "Identify linear and exponential functions", topic: "alg_rate_of_change" },
          { name: "Describe linear and exponential growth and decay", topic: "alg_exponential_growth_decay" },
          { name: "Exponential growth and decay", topic: "alg_exponential_growth_decay" },
          { name: "Exponential growth and decay: word problems", topic: "alg_exponential_growth_decay" },
        ],
      },
    ],
  },
  {
    domain: "AA",
    name: "Logarithms",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "AA",
        desc: "Logarithms.",
        skills: [
          { name: "Convert between exponential and logarithmic form: rational bases", topic: "alg_log_convert" },
          { name: "Convert between natural exponential and logarithmic form", topic: "alg_log_convert" },
          { name: "Convert between exponential and logarithmic form: all bases", topic: "alg_log_convert" },
          { name: "Evaluate logarithms", topic: "alg_evaluate_log" },
          { name: "Evaluate natural logarithms", topic: "alg_evaluate_log" },
          { name: "Change of base formula", topic: "alg_evaluate_log" },
          { name: "Evaluate logarithms using a calculator", topic: "alg_evaluate_log" },
          { name: "Identify properties of logarithms", topic: "alg_log_properties" },
          { name: "Product property of logarithms", topic: "alg_log_properties" },
          { name: "Quotient property of logarithms", topic: "alg_log_properties" },
          { name: "Power property of logarithms", topic: "alg_log_properties" },
          { name: "Properties of logarithms: mixed review", topic: "alg_log_properties" },
          { name: "Evaluate logarithms using properties", topic: "alg_log_properties" },
        ],
      },
    ],
  },
  {
    domain: "BB",
    name: "Logarithmic Functions",
    color: "bg-brand-orange",
    standards: [
      {
        code: "BB",
        desc: "Logarithmic Functions.",
        skills: [
          { name: "Domain and range of logarithmic functions", topic: "alg_evaluate_log" },
          { name: "Graph logarithmic functions", topic: "alg_evaluate_log" },
        ],
      },
    ],
  },
  {
    domain: "CC",
    name: "Exponential and Logarithmic Equations",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "CC",
        desc: "Exponential and Logarithmic Equations.",
        skills: [
          { name: "Solve exponential equations by rewriting the base", topic: "alg_solve_exponential" },
          { name: "Solve exponential equations using common logarithms", topic: "alg_solve_exponential" },
          { name: "Solve exponential equations using natural logarithms", topic: "alg_solve_exponential" },
          { name: "Compound interest: word problems", topic: "alg_compound_interest" },
          { name: "Continuously compounded interest: word problems", topic: "alg_continuous_interest" },
          { name: "Solve logarithmic equations I", topic: "alg_solve_logarithmic" },
          { name: "Solve logarithmic equations II", topic: "alg_solve_logarithmic" },
        ],
      },
    ],
  },
  {
    domain: "DD",
    name: "Parabolas",
    color: "bg-brand-coral",
    standards: [
      {
        code: "DD",
        desc: "Parabolas.",
        skills: [
          { name: "Identify the direction a parabola opens", topic: "alg_quadratic_vertex" },
          { name: "Find the vertex of a parabola", topic: "geo_parabola_vertex" },
          { name: "Find the focus or directrix of a parabola", topic: "geo_parabola_focus" },
          { name: "Find the axis of symmetry of a parabola", topic: "geo_parabola_vertex" },
          { name: "Write equations of parabolas in vertex form from graphs", topic: "geo_parabola_vertex" },
          { name: "Write equations of parabolas in vertex form using properties", topic: "geo_parabola_vertex" },
          { name: "Convert equations of parabolas from general to vertex form", topic: "geo_parabola_vertex" },
          { name: "Find properties of a parabola from equations in general form", topic: "geo_parabola_focus" },
          { name: "Graph parabolas", topic: "geo_parabola_vertex" },
        ],
      },
    ],
  },
  {
    domain: "EE",
    name: "Circles",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "EE",
        desc: "Circles.",
        skills: [
          { name: "Find the center of a circle", topic: "geo_circle_equation" },
          { name: "Find the radius or diameter of a circle", topic: "geo_circle_equation" },
          { name: "Write equations of circles in standard form from graphs", topic: "geo_circle_from_center_radius" },
          { name: "Write equations of circles in standard form using properties", topic: "geo_circle_from_center_radius" },
          { name: "Convert equations of circles from general to standard form", topic: "geo_circle_equation" },
          { name: "Find properties of circles from equations in general form", topic: "geo_circle_equation" },
          { name: "Graph circles", topic: "geo_circle_equation" },
        ],
      },
    ],
  },
  {
    domain: "FF",
    name: "Ellipses",
    color: "bg-brand-green",
    standards: [
      {
        code: "FF",
        desc: "Ellipses.",
        skills: [
          { name: "Find the center, vertices, or co-vertices of an ellipse", topic: "alg_ellipse" },
          { name: "Find the length of the major or minor axis of an ellipse", topic: "alg_ellipse" },
          { name: "Find the foci of an ellipse", topic: "alg_ellipse" },
          { name: "Write equations of ellipses in standard form from graphs", topic: "alg_ellipse" },
          { name: "Write equations of ellipses in standard form using properties", topic: "alg_ellipse" },
          { name: "Convert equations of ellipses from general to standard form", topic: "alg_ellipse" },
          { name: "Find properties of ellipses from equations in general form", topic: "alg_ellipse" },
        ],
      },
    ],
  },
  {
    domain: "GG",
    name: "Hyperbolas",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "GG",
        desc: "Hyperbolas.",
        skills: [
          { name: "Find the center of a hyperbola", topic: "alg_hyperbola" },
          { name: "Find the vertices of a hyperbola", topic: "alg_hyperbola" },
          { name: "Find the length of the transverse or conjugate axes of a hyperbola", topic: "alg_hyperbola" },
          { name: "Find the equations for the asymptotes of a hyperbola", topic: "alg_hyperbola" },
          { name: "Find the foci of a hyperbola", topic: "alg_hyperbola" },
          { name: "Write equations of hyperbolas in standard form from graphs", topic: "alg_hyperbola" },
          { name: "Write equations of hyperbolas in standard form using properties", topic: "alg_hyperbola" },
          { name: "Convert equations of hyperbolas from general to standard form", topic: "alg_hyperbola" },
          { name: "Find properties of hyperbolas from equations in general form", topic: "alg_hyperbola" },
        ],
      },
    ],
  },
  {
    domain: "HH",
    name: "Right Triangle Trigonometry",
    color: "bg-brand-orange",
    standards: [
      {
        code: "HH",
        desc: "Right Triangle Trigonometry.",
        skills: [
          { name: "Pythagorean Theorem and its converse", topic: "geo_pythagorean" },
          { name: "Special right triangles", topic: "geo_special_right" },
          { name: "Trigonometric ratios: sin, cos, and tan", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: csc, sec, and cot", topic: "geo_trig_reciprocal" },
          { name: "Trigonometric ratios in similar right triangles", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: find a side length", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: find an angle measure", topic: "geo_trig_angle" },
          { name: "Solve a right triangle", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: word problems", topic: "geo_trig_ratio" },
        ],
      },
    ],
  },
  {
    domain: "II",
    name: "Angle Measures",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "II",
        desc: "Angle Measures.",
        skills: [
          { name: "Convert between radians and degrees", topic: "geo_radians_degrees" },
          { name: "Radians and arc length", topic: "geo_arc_sector" },
          { name: "Graphs of angles", topic: "alg_coterminal_angle" },
          { name: "Quadrants", topic: "alg_reference_angle" },
          { name: "Coterminal angles", topic: "alg_coterminal_angle" },
          { name: "Reference angles", topic: "alg_reference_angle" },
        ],
      },
    ],
  },
  {
    domain: "JJ",
    name: "Unit Circle Trigonometry",
    color: "bg-brand-coral",
    standards: [
      {
        code: "JJ",
        desc: "Unit Circle Trigonometry.",
        skills: [
          { name: "Determine trigonometric ratios using points on the unit circle", topic: "alg_unit_circle" },
          { name: "Evaluate trigonometric ratios using reference angles: sin, cos, tan", topic: "alg_unit_circle" },
          { name: "Evaluate trigonometric ratios using reference angles: sec, csc, cot", topic: "alg_unit_circle" },
          { name: "Evaluate trigonometric ratios using reference angles", topic: "alg_unit_circle" },
          { name: "Approximate trigonometric ratios using a calculator", topic: "alg_unit_circle" },
          { name: "Inverses of sin, cos, and tan: degrees", topic: "alg_solve_trig" },
          { name: "Inverses of sin, cos, and tan: radians", topic: "alg_solve_trig" },
          { name: "Inverses of csc, sec, and cot", topic: "alg_solve_trig" },
          { name: "Solve trigonometric equations: sin, cos, tan", topic: "alg_solve_trig" },
          { name: "Solve trigonometric equations", topic: "alg_solve_trig" },
        ],
      },
    ],
  },
  {
    domain: "KK",
    name: "Laws of Sines and Cosines",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "KK",
        desc: "Laws of Sines and Cosines.",
        skills: [
          { name: "Law of Sines", topic: "geo_law_of_sines" },
          { name: "Law of Cosines", topic: "geo_law_of_cosines" },
          { name: "Solve a triangle", topic: "geo_law_of_cosines" },
          { name: "Area of a triangle: sine formula", topic: "geo_triangle_area_sine" },
          { name: "Area of a triangle: Law of Sines", topic: "geo_triangle_area_sine" },
        ],
      },
    ],
  },
  {
    domain: "LL",
    name: "Trigonometric Functions",
    color: "bg-brand-green",
    standards: [
      {
        code: "LL",
        desc: "Trigonometric Functions.",
        skills: [
          { name: "Find properties of sine functions", topic: "alg_trig_function_properties" },
          { name: "Write equations of sine functions from graphs", topic: "alg_trig_function_properties" },
          { name: "Write equations of sine functions using properties", topic: "alg_trig_function_properties" },
          { name: "Graph sine functions", topic: "alg_trig_function_properties" },
          { name: "Graph translations of sine functions", topic: "alg_trig_function_properties" },
          { name: "Find properties of cosine functions", topic: "alg_trig_function_properties" },
          { name: "Write equations of cosine functions from graphs", topic: "alg_trig_function_properties" },
          { name: "Write equations of cosine functions using properties", topic: "alg_trig_function_properties" },
          { name: "Graph cosine functions", topic: "alg_trig_function_properties" },
          { name: "Graph translations of cosine functions", topic: "alg_trig_function_properties" },
          { name: "Find properties of sine and cosine functions", topic: "alg_trig_function_properties" },
          { name: "Write equations of sine and cosine functions from graphs", topic: "alg_trig_function_properties" },
          { name: "Write equations of sine and cosine functions using properties", topic: "alg_trig_function_properties" },
          { name: "Graph sine and cosine functions", topic: "alg_trig_function_properties" },
          { name: "Graph translations of sine and cosine functions", topic: "alg_trig_function_properties" },
        ],
      },
    ],
  },
  {
    domain: "MM",
    name: "Trigonometric Identities",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "MM",
        desc: "Trigonometric Identities.",
        skills: [
          { name: "Complementary angle identities", topic: "alg_trig_identity" },
          { name: "Symmetry and periodicity of trigonometric functions", topic: "alg_trig_identity" },
          { name: "Find trigonometric ratios using a Pythagorean or reciprocal identity", topic: "alg_trig_identity" },
          { name: "Find trigonometric ratios using multiple identities", topic: "alg_trig_identity" },
          { name: "Trigonometric sum and difference identities", topic: "alg_trig_identity" },
          { name: "Solve trigonometric equations using sum and difference identities", topic: "alg_trig_identity" },
        ],
      },
    ],
  },
  {
    domain: "NN",
    name: "Sequences",
    color: "bg-brand-orange",
    standards: [
      {
        code: "NN",
        desc: "Sequences.",
        skills: [
          { name: "Find terms of an arithmetic sequence", topic: "alg_arithmetic_sequence" },
          { name: "Find terms of a geometric sequence", topic: "alg_geometric_sequence" },
          { name: "Evaluate recursive formulas for sequences", topic: "alg_recursive_sequence" },
          { name: "Classify formulas and sequences", topic: "alg_recursive_sequence" },
          { name: "Write a formula for an arithmetic sequence", topic: "alg_arithmetic_sequence" },
          { name: "Write a formula for a geometric sequence", topic: "alg_geometric_sequence" },
          { name: "Write a formula for an arithmetic or geometric sequence", topic: "alg_arithmetic_sequence" },
          { name: "Write a recursive formula for a sequence", topic: "alg_recursive_sequence" },
          { name: "Convert a recursive formula to an explicit formula", topic: "alg_recursive_sequence" },
          { name: "Convert an explicit formula to a recursive formula", topic: "alg_recursive_sequence" },
          { name: "Convert between explicit and recursive formulas", topic: "alg_recursive_sequence" },
          { name: "Sequences: mixed review", topic: "alg_arithmetic_sequence" },
        ],
      },
    ],
  },
  {
    domain: "OO",
    name: "Series",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "OO",
        desc: "Series.",
        skills: [
          { name: "Identify arithmetic and geometric series", topic: "alg_arithmetic_series" },
          { name: "Write a series using sigma notation", topic: "alg_arithmetic_series" },
          { name: "Find the sum of an arithmetic series", topic: "alg_arithmetic_series" },
          { name: "Find the sum of a finite geometric series", topic: "alg_geometric_series" },
          { name: "Introduction to partial sums", topic: "alg_arithmetic_series" },
          { name: "Partial sums of arithmetic series", topic: "alg_arithmetic_series" },
          { name: "Partial sums of geometric series", topic: "alg_geometric_series" },
          { name: "Partial sums: mixed review", topic: "alg_geometric_series" },
          { name: "Convergent and divergent geometric series", topic: "alg_infinite_geometric" },
          { name: "Find the value of an infinite geometric series", topic: "alg_infinite_geometric" },
          { name: "Write a repeating decimal as a fraction", topic: "alg_repeating_decimal" },
        ],
      },
    ],
  },
  {
    domain: "PP",
    name: "Probability",
    color: "bg-brand-coral",
    standards: [
      {
        code: "PP",
        desc: "Probability.",
        skills: [
          { name: "Calculate probabilities of events", topic: "geo_probability" },
          { name: "Counting principle", topic: "geo_permutations_combinations" },
          { name: "Combinations and permutations", topic: "geo_permutations_combinations" },
          { name: "Find probabilities using combinations and permutations", topic: "geo_permutations_combinations" },
          { name: "Find probabilities using two-way frequency tables", topic: "geo_probability" },
          { name: "Identify independent events", topic: "geo_probability" },
          { name: "Probability of independent and dependent events", topic: "geo_probability" },
          { name: "Find conditional probabilities", topic: "geo_probability" },
          { name: "Independence and conditional probability", topic: "geo_probability" },
          { name: "Find conditional probabilities using two-way frequency tables", topic: "geo_probability" },
          { name: "Determine independence using conditional probabilities given two way frequency tables", topic: "geo_probability" },
          { name: "Find probabilities using the addition rule", topic: "geo_probability" },
        ],
      },
    ],
  },
  {
    domain: "QQ",
    name: "Probability Distributions",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "QQ",
        desc: "Probability Distributions.",
        skills: [
          { name: "Identify probability distributions", topic: "alg_expected_value" },
          { name: "Identify discrete and continuous random variables", topic: "alg_expected_value" },
          { name: "Write a discrete probability distribution", topic: "alg_expected_value" },
          { name: "Graph a discrete probability distribution", topic: "alg_expected_value" },
          { name: "Expected values of random variables", topic: "alg_expected_value" },
          { name: "Variance of random variables", topic: "alg_expected_value" },
          { name: "Standard deviation of random variables", topic: "alg_expected_value" },
          { name: "Write the probability distribution for a game of chance", topic: "alg_expected_value" },
          { name: "Expected values for a game of chance", topic: "alg_expected_value" },
          { name: "Choose the better bet", topic: "alg_expected_value" },
        ],
      },
    ],
  },
  {
    domain: "RR",
    name: "Binomial and Normal Distributions",
    color: "bg-brand-green",
    standards: [
      {
        code: "RR",
        desc: "Binomial and Normal Distributions.",
        skills: [
          { name: "Find probabilities using the binomial distribution", topic: "alg_binomial_probability" },
          { name: "Find probabilities using the normal distribution I", topic: "alg_zscore" },
          { name: "Find probabilities using the normal distribution II", topic: "alg_zscore" },
          { name: "Find z-values", topic: "alg_zscore" },
          { name: "Find values of normal variables", topic: "alg_zscore" },
          { name: "Distributions of sample means", topic: "alg_zscore" },
        ],
      },
    ],
  },
  {
    domain: "SS",
    name: "Single-Variable Statistics",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "SS",
        desc: "Single-Variable Statistics.",
        skills: [
          { name: "Identify biased samples", topic: "statistics" },
          { name: "Standard deviation of a population", topic: "statistics" },
          { name: "Standard deviation of a sample", topic: "statistics" },
          { name: "Identify an outlier", topic: "alg_quartiles" },
          { name: "Identify an outlier and describe the effect of removing it", topic: "alg_quartiles" },
          { name: "Find confidence intervals for population means", topic: "alg_zscore" },
          { name: "Find confidence intervals for population proportions", topic: "alg_zscore" },
          { name: "Interpret confidence intervals for population means", topic: "alg_zscore" },
          { name: "Experiment design", topic: "statistics" },
          { name: "Analyze the results of an experiment using simulations", topic: "statistics" },
        ],
      },
    ],
  },
  {
    domain: "TT",
    name: "Bivariate Statistics",
    color: "bg-brand-orange",
    standards: [
      {
        code: "TT",
        desc: "Bivariate Statistics.",
        skills: [
          { name: "Outliers in scatter plots", topic: "alg_scatter_correlation" },
          { name: "Match correlation coefficients to scatter plots", topic: "alg_scatter_correlation" },
          { name: "Calculate correlation coefficients", topic: "alg_scatter_correlation" },
          { name: "Find the equation of a regression line", topic: "geo_line_equation" },
          { name: "Interpret regression lines", topic: "alg_scatter_correlation" },
          { name: "Analyze a regression line of a data set", topic: "alg_scatter_correlation" },
          { name: "Exponential regression", topic: "alg_exponential_growth_decay" },
        ],
      },
    ],
  },
  {
    domain: "UU",
    name: "Matrices",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "UU",
        desc: "Matrices.",
        skills: [
          { name: "Matrix vocabulary", topic: "alg_matrix_add" },
          { name: "Matrix operation rules", topic: "alg_matrix_add" },
          { name: "Add and subtract matrices", topic: "alg_matrix_add" },
          { name: "Multiply a matrix by a scalar", topic: "alg_matrix_scalar" },
          { name: "Add and subtract scalar multiples of matrices", topic: "alg_matrix_scalar" },
          { name: "Multiply two matrices", topic: "alg_matrix_multiply" },
          { name: "Add, subtract, and multiply matrices", topic: "alg_matrix_multiply" },
          { name: "Simplify matrix expressions", topic: "alg_matrix_add" },
          { name: "Properties of matrices", topic: "alg_matrix_add" },
          { name: "Solve matrix equations", topic: "alg_matrix_add" },
          { name: "Determinant of a matrix", topic: "alg_matrix_determinant" },
          { name: "Is a matrix invertible?", topic: "alg_matrix_determinant" },
          { name: "Inverse of a matrix", topic: "alg_matrix_determinant" },
          { name: "Identify inverse matrices", topic: "alg_matrix_determinant" },
          { name: "Solve matrix equations using inverses", topic: "alg_matrix_determinant" },
          { name: "Identify transformation matrices", topic: "alg_matrix_scalar" },
          { name: "Transformation matrices: write the vertex matrix", topic: "alg_matrix_scalar" },
          { name: "Transformation matrices: graph the image", topic: "alg_matrix_scalar" },
          { name: "Solve a system of equations using augmented matrices", topic: "alg_system" },
          { name: "Solve a system of equations using augmented matrices: word problems", topic: "alg_system" },
        ],
      },
    ],
  },
];

// Precalculus (grade "12"). The stepper mirrors the full A–JJ section structure of
// a Precalculus skill plan, in alphabetical order, with every listed skill wired to a
// generator (algebra / geometry / calculus generators) so the practice problems match
// the skill's topic.
const PRECALC_PLAN = [
  {
    domain: "A",
    name: "Function Concepts",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "A",
        desc: "Function Concepts.",
        skills: [
          { name: "Domain and range", topic: "alg_evaluate_function" },
          { name: "Identify functions", topic: "alg_evaluate_function" },
          { name: "Evaluate functions", topic: "alg_evaluate_function" },
          { name: "Find values using function graphs", topic: "alg_evaluate_function" },
          { name: "Complete a table for a function graph", topic: "alg_evaluate_function" },
          { name: "Identify graphs: word problems", topic: "alg_evaluate_function" },
          { name: "Add, subtract, multiply, and divide functions", topic: "alg_function_operations" },
          { name: "Composition of functions", topic: "alg_composition" },
        ],
      },
    ],
  },
  {
    domain: "B",
    name: "Inverse Functions",
    color: "bg-brand-green",
    standards: [
      {
        code: "B",
        desc: "Inverse Functions.",
        skills: [
          { name: "Identify inverse functions", topic: "alg_inverse_function" },
          { name: "Find values of inverse functions from tables", topic: "alg_inverse_function" },
          { name: "Find values of inverse functions from graphs", topic: "alg_inverse_function" },
          { name: "Graphs of inverse functions", topic: "alg_inverse_function" },
          { name: "Find inverse functions and relations", topic: "alg_inverse_function" },
        ],
      },
    ],
  },
  {
    domain: "C",
    name: "Function Transformations",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "C",
        desc: "Function Transformations.",
        skills: [
          { name: "Function transformation rules", topic: "alg_quadratic_vertex" },
          { name: "Translations of functions", topic: "alg_quadratic_vertex" },
          { name: "Reflections of functions", topic: "alg_quadratic_vertex" },
          { name: "Dilations of functions", topic: "alg_quadratic_vertex" },
          { name: "Transformations of functions", topic: "alg_quadratic_vertex" },
          { name: "Describe function transformations", topic: "alg_quadratic_vertex" },
        ],
      },
    ],
  },
  {
    domain: "D",
    name: "Quadratic Functions",
    color: "bg-brand-orange",
    standards: [
      {
        code: "D",
        desc: "Quadratic Functions.",
        skills: [
          { name: "Find the maximum or minimum value of a quadratic function", topic: "alg_quadratic_vertex" },
          { name: "Characteristics of quadratic functions", topic: "alg_quadratic_vertex" },
          { name: "Graph a quadratic function", topic: "alg_quadratic_vertex" },
          { name: "Match quadratic functions and graphs", topic: "alg_quadratic_vertex" },
          { name: "Solve a quadratic equation using square roots", topic: "alg_solve_quad_square_roots" },
          { name: "Solve a quadratic equation by factoring", topic: "alg_solve_quad_factoring" },
          { name: "Solve a quadratic equation by completing the square", topic: "alg_quadratic_formula" },
          { name: "Solve a quadratic equation using the quadratic formula", topic: "alg_quadratic_formula" },
          { name: "Solve quadratic equations: word problems", topic: "alg_quadratic_formula" },
          { name: "Using the discriminant", topic: "alg_discriminant" },
        ],
      },
    ],
  },
  {
    domain: "E",
    name: "Polynomial Expressions and Equations",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "E",
        desc: "Polynomial Expressions and Equations.",
        skills: [
          { name: "Polynomial vocabulary", topic: "alg_poly_add_subtract" },
          { name: "Divide polynomials using long division", topic: "alg_poly_divide_monomial" },
          { name: "Divide polynomials using synthetic division", topic: "alg_poly_divide_monomial" },
          { name: "Evaluate polynomials using synthetic division", topic: "alg_evaluate_function" },
          { name: "Factor sums and differences of cubes", topic: "alg_factor_cubes" },
          { name: "Solve equations with sums and differences of cubes", topic: "alg_factor_cubes" },
          { name: "Factor using a quadratic pattern", topic: "alg_factor_quadratic" },
          { name: "Solve equations using a quadratic pattern", topic: "alg_solve_quad_factoring" },
          { name: "Pascal's triangle", topic: "alg_binomial_coefficient" },
          { name: "Pascal's triangle and the Binomial Theorem", topic: "alg_binomial_coefficient" },
          { name: "Binomial Theorem I", topic: "alg_binomial_coefficient" },
          { name: "Binomial Theorem II", topic: "alg_binomial_coefficient" },
        ],
      },
    ],
  },
  {
    domain: "F",
    name: "Polynomial Functions",
    color: "bg-brand-coral",
    standards: [
      {
        code: "F",
        desc: "Polynomial Functions.",
        skills: [
          { name: "Factor theorem", topic: "alg_poly_roots" },
          { name: "Find the roots of factored polynomials", topic: "alg_poly_roots" },
          { name: "Write a polynomial from its roots", topic: "alg_write_poly_from_roots" },
          { name: "Factor a polynomial given a root", topic: "alg_poly_roots" },
          { name: "Rational root theorem", topic: "alg_poly_roots" },
          { name: "Complex conjugate theorem", topic: "alg_complex_conjugate" },
          { name: "Conjugate root theorems", topic: "alg_complex_conjugate" },
          { name: "Descartes' Rule of Signs", topic: "alg_poly_roots" },
          { name: "Fundamental Theorem of Algebra", topic: "alg_poly_roots" },
          { name: "Find the roots of polynomials", topic: "alg_poly_roots" },
          { name: "Match polynomial functions and graphs using end behavior", topic: "alg_poly_roots" },
          { name: "Match polynomial functions and graphs using zeros", topic: "alg_poly_roots" },
          { name: "Match polynomial functions and graphs", topic: "alg_poly_roots" },
          { name: "Domain and range of polynomial functions", topic: "alg_evaluate_function" },
          { name: "Even and odd functions", topic: "alg_evaluate_function" },
        ],
      },
    ],
  },
  {
    domain: "G",
    name: "Rational Functions",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "G",
        desc: "Rational Functions.",
        skills: [
          { name: "Rational functions: asymptotes and excluded values", topic: "alg_rational_expression" },
          { name: "Domain and range of reciprocal functions", topic: "alg_rational_expression" },
          { name: "Solve rational equations", topic: "alg_rational_equation" },
          { name: "Check whether two rational functions are inverses", topic: "alg_inverse_function" },
        ],
      },
    ],
  },
  {
    domain: "H",
    name: "Exponential Functions",
    color: "bg-brand-green",
    standards: [
      {
        code: "H",
        desc: "Exponential Functions.",
        skills: [
          { name: "Domain and range of exponential functions", topic: "alg_exponential_eval" },
          { name: "Match exponential functions and graphs", topic: "alg_exponential_eval" },
          { name: "Linear and exponential functions over unit intervals", topic: "alg_exponential_eval" },
          { name: "Identify linear and exponential functions", topic: "alg_rate_of_change" },
          { name: "Describe linear and exponential growth and decay", topic: "alg_exponential_growth_decay" },
        ],
      },
    ],
  },
  {
    domain: "I",
    name: "Logarithms",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "I",
        desc: "Logarithms.",
        skills: [
          { name: "Convert between exponential and logarithmic form", topic: "alg_log_convert" },
          { name: "Evaluate logarithms", topic: "alg_evaluate_log" },
          { name: "Change of base formula", topic: "alg_evaluate_log" },
          { name: "Product property of logarithms", topic: "alg_log_properties" },
          { name: "Quotient property of logarithms", topic: "alg_log_properties" },
          { name: "Power property of logarithms", topic: "alg_log_properties" },
          { name: "Properties of logarithms: mixed review", topic: "alg_log_properties" },
          { name: "Evaluate logarithms using properties", topic: "alg_log_properties" },
        ],
      },
    ],
  },
  {
    domain: "J",
    name: "Logarithmic Functions",
    color: "bg-brand-orange",
    standards: [
      {
        code: "J",
        desc: "Logarithmic Functions.",
        skills: [
          { name: "Domain and range of logarithmic functions", topic: "alg_evaluate_log" },
          { name: "Graph logarithmic functions", topic: "alg_evaluate_log" },
        ],
      },
    ],
  },
  {
    domain: "K",
    name: "Exponential and Logarithmic Equations",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "K",
        desc: "Exponential and Logarithmic Equations.",
        skills: [
          { name: "Solve exponential equations by rewriting the base", topic: "alg_solve_exponential" },
          { name: "Solve exponential equations using logarithms", topic: "alg_solve_exponential" },
          { name: "Solve logarithmic equations with one logarithm", topic: "alg_solve_logarithmic" },
          { name: "Solve logarithmic equations with multiple logarithms", topic: "alg_solve_logarithmic" },
          { name: "Exponential growth and decay: word problems", topic: "alg_exponential_growth_decay" },
          { name: "Compound interest: word problems", topic: "alg_compound_interest" },
        ],
      },
    ],
  },
  {
    domain: "L",
    name: "Radical Expressions and Functions",
    color: "bg-brand-coral",
    standards: [
      {
        code: "L",
        desc: "Radical Expressions and Functions.",
        skills: [
          { name: "Simplify radical expressions with variables", topic: "alg_simplify_radical" },
          { name: "Nth roots", topic: "alg_square_cube_roots" },
          { name: "Domain and range of radical functions", topic: "alg_radical_equation" },
          { name: "Graph square root functions", topic: "alg_radical_equation" },
          { name: "Solve radical equations", topic: "alg_radical_equation" },
        ],
      },
    ],
  },
  {
    domain: "M",
    name: "Rational Exponents",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "M",
        desc: "Rational Exponents.",
        skills: [
          { name: "Evaluate rational exponents", topic: "alg_rational_exponent" },
          { name: "Convert between rational exponents and radicals", topic: "alg_rational_exponent" },
          { name: "Operations with rational exponents", topic: "alg_rational_exponent" },
          { name: "Simplify expressions involving rational exponents", topic: "alg_rational_exponent" },
          { name: "Solve equations with rational exponents", topic: "alg_rational_exponent" },
        ],
      },
    ],
  },
  {
    domain: "N",
    name: "Systems of Equations",
    color: "bg-brand-green",
    standards: [
      {
        code: "N",
        desc: "Systems of Equations.",
        skills: [
          { name: "Solve a system of equations by graphing", topic: "alg_system" },
          { name: "Solve a system of equations by graphing: word problems", topic: "alg_system" },
          { name: "Classify a system of equations", topic: "alg_system" },
          { name: "Solve a system of equations using substitution", topic: "alg_system" },
          { name: "Solve a system of equations using substitution: word problems", topic: "alg_system" },
          { name: "Solve a system of equations using elimination", topic: "alg_system" },
          { name: "Solve a system of equations using elimination: word problems", topic: "alg_system" },
          { name: "Solve a system of equations using augmented matrices", topic: "alg_system" },
          { name: "Solve a system of equations using augmented matrices: word problems", topic: "alg_system" },
          { name: "Solve a system of equations in three variables using substitution", topic: "alg_system" },
          { name: "Solve a system of equations in three variables using elimination", topic: "alg_system" },
          { name: "Determine the number of solutions to a system of equations in three variables", topic: "alg_system" },
        ],
      },
    ],
  },
  {
    domain: "O",
    name: "Systems of Inequalities",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "O",
        desc: "Systems of Inequalities.",
        skills: [
          { name: "Solve systems of linear inequalities by graphing", topic: "alg_two_var_inequality" },
          { name: "Solve systems of linear and absolute value inequalities by graphing", topic: "alg_two_var_inequality" },
          { name: "Find the vertices of a solution set", topic: "alg_two_var_inequality" },
          { name: "Linear programming", topic: "alg_two_var_inequality" },
        ],
      },
    ],
  },
  {
    domain: "P",
    name: "Nonlinear Inequalities",
    color: "bg-brand-orange",
    standards: [
      {
        code: "P",
        desc: "Nonlinear Inequalities.",
        skills: [
          { name: "Graph solutions to quadratic inequalities", topic: "alg_solve_quad_factoring" },
          { name: "Solve quadratic inequalities", topic: "alg_solve_quad_factoring" },
          { name: "Graph solutions to higher-degree inequalities", topic: "alg_solve_quad_factoring" },
          { name: "Solve higher-degree inequalities", topic: "alg_solve_quad_factoring" },
        ],
      },
    ],
  },
  {
    domain: "Q",
    name: "Matrices",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "Q",
        desc: "Matrices.",
        skills: [
          { name: "Matrix vocabulary", topic: "alg_matrix_add" },
          { name: "Matrix operation rules", topic: "alg_matrix_add" },
          { name: "Add and subtract matrices", topic: "alg_matrix_add" },
          { name: "Multiply a matrix by a scalar", topic: "alg_matrix_scalar" },
          { name: "Add and subtract scalar multiples of matrices", topic: "alg_matrix_scalar" },
          { name: "Multiply two matrices", topic: "alg_matrix_multiply" },
          { name: "Add, subtract, and multiply matrices", topic: "alg_matrix_multiply" },
          { name: "Simplify matrix expressions", topic: "alg_matrix_add" },
          { name: "Properties of matrices", topic: "alg_matrix_add" },
          { name: "Solve matrix equations", topic: "alg_matrix_add" },
          { name: "Determinant of a matrix", topic: "alg_matrix_determinant" },
          { name: "Is a matrix invertible?", topic: "alg_matrix_determinant" },
          { name: "Inverse of a 2 x 2 matrix", topic: "alg_matrix_determinant" },
          { name: "Inverse of a 3 x 3 matrix", topic: "alg_matrix_determinant" },
          { name: "Identify inverse matrices", topic: "alg_matrix_determinant" },
          { name: "Solve matrix equations using inverses", topic: "alg_matrix_determinant" },
          { name: "Identify transformation matrices", topic: "alg_matrix_scalar" },
          { name: "Transformation matrices: write the vertex matrix", topic: "alg_matrix_scalar" },
          { name: "Transformation matrices: graph the image", topic: "alg_matrix_scalar" },
        ],
      },
    ],
  },
  {
    domain: "R",
    name: "Trigonometry",
    color: "bg-brand-coral",
    standards: [
      {
        code: "R",
        desc: "Trigonometry.",
        skills: [
          { name: "Convert between radians and degrees", topic: "geo_radians_degrees" },
          { name: "Radians and arc length", topic: "geo_arc_sector" },
          { name: "Quadrants", topic: "alg_reference_angle" },
          { name: "Coterminal and reference angles", topic: "alg_coterminal_angle" },
          { name: "Find trigonometric ratios using right triangles", topic: "geo_trig_ratio" },
          { name: "Find trigonometric ratios using the unit circle", topic: "alg_unit_circle" },
          { name: "Find trigonometric ratios of special angles", topic: "alg_unit_circle" },
          { name: "Find trigonometric ratios using reference angles", topic: "alg_unit_circle" },
          { name: "Inverses of trigonometric functions", topic: "alg_solve_trig" },
          { name: "Inverses of trigonometric functions using a calculator", topic: "alg_solve_trig" },
          { name: "Solve trigonometric equations", topic: "alg_solve_trig" },
          { name: "Trigonometric ratios: find a side length", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: find an angle measure", topic: "geo_trig_angle" },
          { name: "Solve a right triangle", topic: "geo_trig_ratio" },
          { name: "Trigonometric ratios: word problems", topic: "geo_trig_ratio" },
          { name: "Law of Sines", topic: "geo_law_of_sines" },
          { name: "Law of Cosines", topic: "geo_law_of_cosines" },
          { name: "Solve a triangle", topic: "geo_law_of_cosines" },
          { name: "Area of a triangle: sine formula", topic: "geo_triangle_area_sine" },
          { name: "Area of a triangle: Heron's formula", topic: "geo_herons" },
        ],
      },
    ],
  },
  {
    domain: "S",
    name: "Trigonometric Functions",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "S",
        desc: "Trigonometric Functions.",
        skills: [
          { name: "Find properties of sine functions", topic: "alg_trig_function_properties" },
          { name: "Write equations of sine functions from graphs", topic: "alg_trig_function_properties" },
          { name: "Write equations of sine functions using properties", topic: "alg_trig_function_properties" },
          { name: "Graph sine functions", topic: "alg_trig_function_properties" },
          { name: "Graph translations of sine functions", topic: "alg_trig_function_properties" },
          { name: "Find properties of cosine functions", topic: "alg_trig_function_properties" },
          { name: "Write equations of cosine functions from graphs", topic: "alg_trig_function_properties" },
          { name: "Write equations of cosine functions using properties", topic: "alg_trig_function_properties" },
          { name: "Graph cosine functions", topic: "alg_trig_function_properties" },
          { name: "Graph translations of cosine functions", topic: "alg_trig_function_properties" },
          { name: "Find properties of sine and cosine functions", topic: "alg_trig_function_properties" },
          { name: "Write equations of sine and cosine functions from graphs", topic: "alg_trig_function_properties" },
          { name: "Write equations of sine and cosine functions using properties", topic: "alg_trig_function_properties" },
          { name: "Graph sine and cosine functions", topic: "alg_trig_function_properties" },
          { name: "Graph translations of sine and cosine functions", topic: "alg_trig_function_properties" },
        ],
      },
    ],
  },
  {
    domain: "T",
    name: "Trigonometric Identities",
    color: "bg-brand-green",
    standards: [
      {
        code: "T",
        desc: "Trigonometric Identities.",
        skills: [
          { name: "Complementary angle identities", topic: "alg_trig_identity" },
          { name: "Symmetry and periodicity of trigonometric functions", topic: "alg_trig_identity" },
          { name: "Find trigonometric ratios using a Pythagorean or reciprocal identity", topic: "alg_trig_identity" },
          { name: "Find trigonometric ratios using multiple identities", topic: "alg_trig_identity" },
          { name: "Trigonometric sum and difference identities", topic: "alg_trig_identity" },
          { name: "Solve trigonometric equations using sum and difference identities", topic: "alg_trig_identity" },
        ],
      },
    ],
  },
  {
    domain: "U",
    name: "Conic Sections",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "U",
        desc: "Conic Sections.",
        skills: [
          { name: "Find properties of parabolas", topic: "geo_parabola_focus" },
          { name: "Write equations of parabolas in vertex form", topic: "geo_parabola_vertex" },
          { name: "Graph parabolas", topic: "geo_parabola_vertex" },
          { name: "Find properties of circles", topic: "geo_circle_equation" },
          { name: "Write equations of circles in standard form", topic: "geo_circle_from_center_radius" },
          { name: "Graph circles", topic: "geo_circle_equation" },
          { name: "Find properties of ellipses", topic: "alg_ellipse" },
          { name: "Find the eccentricity of an ellipse", topic: "alg_ellipse" },
          { name: "Write equations of ellipses in standard form", topic: "alg_ellipse" },
          { name: "Find properties of hyperbolas", topic: "alg_hyperbola" },
          { name: "Find the eccentricity of a hyperbola", topic: "alg_hyperbola" },
          { name: "Write equations of hyperbolas in standard form", topic: "alg_hyperbola" },
          { name: "Convert equations of conic sections from general to standard form", topic: "geo_circle_equation" },
        ],
      },
    ],
  },
  {
    domain: "V",
    name: "Complex Numbers",
    color: "bg-brand-orange",
    standards: [
      {
        code: "V",
        desc: "Complex Numbers.",
        skills: [
          { name: "Add and subtract complex numbers", topic: "alg_complex_add_subtract" },
          { name: "Complex conjugates", topic: "alg_complex_conjugate" },
          { name: "Multiply complex numbers", topic: "alg_complex_multiply" },
          { name: "Divide complex numbers", topic: "alg_complex_multiply" },
          { name: "Multiply and divide complex numbers", topic: "alg_complex_multiply" },
          { name: "Add, subtract, multiply, and divide complex numbers", topic: "alg_complex_multiply" },
          { name: "Absolute values of complex numbers", topic: "alg_complex_abs" },
          { name: "Powers of i", topic: "alg_powers_of_i" },
        ],
      },
    ],
  },
  {
    domain: "W",
    name: "Complex Plane",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "W",
        desc: "Complex Plane.",
        skills: [
          { name: "Introduction to the complex plane", topic: "alg_complex_plane" },
          { name: "Graph complex numbers", topic: "alg_complex_plane" },
          { name: "Addition in the complex plane", topic: "alg_complex_add_subtract" },
          { name: "Subtraction in the complex plane", topic: "alg_complex_add_subtract" },
          { name: "Graph complex conjugates", topic: "alg_complex_conjugate" },
          { name: "Absolute value in the complex plane", topic: "alg_complex_abs" },
          { name: "Midpoints in the complex plane", topic: "alg_complex_plane" },
          { name: "Distance in the complex plane", topic: "alg_complex_plane" },
        ],
      },
    ],
  },
  {
    domain: "X",
    name: "Polar Form",
    color: "bg-brand-coral",
    standards: [
      {
        code: "X",
        desc: "Polar Form.",
        skills: [
          { name: "Find the modulus and argument of a complex number", topic: "alg_polar_form" },
          { name: "Convert complex numbers from rectangular to polar form", topic: "alg_polar_form" },
          { name: "Convert complex numbers from polar to rectangular form", topic: "alg_polar_form" },
          { name: "Convert complex numbers between rectangular and polar form", topic: "alg_polar_form" },
          { name: "Match polar equations and graphs", topic: "alg_polar_form" },
        ],
      },
    ],
  },
  {
    domain: "Y",
    name: "Two-Dimensional Vectors",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "Y",
        desc: "Two-Dimensional Vectors.",
        skills: [
          { name: "Find the magnitude of a vector", topic: "geo_vectors" },
          { name: "Find the component form of a vector", topic: "geo_vectors" },
          { name: "Find the direction angle of a vector", topic: "alg_vector_2d" },
          { name: "Find the component form of a vector from its magnitude and direction angle", topic: "alg_vector_2d" },
          { name: "Graph a resultant vector using the triangle method", topic: "geo_vectors" },
          { name: "Graph a resultant vector using the parallelogram method", topic: "geo_vectors" },
          { name: "Add vectors", topic: "geo_vectors" },
          { name: "Subtract vectors", topic: "geo_vectors" },
          { name: "Find the magnitude and direction of a vector sum", topic: "alg_vector_2d" },
          { name: "Multiply a vector by a scalar", topic: "alg_vector_2d" },
          { name: "Find the magnitude of a vector scalar multiple", topic: "alg_vector_2d" },
          { name: "Determine the direction of a vector scalar multiple", topic: "alg_vector_2d" },
          { name: "Find a unit vector", topic: "alg_vector_2d" },
          { name: "Linear combinations of vectors", topic: "alg_vector_2d" },
        ],
      },
    ],
  },
  {
    domain: "Z",
    name: "Three-Dimensional Vectors",
    color: "bg-brand-green",
    standards: [
      {
        code: "Z",
        desc: "Three-Dimensional Vectors.",
        skills: [
          { name: "Find the magnitude of a three-dimensional vector", topic: "alg_vector_3d" },
          { name: "Find the component form of a three-dimensional vector", topic: "geo_distance_3d" },
          { name: "Add and subtract three-dimensional vectors", topic: "alg_vector_3d" },
          { name: "Scalar multiples of three-dimensional vectors", topic: "alg_vector_3d" },
          { name: "Find a three-dimensional unit vector", topic: "alg_vector_3d" },
          { name: "Linear combinations of three-dimensional vectors", topic: "alg_vector_3d" },
        ],
      },
    ],
  },
  {
    domain: "AA",
    name: "Sequences",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "AA",
        desc: "Sequences.",
        skills: [
          { name: "Find terms of a sequence", topic: "alg_arithmetic_sequence" },
          { name: "Find terms of a recursive sequence", topic: "alg_recursive_sequence" },
          { name: "Identify a sequence as explicit or recursive", topic: "alg_recursive_sequence" },
          { name: "Find a recursive formula", topic: "alg_recursive_sequence" },
          { name: "Find recursive and explicit formulas", topic: "alg_recursive_sequence" },
          { name: "Convert a recursive formula to an explicit formula", topic: "alg_recursive_sequence" },
          { name: "Convert an explicit formula to a recursive formula", topic: "alg_recursive_sequence" },
          { name: "Convert between explicit and recursive formulas", topic: "alg_recursive_sequence" },
        ],
      },
    ],
  },
  {
    domain: "BB",
    name: "Series",
    color: "bg-brand-orange",
    standards: [
      {
        code: "BB",
        desc: "Series.",
        skills: [
          { name: "Identify arithmetic and geometric series", topic: "alg_arithmetic_series" },
          { name: "Introduction to sigma notation", topic: "alg_arithmetic_series" },
          { name: "Find the sum of an arithmetic series", topic: "alg_arithmetic_series" },
          { name: "Find the sum of a finite geometric series", topic: "alg_geometric_series" },
          { name: "Introduction to partial sums", topic: "alg_arithmetic_series" },
          { name: "Partial sums of arithmetic series", topic: "alg_arithmetic_series" },
          { name: "Partial sums of geometric series", topic: "alg_geometric_series" },
          { name: "Partial sums: mixed review", topic: "alg_geometric_series" },
          { name: "Convergent and divergent geometric series", topic: "alg_infinite_geometric" },
          { name: "Find the value of an infinite geometric series", topic: "alg_infinite_geometric" },
          { name: "Write a repeating decimal as a fraction", topic: "alg_repeating_decimal" },
        ],
      },
    ],
  },
  {
    domain: "CC",
    name: "Probability",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "CC",
        desc: "Probability.",
        skills: [
          { name: "Calculate probabilities of events", topic: "geo_probability" },
          { name: "Combinations and permutations", topic: "geo_permutations_combinations" },
          { name: "Find probabilities using combinations and permutations", topic: "geo_permutations_combinations" },
          { name: "Find probabilities using two-way frequency tables", topic: "geo_probability" },
          { name: "Identify independent events", topic: "geo_probability" },
          { name: "Find conditional probabilities", topic: "geo_probability" },
          { name: "Independence and conditional probability", topic: "geo_probability" },
          { name: "Find conditional probabilities using two-way frequency tables", topic: "geo_probability" },
          { name: "Find probabilities using the addition rule", topic: "geo_probability" },
        ],
      },
    ],
  },
  {
    domain: "DD",
    name: "Probability Distributions",
    color: "bg-brand-coral",
    standards: [
      {
        code: "DD",
        desc: "Probability Distributions.",
        skills: [
          { name: "Identify probability distributions", topic: "alg_expected_value" },
          { name: "Identify discrete and continuous random variables", topic: "alg_expected_value" },
          { name: "Write a discrete probability distribution", topic: "alg_expected_value" },
          { name: "Graph a discrete probability distribution", topic: "alg_expected_value" },
          { name: "Expected values of random variables", topic: "alg_expected_value" },
          { name: "Variance of random variables", topic: "alg_expected_value" },
          { name: "Standard deviation of random variables", topic: "alg_expected_value" },
          { name: "Write the probability distribution for a game of chance", topic: "alg_expected_value" },
          { name: "Expected values for a game of chance", topic: "alg_expected_value" },
          { name: "Choose the better bet", topic: "alg_expected_value" },
        ],
      },
    ],
  },
  {
    domain: "EE",
    name: "Binomial and Normal Distributions",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "EE",
        desc: "Binomial and Normal Distributions.",
        skills: [
          { name: "Find probabilities using the binomial distribution", topic: "alg_binomial_probability" },
          { name: "Mean, variance, and standard deviation of binomial distributions", topic: "alg_binomial_probability" },
          { name: "Find probabilities using the normal distribution I", topic: "alg_zscore" },
          { name: "Find probabilities using the normal distribution II", topic: "alg_zscore" },
          { name: "Find z-values", topic: "alg_zscore" },
          { name: "Find values of normal variables", topic: "alg_zscore" },
          { name: "Distributions of sample means", topic: "alg_zscore" },
          { name: "The Central Limit Theorem", topic: "alg_zscore" },
          { name: "Use normal distributions to approximate binomial distributions", topic: "alg_zscore" },
        ],
      },
    ],
  },
  {
    domain: "FF",
    name: "Single-Variable Statistics",
    color: "bg-brand-green",
    standards: [
      {
        code: "FF",
        desc: "Single-Variable Statistics.",
        skills: [
          { name: "Identify biased samples", topic: "statistics" },
          { name: "Standard deviation of a population", topic: "statistics" },
          { name: "Standard deviation of a sample", topic: "statistics" },
          { name: "Identify an outlier", topic: "alg_quartiles" },
          { name: "Identify an outlier and describe the effect of removing it", topic: "alg_quartiles" },
          { name: "Find confidence intervals for population means", topic: "alg_zscore" },
          { name: "Find confidence intervals for population proportions", topic: "alg_zscore" },
          { name: "Interpret confidence intervals for population means", topic: "alg_zscore" },
          { name: "Experiment design", topic: "statistics" },
          { name: "Analyze the results of an experiment using simulations", topic: "statistics" },
        ],
      },
    ],
  },
  {
    domain: "GG",
    name: "Bivariate Statistics",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "GG",
        desc: "Bivariate Statistics.",
        skills: [
          { name: "Outliers in scatter plots", topic: "alg_scatter_correlation" },
          { name: "Match correlation coefficients to scatter plots", topic: "alg_scatter_correlation" },
          { name: "Calculate correlation coefficients", topic: "alg_scatter_correlation" },
          { name: "Find the equation of a regression line", topic: "geo_line_equation" },
          { name: "Interpret regression lines", topic: "alg_scatter_correlation" },
          { name: "Analyze a regression line of a data set", topic: "alg_scatter_correlation" },
          { name: "Analyze a regression line using statistics of a data set", topic: "alg_scatter_correlation" },
          { name: "Exponential regression", topic: "alg_exponential_growth_decay" },
        ],
      },
    ],
  },
  {
    domain: "HH",
    name: "Limits",
    color: "bg-brand-orange",
    standards: [
      {
        code: "HH",
        desc: "Limits.",
        skills: [
          { name: "Find limits using graphs", topic: "calc_limits" },
          { name: "Find one-sided limits using graphs", topic: "calc_limits" },
          { name: "Determine if a limit exists", topic: "calc_limits" },
          { name: "Find limits at vertical asymptotes using graphs", topic: "calc_limits" },
          { name: "Determine end behavior using graphs", topic: "calc_limits" },
          { name: "Find limits using addition, subtraction, and multiplication laws", topic: "calc_limits" },
          { name: "Find limits using the division law", topic: "calc_limits" },
          { name: "Find limits using power and root laws", topic: "calc_limits" },
          { name: "Find limits using limit laws", topic: "calc_limits" },
          { name: "Find limits of polynomials and rational functions", topic: "calc_limits" },
          { name: "Find limits involving factorization and rationalization", topic: "calc_limits" },
        ],
      },
    ],
  },
  {
    domain: "II",
    name: "Continuity",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "II",
        desc: "Continuity.",
        skills: [
          { name: "Identify graphs of continuous functions", topic: "calc_continuity" },
          { name: "Determine continuity using graphs", topic: "calc_continuity" },
          { name: "Determine one-sided continuity using graphs", topic: "calc_continuity" },
          { name: "Find and analyze points of discontinuity using graphs", topic: "calc_continuity" },
          { name: "Determine continuity on an interval using graphs", topic: "calc_continuity" },
        ],
      },
    ],
  },
  {
    domain: "JJ",
    name: "Introduction to Derivatives",
    color: "bg-brand-coral",
    standards: [
      {
        code: "JJ",
        desc: "Introduction to Derivatives.",
        skills: [
          { name: "Average rate of change I", topic: "calc_avg_rate_of_change" },
          { name: "Average rate of change II", topic: "calc_avg_rate_of_change" },
          { name: "Find instantaneous rates of change", topic: "calc_tangent_line" },
          { name: "Velocity as a rate of change", topic: "calc_avg_rate_of_change" },
          { name: "Find values of derivatives using limits", topic: "calc_derivative_rules" },
          { name: "Find the slope of a tangent line using limits", topic: "calc_tangent_line" },
          { name: "Find equations of tangent lines using limits", topic: "calc_tangent_line" },
        ],
      },
    ],
  },
];

// Calculus (grade "C") — Common Core Math stops at Algebra II, so there are no
// official standard codes here. The plan mirrors the section structure (A–W) of a
// full Calculus AB skill plan; the "codes" are informal section letters, not CCSS
// codes. Every skill is wired to a real calculus generator, so the generated
// problems always match the skill label.
const CALC_PLAN = [
  {
    domain: "A",
    name: "Introduction to limits",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "A",
        desc: "Find limits using graphs and determine whether a limit exists.",
        skills: [
          { name: "Find limits using graphs", topic: "calc_limit_graph" },
          { name: "Find one-sided limits using graphs", topic: "calc_onesided_limit_graph" },
          { name: "Determine if a limit exists", topic: "calc_limit_exists_graph" },
        ],
      },
    ],
  },
  {
    domain: "B",
    name: "Calculate limits",
    color: "bg-brand-green",
    standards: [
      {
        code: "B",
        desc: "Use limit laws, factoring, and identities to evaluate limits algebraically.",
        skills: [
          { name: "Find limits using addition, subtraction, and multiplication laws", topic: "calc_limit_laws_sum" },
          { name: "Find limits using the division law", topic: "calc_limit_laws_division" },
          { name: "Find limits using power and root laws", topic: "calc_limit_laws_power_root" },
          { name: "Find limits using limit laws", topic: "calc_limit_laws_mixed" },
          { name: "Find limits of polynomials and rational functions", topic: "calc_limit_poly_rational" },
          { name: "Find limits involving factorization and rationalization", topic: "calc_limit_factor_rationalize" },
          { name: "Find limits involving absolute value functions", topic: "calc_limit_abs_value" },
          { name: "Find limits involving trigonometric functions", topic: "calc_limit_trig" },
        ],
      },
    ],
  },
  {
    domain: "C",
    name: "Infinite limits",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "C",
        desc: "Determine limits at vertical asymptotes.",
        skills: [
          { name: "Find limits at vertical asymptotes using graphs", topic: "calc_vert_asymptote_graph" },
          { name: "Find the limit at a vertical asymptote of a rational function I", topic: "calc_vert_asymptote_I" },
          { name: "Find the limit at a vertical asymptote of a rational function II", topic: "calc_vert_asymptote_II" },
        ],
      },
    ],
  },
  {
    domain: "D",
    name: "Limits at infinity",
    color: "bg-brand-orange",
    standards: [
      {
        code: "D",
        desc: "Determine end behavior of functions.",
        skills: [
          { name: "Determine end behavior using graphs", topic: "calc_end_behavior_graph" },
          { name: "Determine end behavior of polynomial and rational functions", topic: "calc_end_behavior_algebraic" },
        ],
      },
    ],
  },
  {
    domain: "E",
    name: "Continuity",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "E",
        desc: "Analyze continuity from graphs and algebraically.",
        skills: [
          { name: "Identify graphs of continuous functions", topic: "calc_continuous_graph_identify" },
          { name: "Determine continuity using graphs", topic: "calc_continuity_at_point_graph" },
          { name: "Determine one-sided continuity using graphs", topic: "calc_onesided_continuity_graph" },
          { name: "Find and analyze points of discontinuity using graphs", topic: "calc_discontinuity_points_graph" },
          { name: "Determine continuity on an interval using graphs", topic: "calc_continuity_interval_graph" },
          { name: "Determine the continuity of a piecewise function at a point", topic: "calc_piecewise_continuity_check" },
          { name: "Make a piecewise function continuous", topic: "calc_continuity" },
          { name: "Intermediate Value Theorem", topic: "calc_ivt" },
        ],
      },
    ],
  },
  {
    domain: "F",
    name: "Introduction to derivatives",
    color: "bg-brand-coral",
    standards: [
      {
        code: "F",
        desc: "Average rate of change, instantaneous rate, and tangent lines via limits.",
        skills: [
          { name: "Average rate of change I", topic: "calc_avg_rate_of_change_I" },
          { name: "Average rate of change II", topic: "calc_avg_rate_of_change_II" },
          { name: "Find instantaneous rates of change", topic: "calc_instantaneous_rate" },
          { name: "Velocity as a rate of change", topic: "calc_velocity_rate" },
          { name: "Find values of derivatives using limits", topic: "calc_deriv_via_limit_value" },
          { name: "Find the slope of a tangent line using limits", topic: "calc_tangent_slope_via_limit" },
          { name: "Find equations of tangent lines using limits", topic: "calc_tangent_eq_via_limit" },
        ],
      },
    ],
  },
  {
    domain: "G",
    name: "Derivative rules",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "G",
        desc: "Sum/difference, product, quotient, power, chain, and inverse function rules.",
        skills: [
          { name: "Sum and difference rules", topic: "calc_sum_diff_rule" },
          { name: "Product rule", topic: "calc_product_rule" },
          { name: "Quotient rule", topic: "calc_quotient_rule" },
          { name: "Power rule I", topic: "calc_power_rule_I" },
          { name: "Power rule II", topic: "calc_power_rule_II" },
          { name: "Chain rule", topic: "calc_chain_rule" },
          { name: "Inverse function rule", topic: "calc_inverse_function_rule" },
        ],
      },
    ],
  },
  {
    domain: "H",
    name: "Calculate derivatives",
    color: "bg-brand-green",
    standards: [
      {
        code: "H",
        desc: "Differentiate polynomial, trig, exponential, logarithmic, and radical functions.",
        skills: [
          { name: "Find derivatives of polynomials", topic: "calc_deriv_polynomials" },
          { name: "Find derivatives of rational functions", topic: "calc_deriv_rational" },
          { name: "Find derivatives of sine and cosine functions", topic: "calc_deriv_sin_cos" },
          { name: "Find derivatives of other trigonometric functions", topic: "calc_deriv_other_trig" },
          { name: "Find derivatives of exponential functions", topic: "calc_deriv_exponential" },
          { name: "Find derivatives of logarithmic functions", topic: "calc_deriv_logarithmic" },
          { name: "Find derivatives of inverse trigonometric functions", topic: "calc_deriv_inverse_trig" },
          { name: "Find derivatives of radical functions", topic: "calc_deriv_radical" },
          { name: "Find derivatives using the product rule I", topic: "calc_product_rule_I" },
          { name: "Find derivatives using the product rule II", topic: "calc_product_rule_II" },
          { name: "Find derivatives using the quotient rule I", topic: "calc_quotient_rule_I" },
          { name: "Find derivatives using the quotient rule II", topic: "calc_quotient_rule_II" },
          { name: "Find derivatives of trigonometric functions using the product and quotient rules", topic: "calc_deriv_trig_product_quotient" },
          { name: "Find derivatives using the chain rule I", topic: "calc_chain_rule_I" },
          { name: "Find derivatives using the chain rule II", topic: "calc_chain_rule_II" },
          { name: "Find equations of tangent lines using derivatives: polynomial functions", topic: "calc_tangent_line_poly" },
          { name: "Find equations of tangent lines using derivatives: other functions", topic: "calc_tangent_line_other" },
        ],
      },
    ],
  },
  {
    domain: "I",
    name: "Derivative strategies",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "I",
        desc: "Implicit and logarithmic differentiation.",
        skills: [
          { name: "Find derivatives using implicit differentiation", topic: "calc_implicit_diff" },
          { name: "Find tangent lines using implicit differentiation", topic: "calc_implicit_tangent" },
          { name: "Find derivatives using logarithmic differentiation", topic: "calc_log_differentiation" },
        ],
      },
    ],
  },
  {
    domain: "J",
    name: "Calculate higher derivatives",
    color: "bg-brand-orange",
    standards: [
      {
        code: "J",
        desc: "Second and higher derivatives of polynomial, rational, trig, exponential, and log functions.",
        skills: [
          { name: "Find higher derivatives of polynomials", topic: "calc_higher_deriv_poly" },
          { name: "Find higher derivatives of rational and radical functions", topic: "calc_higher_deriv_rational_radical" },
          { name: "Find second derivatives of trigonometric, exponential, and logarithmic functions", topic: "calc_second_deriv_trig_exp_log" },
          { name: "Find higher derivatives of exponential and trigonometric functions", topic: "calc_higher_deriv_exp_trig" },
          { name: "Find higher derivatives of logarithmic and power functions", topic: "calc_higher_deriv_log" },
        ],
      },
    ],
  },
  {
    domain: "K",
    name: "Rates of change",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "K",
        desc: "Relate position, velocity, and acceleration; introduce related rates.",
        skills: [
          { name: "Relate position, velocity, speed, and acceleration using derivatives", topic: "calc_position_velocity_accel" },
          { name: "Introduction to related rates", topic: "calc_related_rates" },
        ],
      },
    ],
  },
  {
    domain: "L",
    name: "L\'Hospital\'s rule",
    color: "bg-brand-coral",
    standards: [
      {
        code: "L",
        desc: "Evaluate indeterminate forms involving quotients.",
        skills: [
          { name: "L\'Hospital\'s rule: indeterminate forms involving quotients", topic: "calc_lhopital" },
        ],
      },
    ],
  },
  {
    domain: "M",
    name: "Analyze functions using the first derivative",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "M",
        desc: "Mean Value Theorem, critical points, extrema, and derivative graphs.",
        skills: [
          { name: "Mean Value Theorem", topic: "calc_mvt" },
          { name: "Find critical points of functions", topic: "calc_critical_points" },
          { name: "Find intervals of increase and decrease", topic: "calc_increase_decrease" },
          { name: "Find local extrema using the first derivative test", topic: "calc_first_deriv_test" },
          { name: "Find absolute extrema on a closed interval", topic: "calc_absolute_extrema" },
          { name: "Identify the graph of the derivative from the graph of the function", topic: "calc_deriv_graph_from_function" },
          { name: "Identify the graph of a function from the graph of its derivative", topic: "calc_function_graph_from_deriv" },
        ],
      },
    ],
  },
  {
    domain: "N",
    name: "Analyze functions using the second derivative",
    color: "bg-brand-green",
    standards: [
      {
        code: "N",
        desc: "Concavity, inflection points, and the second derivative test.",
        skills: [
          { name: "Identify the graph of the second derivative from the graph of the function", topic: "calc_second_deriv_graph" },
          { name: "Classify local extrema using the second derivative test", topic: "calc_second_deriv_test" },
          { name: "Identify the graph of a function using its first and second derivatives", topic: "calc_function_from_first_second_deriv" },
        ],
      },
    ],
  },
  {
    domain: "O",
    name: "Optimization",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "O",
        desc: "Find maximum and minimum values in applied problems.",
        skills: [
          { name: "Introduction to optimization", topic: "calc_optimization" },
        ],
      },
    ],
  },
  {
    domain: "P",
    name: "Linear approximation",
    color: "bg-brand-orange",
    standards: [
      {
        code: "P",
        desc: "Estimate function values using the tangent line.",
        skills: [
          { name: "Linear approximation", topic: "calc_linear_approx" },
        ],
      },
    ],
  },
  {
    domain: "Q",
    name: "Introduction to integration",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "Q",
        desc: "Riemann sums, definite integrals, net area, and their properties.",
        skills: [
          { name: "Approximate the area under a curve: left and right endpoints", topic: "calc_riemann_left_right" },
          { name: "Approximate the area under a curve: midpoints", topic: "calc_riemann_midpoint" },
          { name: "Approximate the area under a curve: trapezoids", topic: "calc_riemann_trapezoid" },
          { name: "Relate Riemann sums and definite integrals", topic: "calc_riemann_integral_relation" },
          { name: "Definite integrals and net area", topic: "calc_net_area" },
          { name: "Evaluate definite integrals using graphs", topic: "calc_definite_integral_graph" },
          { name: "Properties of definite integrals", topic: "calc_integral_properties" },
        ],
      },
    ],
  },
  {
    domain: "R",
    name: "Fundamental Theorem of Calculus",
    color: "bg-brand-coral",
    standards: [
      {
        code: "R",
        desc: "Apply both parts of the Fundamental Theorem of Calculus.",
        skills: [
          { name: "Fundamental Theorem of Calculus, Part 1", topic: "calc_ftc1" },
          { name: "Fundamental Theorem of Calculus, Part 2", topic: "calc_ftc2" },
        ],
      },
    ],
  },
  {
    domain: "S",
    name: "Antiderivatives and indefinite integrals",
    color: "bg-brand-blue text-white",
    standards: [
      {
        code: "S",
        desc: "Find antiderivatives using the power rule and beyond.",
        skills: [
          { name: "Find antiderivatives", topic: "calc_antideriv_general" },
          { name: "Find indefinite integrals using the power rule", topic: "calc_indef_integral_power" },
          { name: "Find indefinite integrals involving exponential and logarithmic functions", topic: "calc_indef_integral_explog" },
          { name: "Find indefinite integrals involving trigonometric functions", topic: "calc_indef_integral_trig" },
          { name: "Find indefinite integrals: mixed review", topic: "calc_indef_integral_mixed" },
        ],
      },
    ],
  },
  {
    domain: "T",
    name: "Definite integrals",
    color: "bg-brand-green",
    standards: [
      {
        code: "T",
        desc: "Evaluate definite integrals of power, exponential, log, and trig functions.",
        skills: [
          { name: "Evaluate definite integrals using the power rule", topic: "calc_def_integral_power" },
          { name: "Evaluate definite integrals involving exponential and logarithmic functions", topic: "calc_def_integral_explog" },
          { name: "Evaluate definite integrals involving trigonometric functions", topic: "calc_def_integral_trig" },
          { name: "Evaluate definite integrals: mixed review", topic: "calc_def_integral_mixed" },
        ],
      },
    ],
  },
  {
    domain: "U",
    name: "Integration techniques",
    color: "bg-[#F3E8FF]",
    standards: [
      {
        code: "U",
        desc: "Substitution, inverse trig integrals, long division, and completing the square.",
        skills: [
          { name: "Find indefinite integrals using substitution", topic: "calc_indef_integral_substitution" },
          { name: "Evaluate definite integrals using substitution", topic: "calc_def_integral_substitution" },
          { name: "Integrals involving inverse trigonometric functions", topic: "calc_inverse_trig_integral" },
          { name: "Integrate using long division", topic: "calc_long_division_integral" },
          { name: "Integrate by completing the square", topic: "calc_complete_square_integral" },
          { name: "Choose an integration strategy: indefinite integrals", topic: "calc_choose_strategy_indefinite" },
          { name: "Choose an integration strategy: definite integrals", topic: "calc_choose_strategy_definite" },
        ],
      },
    ],
  },
  {
    domain: "V",
    name: "Differential equations",
    color: "bg-brand-orange",
    standards: [
      {
        code: "V",
        desc: "Solve initial value problems and separable differential equations.",
        skills: [
          { name: "Solve first-order initial value problems", topic: "calc_ivp_first_order" },
          { name: "Solve higher-order initial value problems", topic: "calc_ivp_higher_order" },
          { name: "Solve initial value problems involving linear motion", topic: "calc_ivp_linear_motion" },
          { name: "Verify solutions to differential equations", topic: "calc_verify_ode_solution" },
          { name: "Slope fields", topic: "calc_slope_field" },
          { name: "Solutions to differential equations using slope fields", topic: "calc_slope_field_solution" },
          { name: "Find general solutions to separable differential equations", topic: "calc_separable_general" },
          { name: "Solve initial value problems for separable differential equations", topic: "calc_separable_ivp" },
        ],
      },
    ],
  },
  {
    domain: "W",
    name: "Applications of integration",
    color: "bg-brand-yellow",
    standards: [
      {
        code: "W",
        desc: "Average value, motion, area between curves, and volumes of revolution.",
        skills: [
          { name: "Average value of a function", topic: "calc_average_value" },
          { name: "Relate position, velocity, speed, and acceleration using integrals", topic: "calc_position_velocity_integral" },
          { name: "Areas of regions bounded by graphs and the x-axis", topic: "calc_area_x_axis" },
          { name: "Areas of regions bounded by two curves: functions of x", topic: "calc_area_two_curves_x" },
          { name: "Areas of regions bounded by graphs and the y-axis", topic: "calc_area_y_axis" },
          { name: "Areas of regions bounded by two curves: functions of y", topic: "calc_area_two_curves_y" },
          { name: "Areas of regions bounded by curves with more than two intersection points", topic: "calc_area_multiple_intersections" },
          { name: "Find volumes using the disk method: revolve around the x-axis or y-axis", topic: "calc_disk_volume" },
        ],
      },
    ],
  },
];

const CCSS_PLANS = { P: PRE_K_PLAN, K: K_CCSS_PLAN, "1": G1_CCSS_PLAN, "2": G2_CCSS_PLAN, "3": G3_CCSS_PLAN, "4": G4_CCSS_PLAN, "5": G5_CCSS_PLAN, "6": G6_CCSS_PLAN, "7": G7_CCSS_PLAN, "8": G8_CCSS_PLAN, "9": ALG1_PLAN, "10": GEO_PLAN, "11": ALG2_PLAN, "12": PRECALC_PLAN, C: CALC_PLAN };

// Filter a CCSS plan down to skills whose name matches the search query,
// dropping standards/domains left with no matching skills. Empty query
// returns the plan unchanged (no filtering cost on the common path).
function filterPlan(plan, query) {
  const q = query.trim().toLowerCase();
  if (!q) return plan;
  return plan
    .map(domain => ({
      ...domain,
      standards: domain.standards
        .map(std => ({ ...std, skills: std.skills.filter(s => s.name.toLowerCase().includes(q)) }))
        .filter(std => std.skills.length > 0),
    }))
    .filter(domain => domain.standards.length > 0);
}

// ─── CCSS skill plan component (domain block -> standard row -> skill chips) ──
function CCSSPlanBlock({ plan, onSkillClick, loading }) {
  return (
    <div className="space-y-5">
      {plan.map(domain => (
        <div key={domain.domain} className="brutal-card bg-white overflow-hidden">
          <div className={`${domain.color} border-b-[3px] border-[#0A0A0A] px-5 py-3`}>
            <span className="font-heading font-bold text-base">
              {domain.domain} | {domain.name}
            </span>
          </div>
          <div className="divide-y-[2px] divide-[#0A0A0A]/10">
            {domain.standards.map(std => (
              <div key={std.code} className="px-5 py-4">
                <div className="sm:grid sm:grid-cols-[220px_1fr] gap-4">
                  <div className="mb-2 sm:mb-0">
                    <p className="text-xs text-muted-foreground leading-snug">{std.desc}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {std.skills.map(skill => (
                      <button
                        key={skill.name}
                        onClick={() => skill.disabled ? undefined : onSkillClick(skill)}
                        disabled={!!loading || skill.disabled}
                        className={`brutal-btn text-xs px-3 py-1.5 font-semibold inline-flex items-center gap-1.5 disabled:pointer-events-none disabled:opacity-50 ${
                          loading === skill.name ? "bg-brand-yellow" : "bg-white hover:bg-brand-yellow"
                        }`}
                      >
                        {loading === skill.name
                          ? <Loader2 strokeWidth={3} className="w-3 h-3 animate-spin" />
                          : <Sparkles strokeWidth={3} className="w-3 h-3 opacity-40" />}
                        {skill.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Grade list ───────────────────────────────────────────────────────────────
const GRADE_LIST = [
  { id: "P",  label: "Pre-K",        color: "bg-[#F3E8FF]" },
  { id: "K",  label: "Kindergarten", color: "bg-brand-coral" },
  { id: "1",  label: "Grade 1",      color: "bg-brand-yellow" },
  { id: "2",  label: "Grade 2",      color: "bg-brand-green" },
  { id: "3",  label: "Grade 3",      color: "bg-brand-blue text-white" },
  { id: "4",  label: "Grade 4",      color: "bg-brand-orange text-white" },
  { id: "5",  label: "Grade 5",      color: "bg-brand-coral" },
  { id: "6",  label: "Grade 6",      color: "bg-brand-yellow" },
  { id: "7",  label: "Grade 7",      color: "bg-brand-green" },
  { id: "8",  label: "Grade 8",      color: "bg-brand-blue text-white" },
  { id: "9",  label: "Algebra 1",    color: "bg-brand-coral" },
  { id: "10", label: "Geometry",     color: "bg-brand-yellow" },
  { id: "11", label: "Algebra 2",    color: "bg-brand-green" },
  { id: "12", label: "Pre-Calculus", color: "bg-brand-orange text-white" },
  { id: "C",  label: "Calculus",     color: "bg-brand-blue text-white" },
];

const STEPS = ["Pick a grade", "Pick a skill", "Generate"];

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Home() {
  const navigate = useNavigate();
  const [grade, setGrade] = useState(null);
  const [loading, setLoading] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");

  const gradeLabel = grade ? GRADE_LIST.find(g => g.id === grade)?.label : null;
  const skills = grade && grade !== "K"
    ? (GRADE_SKILLS[grade] || []).filter(s => s.status === "available")
    : [];
  const filteredPlan = grade && CCSS_PLANS[grade] ? filterPlan(CCSS_PLANS[grade], skillSearch) : null;

  const currentStep = grade === null ? 0 : 1;

  const handleSkillClick = async (skill) => {
    // accepts { topic, name } (K_CCSS_PLAN) or { category } (GRADE_SKILLS)
    const topicKey = skill.topic || TOPIC_KEY_FOR[skill.category] || "counting";
    const displayName = skill.name || skill.category;
    setLoading(displayName);
    try {
      const ws = await generateWorksheet({
        grade,
        topic: topicKey,
        difficulty: "medium",
        count: 10,
        multiple_choice: true,
        title: `${gradeLabel} – ${displayName}`,
      });
      navigate(`/practice/${ws.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (!e?.response) {
        toast.error("Could not reach the server — please try again.");
      } else {
        toast.error(detail || "Failed to generate worksheet — try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SiteVerification />
      <Helmet>
        <title>iLoveMathWorksheets — Free K-12 Math Worksheets</title>
        <meta name="description" content="Free K-12 math worksheets aligned to California Common Core Content Standards, for every grade and skill. Auto-graded online, AI tutor, print-ready PDFs. Kindergarten through Calculus." />
        <link rel="canonical" href="https://www.ilovemathworksheets.com" />
        <meta property="og:title" content="iLoveMathWorksheets — Free K-12 Math Worksheets" />
        <meta property="og:description" content="Free K-12 math worksheets aligned to California Common Core Content Standards. Auto-graded, AI tutor, PDF answer keys." />
        <meta property="og:url" content="https://www.ilovemathworksheets.com" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.ilovemathworksheets.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:image" content="https://www.ilovemathworksheets.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(websiteSchema())}</script>
      </Helmet>
      <Header />

      <main className="flex-1 flex flex-col items-center px-5 py-12 sm:py-16">
        {/* Container widens for K to accommodate the two-column CCSS layout */}
        <div className={`w-full transition-all duration-300 ${CCSS_PLANS[grade] ? "max-w-4xl" : "max-w-2xl"}`}>

          {/* Page heading */}
          <div className="text-center mb-10">
            <span className="brutal-badge bg-brand-green">
              Aligned to California Common Core Content Standards
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight mt-3">
              Free math worksheets,<br className="hidden sm:block" /> K–12
            </h1>
            <p className="text-muted-foreground mt-3 text-base sm:text-lg">
              No sign-up needed. Pick a grade and skill — get a worksheet in seconds.
            </p>
          </div>

          {/* Secondary path: turn a prompt, text, photo, or a file into a worksheet (landing view only) */}
          {!grade && (
            <Link
              to="/worksheet-from-photo"
              className="brutal-card bg-brand-yellow hover:translate-y-[2px] transition block p-4 sm:p-5 mb-8"
              data-testid="home-photo-cta"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-white border-[3px] border-[#0A0A0A] flex items-center justify-center">
                  <ImageUp strokeWidth={2.5} className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-heading font-bold text-sm sm:text-base flex items-center gap-1.5">
                    Turn a prompt, text, photo, or a file (docx or pdf) into a Worksheet
                    <Sparkles strokeWidth={2.5} className="w-4 h-4 text-brand-coral" />
                  </p>
                  <p className="text-xs sm:text-sm text-[#0A0A0A]/70">
                    Describe a topic, paste text, snap a photo, or upload a docx/pdf — get a fresh worksheet with answers.
                  </p>
                </div>
                <ArrowRight strokeWidth={3} className="w-5 h-5 ml-auto shrink-0" />
              </div>
            </Link>
          )}

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {STEPS.map((label, i) => {
              const done = i < currentStep;
              const active = i === currentStep;
              return (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full border-[2.5px] border-[#0A0A0A] flex items-center justify-center text-xs font-bold transition-colors ${
                    done ? "bg-brand-green" : active ? "bg-[#0A0A0A] text-white" : "bg-white text-muted-foreground"
                  }`}>
                    {done ? <Check strokeWidth={3} className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={`text-sm font-bold hidden sm:block ${active ? "text-[#0A0A0A]" : "text-muted-foreground"}`}>
                    {label}
                  </span>
                  {i < STEPS.length - 1 && <span className="h-px w-6 sm:w-10 bg-[#0A0A0A]/20" />}
                </div>
              );
            })}
          </div>

          {/* Step 1: Grade */}
          {!grade && (
            <div className="brutal-card p-6 sm:p-8 bg-white">
              <p className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground mb-5">
                Step 1 — Pick a grade
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {GRADE_LIST.map(g => (
                  <button
                    key={g.id}
                    onClick={() => { setGrade(g.id); setSkillSearch(""); }}
                    className={`brutal-btn ${g.color} py-3 px-4 font-bold text-sm`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Skill */}
          {grade && (
            <div>
              {/* Back + header */}
              <div className="flex items-center gap-3 mb-5">
                <button
                  onClick={() => { setGrade(null); setLoading(false); setSkillSearch(""); }}
                  className="brutal-btn bg-white p-2 inline-flex items-center"
                  aria-label="Back to grade selection"
                >
                  <ChevronLeft strokeWidth={3} className="w-4 h-4" />
                </button>
                <p className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Step 2 — Pick a skill
                  <span className="normal-case text-[#0A0A0A]"> · {gradeLabel}</span>
                </p>
              </div>

              {CCSS_PLANS[grade] ? (
                <>
                  <div className="relative mb-5">
                    <Search strokeWidth={3} className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      value={skillSearch}
                      onChange={(e) => setSkillSearch(e.target.value)}
                      placeholder={`Search ${gradeLabel} skills…`}
                      aria-label="Search skills"
                      data-testid="skill-search"
                      className="brutal-input w-full pl-10 pr-10 py-2.5 text-sm font-semibold placeholder:text-muted-foreground placeholder:font-normal"
                    />
                    {skillSearch && (
                      <button
                        onClick={() => setSkillSearch("")}
                        aria-label="Clear search"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#0A0A0A]"
                      >
                        <X strokeWidth={3} className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {filteredPlan.length === 0 ? (
                    <div className="brutal-card p-6 sm:p-8 bg-white text-center">
                      <p className="text-muted-foreground text-sm">
                        No skills match “{skillSearch}”. Try a different word.
                      </p>
                    </div>
                  ) : (
                    <CCSSPlanBlock plan={filteredPlan} onSkillClick={handleSkillClick} loading={loading} />
                  )}
                </>
              ) : (
                <div className="brutal-card p-6 sm:p-8 bg-white">
                  {skills.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No skills available for this grade yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {skills.map(skill => (
                        <button
                          key={skill.category}
                          onClick={() => handleSkillClick(skill)}
                          disabled={!!loading}
                          className="brutal-btn bg-white hover:bg-brand-yellow text-left px-4 py-3 flex items-center justify-between gap-3 group disabled:opacity-60 disabled:pointer-events-none"
                        >
                          <span className="font-bold text-sm leading-tight">{skill.category}</span>
                          {loading === skill.category ? (
                            <Loader2 strokeWidth={3} className="w-4 h-4 animate-spin shrink-0" />
                          ) : (
                            <Sparkles strokeWidth={3} className="w-4 h-4 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Loading caption */}
          {loading && (
            <p className="text-center text-sm text-muted-foreground font-semibold mt-4 animate-pulse">
              Generating 10 problems…
            </p>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
}
